import { type Translation } from "../hooks/useTranslations";
import type { NoticeParams } from "../pages/engines/lockCalculator/model/noticeParams";
import { type UILanguageCode } from "../types";

const es: Translation = {
  languageCode: "es" as UILanguageCode,
  projects: {
    all: "Todos los proyectos",
    new: "Nuevo proyecto",
    singular: "Proyecto",
    plural: "Proyectos",
    my: "Mis Proyectos",
    createNew: "Crear nuevo proyecto",
    delete: "Eliminar proyecto",
    search: "Buscar proyectos",
    shareWithUser: "Compartir proyecto con usuario",
    editPermissions: "Editar permisos",
    editSharings: "Editar comparticiones",
    invitations: "Invitaciones al proyecto",

    invitationStatus: {
      pending: "Pendiente",
      accepted: "Aceptado",
      rejected: "Rechazado",
      cancelled: "Cancelado",
      statusChanges: {
        accept: "Invitación aceptada con éxito.",
        rejected: "Invitación rechazada.",
      },
    },

    invitePage: {
      badge: "Invitación de acceso al proyecto",
      fallbackAuthorName: "un miembro del equipo",
      collaborationLead: "te ha invitado a colaborar en este proyecto y revisar su contenido compartido.",
      invitedBy: "Invitado por",
      invitationSentTo: "Invitación enviada a",
      projectName: "Nombre del proyecto",
      description: "Descripción",
      inviteNoLongerAvailable: "Esta invitación ya no está disponible porque ya fue aceptada, rechazada o cancelada.",
      acceptSignedIn: "Aceptar esta invitación abrirá el proyecto inmediatamente.",
      acceptSignedOut: "Aceptar esta invitación te pedirá iniciar sesión antes de concederte acceso al proyecto.",
      acceptAction: "Aceptar invitación",
      rejectAction: "Rechazar invitación",
    },

    questions: {
      delete: "¿Realmente deseas eliminar este proyecto?",
      unshare: "¿Realmente deseas dejar de compartir el proyecto con este usuario?",
    },

    errors: {
      deleteProjectWithConfigurationsNotPossible:
        "No es posible eliminar un proyecto si hay configuraciones presentes.",
      deleteProjectWithDocumentsNotPossible: "No es posible eliminar un proyecto si hay documentos presentes.",
      notFound: "Proyecto no encontrado.",
      sharingNotFound: "Compartición no encontrada.",
      sharingAlreadyExists: "El proyecto ya está compartido con este usuario.",
      documentNotFound: "Documento no encontrado.",
      missingDocumentName: "Falta el nombre del documento.",
      documentAlreadyPresent: "Documento ya presente.",
      missingProjectStatus: "Falta el estato del proyecto.",
      inviteReadNotAllowed: "No tienes permiso para ver esta invitación.",
      inviteInsertNotAllowed: "No tienes permiso para enviar una invitación para este proyecto.",
      inviteUpdateNotAllowed: "No tienes permiso para aceptar o rechazar esta invitación.",
      inviteDeleteNotAllowed: "No tienes permiso para eliminar esta invitación.",
      inviteAlreadyExists: "Ya existe una invitación para este usuario.",
      inviteNotFound: "Invitación no encontrada.",
      inviteAlreadyCancelled: "Esta invitación ya ha sido cancelada.",
      inviteAlreadyAccepted: "Esta invitación ya ha sido aceptada.",
      inviteAlreadyRejected: "Esta invitación ya ha sido rechazada.",
    },
  },

  calculations: {
    all: "Todos los cálculos",
    new: "Nuevo cálculo",
    plural: "Cálculos",
    create: "Crear cálculo",
    createNew: "Crear nuevo cálculo",
    delete: "Eliminar cálculo",
    duplicate: "Duplicar cálculo",
    my: "Mis cálculos",
    search: "Buscar cálculos",
    reset: "Restablecer cálculo",
    newCalculationName: "Nombre del nuevo cálculo",
    shareWithUser: "Compartir cálculos con usuario",
    editSharings: "Editar comparticiones",
    invitations: "Invitaciones de cálculo",
    invitePage: {
      badge: "Invitación de acceso a un cálculo",
      collaborationLead: "te ha invitado a colaborar en este cálculo y consultar su contenido compartido.",
      calculationName: "Nombre del cálculo",
      inviteNoLongerAvailable:
        "Esta invitación ya no está disponible porque ya ha sido aceptada, rechazada o cancelada.",
      acceptSignedIn: "Al aceptar esta invitación se abrirá el cálculo inmediatamente.",
      acceptSignedOut:
        "Al aceptar esta invitación se te pedirá que inicies sesión antes de conceder el acceso al cálculo.",
    },

    questions: {
      delete: "¿Realmente deseas eliminar este cálculo?",
      unshare: "¿Realmente deseas dejar de compartir el cálculo con este usuario?",
      reset: "¿Realmente deseas restablecer todos los datos del cálculo actual?",
    },

    errors: {
      notFound: "Cálculo no encontrado.",
      sharingNotFound: "Compartición no encontrada.",
      sharingAlreadyExists: "El cálculo ya está compartido con este usuario.",
      moduleNotSupported: "Módulo de cálculo no compatible.",
      moduleRestricted: "Este módulo no permite crear, editar o duplicar cálculos.",
      inviteInsertNotAllowed: "No tienes permiso para enviar una invitación para este cálculo.",
      inviteUpdateNotAllowed: "No tienes permiso para aceptar o rechazar esta invitación.",
      inviteDeleteNotAllowed: "No tienes permiso para eliminar esta invitación.",
      inviteAlreadyExists: "Ya existe una invitación para este usuario.",
      inviteNotFound: "Invitación no encontrada.",
      inviteAlreadyCancelled: "Esta invitación ya ha sido cancelada.",
      inviteAlreadyAccepted: "Esta invitación ya ha sido aceptada.",
      inviteAlreadyRejected: "Esta invitación ya ha sido rechazada.",
    },
  },

  sharingRequests: {
    title: "Solicitudes de acceso",
    requestedAt: "Solicitada el",
    message: "Mensaje",
    accept: "Aceptar",
    reject: "Rechazar",
    acceptTitle: "Aceptar la solicitud de acceso",
    rejectQuestion: "¿Realmente deseas rechazar esta solicitud de acceso?",
    accepted: "Solicitud de acceso aceptada.",
    rejected: "Solicitud de acceso rechazada.",
    notShared: {
      projectTitle: "Proyecto no compartido",
      calculationTitle: "Cálculo no compartido",
      projectText: "Este proyecto no se ha compartido contigo, por lo que no puedes acceder a él.",
      calculationText: "Este cálculo no se ha compartido contigo, por lo que no puedes acceder a él.",
      requestText: "Puedes pedir a su propietario que lo comparta contigo: recibirá tu solicitud por correo electrónico.",
      messageLabel: "Mensaje para el propietario",
      request: "Solicitar acceso",
      sent: "Tu solicitud de acceso se ha enviado.",
      pending: (date: string) =>
        `Solicitaste acceso el ${date}: recibirás un correo electrónico en cuanto el propietario responda.`,
    },
    managePage: {
      projectTitle: "Solicitud de acceso a un proyecto",
      calculationTitle: "Solicitud de acceso a un cálculo",
      requestedBy: "Solicitada por",
      project: "Proyecto",
      calculation: "Cálculo",
      projectLead: "te solicita acceso a este proyecto.",
      calculationLead: "te solicita acceso a este cálculo.",
      alreadyAccepted: "Esta solicitud ya ha sido aceptada.",
      alreadyRejected: "Esta solicitud ya ha sido rechazada.",
    },
    errors: {
      notFound: "Solicitud de acceso no encontrada.",
      readNotAllowed: "No tienes permiso para ver esta solicitud de acceso.",
      alreadyExists: "Ya has solicitado acceso: el propietario aún no ha respondido.",
      alreadyShared: "Ya se ha compartido contigo: vuelve a cargar la página.",
      alreadyAccepted: "Esta solicitud de acceso ya ha sido aceptada.",
      alreadyRejected: "Esta solicitud de acceso ya ha sido rechazada.",
    },
  },

  notifications: {
    title: "Notificaciones",
    projectSharingRequest: (user: string, project: string) =>
      `${user} solicita acceso al proyecto ${project}`,
    calculationSharingRequest: (user: string, calculation: string) =>
      `${user} solicita acceso al cálculo ${calculation}`,
    projectSharingInvite: (user: string, project: string) =>
      `${user} te ha invitado al proyecto ${project}`,
    calculationSharingInvite: (user: string, calculation: string) =>
      `${user} te ha invitado al cálculo ${calculation}`,
  },

  users: {
    add: "Agregar usuario",
    all: "Todos los usuarios",
    plural: "Usuarios",
    remove: "Eliminar usuario",
    invite: "Invitar usuario",
    search: "Buscar usuarios",
    usersEmail: "Correo electrónico de los usuarios",
    usersName: "Nombre de los usuarios",

    questions: {},

    pending: {
      plural: "Usuarios pendientes",
      edit: "Editar usuario pendiente",
      delete: "Eliminar usuario pendiente",
      createdAt: "Añadido",
      info: "Si este correo electrónico aún no tiene una cuenta, el usuario queda pendiente hasta su primer inicio de sesión, cuando recibe estos roles y permisos.",
    },

    errors: {
      notFound: "Usuario no encontrado.",
      alreadyExists: "Ya existe un usuario con este correo electrónico.",
      pendingAlreadyExists: "Ya existe un usuario pendiente con este correo electrónico.",
    },
  },

  items: {
    all: "Todos los ítems",
    singular: "Ítem",
    plural: "Ítems",
    createNew: "Crear nuevo ítem",
    delete: "Eliminar ítem",
    search: "Buscar ítems",
    list: "Lista de ítems",

    questions: {
      delete: "¿Realmente deseas eliminar este ítem?",
    },

    errors: {
      notFound: "Artículo no encontrado.",
      doesNotExist: "El artículo con este código no existe",
      missingCode: "Falta el código del artículo.",
    },
  },

  clients: {
    all: "Todos los clientes",
    plural: "Clientes",
    createNew: "Crear nuevo cliente",
    delete: "Eliminar cliente",
    search: "Buscar clientes",

    questions: {
      delete: "¿Realmente quieres eliminar este cliente?",
    },

    errors: {
      notFound: "Cliente no encontrado.",
      insertNotAllowed: "No tienes permiso para insertar un nuevo cliente.",
      readNotAllowed: "No tienes permiso para leer a este cliente.",
      updateNotAllowed: "No tienes permiso para actualizar a este cliente.",
      deleteNotAllowed: "No tienes permiso para eliminar a este cliente.",
    },
  },

  accessControl: {
    title: "Control de acceso",
    plural: "Control de acceso",
    disclaimer:
      "El control de acceso define qué usuarios pueden usar cada aplicación. No anula el geobloqueo: un usuario permitido sigue siendo rechazado en los países donde la aplicación, o uno de sus módulos, tiene geobloqueo. Para que un usuario acceda desde cualquier país, asígnale el permiso geo_blocking:bypass (por ejemplo, con el rol geo-blocking-bypass): elimina todo geobloqueo, en todas las aplicaciones y módulos.",
    applications: "Aplicaciones",
    modules: "Módulos",
    modulesDisclaimer:
      "El control de acceso de un módulo solo se aplica en la aplicación principal: la aplicación propia del módulo, si existe, sigue su propia pestaña. Un usuario excluido de un módulo no lo ve, ni ve sus cálculos, y no puede abrirlos.",

    settings: {
      lockSwitchLabel: "Restringir la aplicación a usuarios autorizados",
      lockSwitchDescription:
        "Cuando está activado, solo los usuarios con una regla 'permitido' o con acceso de administrador pueden utilizar la aplicación.",
      confirmEnable: "¿Realmente quieres restringir la aplicación solo a usuarios autorizados?",
      confirmDisable: "¿Realmente quieres permitir de nuevo el acceso a todos los usuarios autenticados?",
    },

    // the modules' tab: a module's lock switch, in the main application only
    moduleSettings: {
      lockSwitchLabel: "Restringir el módulo a usuarios autorizados",
      lockSwitchDescription:
        "Cuando está activado, solo los usuarios con una regla 'permitido' o con acceso de administrador pueden utilizar el módulo en la aplicación principal.",
      confirmEnable: "¿Realmente quieres restringir el módulo solo a usuarios autorizados?",
      confirmDisable: "¿Realmente quieres permitir de nuevo el acceso a todos los usuarios autenticados?",
    },

    userAccess: {
      notAllowed: "No permitido",
      causes: {
        rule: "Regla",
        default: "Predeterminado",
        admin: "Administrador",
        locked: "App restringida",
      },
    },

    rules: {
      plural: "Reglas de acceso",
      app: "Aplicación",
      createNew: "Añadir regla",
      search: "Buscar por correo o motivo",
      delete: "Eliminar regla",
      email: "Correo electrónico",
      user: "Usuario",
      status: "Estado",
      statusAllowed: "Permitido",
      statusBlocked: "Bloqueado",
      reason: "Motivo",
      createdAt: "Creado el",
      import: "Importar",
      importTitle: "Importar reglas",
      importFrom: "Importar desde",
      currentStatus: "Estado actual",
      selectAll: "Seleccionar todo",
      importHint:
        "Las reglas ya presentes con un estado diferente no están seleccionadas: selecciónalas para sobrescribir el estado y el motivo actuales.",

      questions: {
        delete: "¿Realmente quieres eliminar esta regla de acceso?",
      },
    },

    forbiddenTitle: "No permitido",
    forbiddenText: "No estás autorizado para acceder a esta aplicación.",
    signedInAs: "Sesión iniciada como",

    errors: {
      forbidden: "No estás autorizado para acceder a esta aplicación.",
      ruleNotFound: "Regla de acceso no encontrada.",
      ruleMissingEmail: "El correo electrónico es obligatorio.",
      ruleInvalidStatus: "El estado debe ser permitido o bloqueado.",
      ruleCannotTargetSelf: "No puedes añadir una regla de acceso para ti mismo.",
      settingsReadNotAllowed: "No tienes permiso para leer la configuración de acceso.",
      settingsUpdateNotAllowed: "No tienes permiso para actualizar la configuración de acceso.",
      ruleReadNotAllowed: "No tienes permiso para leer las reglas de acceso.",
      ruleInsertNotAllowed: "No tienes permiso para crear una regla de acceso.",
      ruleDeleteNotAllowed: "No tienes permiso para eliminar una regla de acceso.",
      lastAdmin: "No puedes eliminar al último usuario capaz de gestionar el control de acceso de la aplicación.",
      invalidScope: "Ámbito de acceso desconocido.",
      invalidModule: "Módulo desconocido.",
    },
  },

  geoBlocking: {
    plural: "Geobloqueo",
    disclaimer:
      "El geobloqueo define desde qué países se puede usar cada aplicación, o un módulo. Las reglas de un módulo se aplican dondequiera que se use el módulo, en la aplicación principal y en la suya; las de una aplicación, solo a esa aplicación. Sin reglas, todos los países están permitidos; en cuanto una regla es 'Permitido', solo los países permitidos. El control de acceso no lo anula: para que un usuario acceda desde cualquier país, asígnale el permiso geo_blocking:bypass (por ejemplo, con el rol geo-blocking-bypass).",
    settings: "Regiones y países",
    modules: "Módulos",
    rules: {
      singular: "Regla",
      target: "Región o país",
      type: "Tipo",
      region: "Región",
      country: "País",
      status: "Estado",
      statusAllowed: "Permitido",
      statusBlocked: "Bloqueado",
      createdAt: "Creada",
    },
    regions: {
      plural: "Regiones",
      singular: "Región",
      countries: "Países",
    },
    countries: {
      plural: "Países",
      code: "Código",
    },
    errors: {
      moduleBlocked: "No tienes permiso para acceder a este módulo.",
      ruleAlreadyExists: "Ya existe una regla para esta región o este país.",
      regionNameAlreadyExists: "Ya existe una región con este nombre.",
      regionInUse: "La región la usa una regla de geobloqueo.",
      blocksSelf: "Este cambio bloquearía tu propio acceso a la aplicación.",
    },
  },

  usageStatistics: {
    plural: "Estadísticas de uso",
    from: "Desde",
    to: "Hasta",
    apps: "Aplicaciones",
    visits: "Visitas",
    description: "Número de usuarios distintos por día (UTC).",
    errors: {
      readNotAllowed: "No tienes permiso para ver las estadísticas de uso.",
      invalidPeriod: "Elige un período válido, de un año como máximo.",
    },
  },

  helpRequests: {
    title: "Ayuda",
    dialogTitle: "Solicitar ayuda",
    intro: "Describe tu problema: nuestro equipo de asesoramiento técnico te responderá por correo electrónico.",
    type: "Tipo",
    subject: "Asunto",
    message: "Mensaje",
    screenshot: "Captura de pantalla",
    attachScreenshot: "Adjuntar la captura de pantalla de la página actual",
    screenshotUnavailable: "No se ha podido realizar la captura de pantalla de la página.",
    attachments: "Archivos adjuntos",
    attachmentsHint: (maxFiles: number, maxMegabytes: number) =>
      `Hasta ${maxFiles} archivos, ${maxMegabytes} MB en total.`,
    removeAttachment: "Eliminar archivo adjunto",
    diagnosticsInfo:
      "Se adjuntará información técnica sobre tu navegador y los últimos errores de la página para ayudarnos a analizar el problema.",
    send: "Enviar",
    success: "Tu solicitud ha sido enviada. Recibirás una confirmación por correo electrónico.",

    errors: {
      invalidForm: "No se ha podido leer la solicitud o supera el tamaño permitido.",
      missingSubject: "El asunto es obligatorio.",
      subjectTooLong: "El asunto es demasiado largo.",
      missingMessage: "El mensaje es obligatorio.",
      messageTooLong: "El mensaje es demasiado largo.",
      tooManyAttachments: (maxFiles: number) => `Puedes adjuntar hasta ${maxFiles} archivos.`,
      attachmentsTooLarge: (maxMegabytes: number) => `Los archivos adjuntos superan ${maxMegabytes} MB en total.`,
      invalidScreenshot: "La captura de pantalla no es válida.",
      invalidDiagnostics: "La información técnica no es válida.",
    },
  },

  feedbacks: {
    title: "Informar a desarrolladores",
    dialogTitle: "Enviar comentarios",
    intro: "Informe de un error o comparta una sugerencia: el equipo de desarrollo la recibirá.",
    types: {
      bug: "Error",
      suggestion: "Sugerencia",
      other: "Otro",
    },
    success: "Gracias, tus comentarios han sido enviados.",

    errors: {
      sendNotAllowed: "No tienes permiso para enviar comentarios.",
      invalidType: "Elige un tipo de comentario válido.",
    },
  },

  home: {
    greeting: (name: string) => `Hola, ${name}`,
    subtitle: "Elige una calculadora para empezar un nuevo cálculo.",
    availableCalculators: "Calculadoras disponibles",
  },

  modules: {
    all: "Todos los módulos",
    plural: "Módulos",
    createNew: "Crear nuevo módulo",
    delete: "Eliminar módulo",
    search: "Buscar módulos",
    singular: "Módulo",

    questions: {
      delete: "¿Realmente quieres eliminar este módulo?",
    },

    errors: {
      notFound: "Módulo no encontrado.",
      insertNotAllowed: "No tienes permisos para insertar un nuevo módulo.",
      readNotAllowed: "No tienes permisos para leer este módulo.",
      updateNotAllowed: "No tienes permisos para modificar este módulo.",
      deleteNotAllowed: "No tienes permisos para eliminar este módulo.",
    },
  },

  categories: {
    all: "Todas las categorías",
    plural: "Categorías",
    createNew: "Crear nueva categoría",
    delete: "Eliminar categoría",
    search: "Buscar categorías",
    view: "Ver categorías",
    edit: "Editar categoría",

    questions: {
      delete: "¿Realmente quieres eliminar esta categoría?",
    },

    errors: {
      notFound: "Categoría no encontrada.",
      insertNotAllowed: "No tienes permisos para insertar una nueva categoría.",
      readNotAllowed: "No tienes permisos para leer esta categoría.",
      updateNotAllowed: "No tienes permisos para modificar esta categoría.",
      deleteNotAllowed: "No tienes permisos para eliminar esta categoría.",
    },
  },

  roles: {
    plural: "Roles",
  },

  permissions: {
    plural: "Permisos",
  },

  secrets: {
    plural: "Secrets",

    errors: {
      missingValue: "Falta el valor de secret.",
    },
  },

  adminDashboard: {
    projects: "Consulta y gestiona los proyectos de todos los usuarios",
    calculations: "Consulta y gestiona los cálculos de todos los usuarios",
    modules: "Configura los módulos de cálculo y sus ajustes",
    categories: "Gestiona las categorías usadas para agrupar los módulos",
    items: "Gestiona el catálogo de artículos",
    clients: "Gestiona los clientes API, sus secretos y permisos",
    users: "Gestiona los usuarios y sus roles",
    accessControl: "Restringe las aplicaciones y gestiona los usuarios permitidos o bloqueados",
    usageStatistics: "Consulta cuántos usuarios acceden cada día a las aplicaciones y a los módulos",
    geoBlocking: "Permite o bloquea aplicaciones y módulos por país",
  },

  common: {
    date: "Fecha",
    active: "Activo",
    add: "Agregar",
    errorInvalidVisibility: "Visibilidad no válida: elige pública, interna u oculta.",
    administration: "Administración",
    all: "Todo",
    alreadyPresent: "Ya presente",
    angle: "Ángulo",
    angles: "Ángulos",
    archived: "Archivado",
    author: "Autor",
    automaticSync: "Sincronización automática",
    clamped: "Fijado",
    thickness: "Espesor",
    curve: "Curva",
    back: "Atrás",
    baseHeight: "Altura base",
    cancel: "Cancelar",
    close: "Cerrar",
    code: "Código",
    clearPoints: "Borrar puntos",
    undo: "Deshacer",
    confirmed: "Confirmado",
    coordinates: "Coordenadas",
    copy: "Copia",
    country: "País",
    create: "Crear",
    created: "Creado",
    createNew: "Crear nuevo",
    dataHasBeenSavedSuccessfully: "Los datos se han guardado exitosamente.",
    download: "Descargar",
    delete: "Eliminar",
    description: "Descripción",
    designer: "Diseñador",
    details: "Detalles",
    dragAndDropOrUploadAnImage: "Arrastra y suelta o haz clic para subir una imagen",
    dropIt: "¡Suelta aquí!",
    duplicate: "Duplicar",
    edit: "Editar",
    editor: "Editor",
    email: "Correo electrónico",
    error: "Error",
    errorLoadingData: "Error al cargar los datos",
    failed: "Fallido",
    fieldContainsInvalidValueInsertBiggerOne: "El campo contiene un valor inválido, inserta uno mayor.",
    colorHexInvalid: "Por favor, introduce un color HEX válido en formato #RRGGBB.",
    fieldInvalidValue: "El campo contiene un valor inválido.",
    fixed: "Fijo",
    forbidden: "No permitido",
    generalData: "Datos generales",
    genericError: "Ha ocurrido un error, por favor intenta de nuevo.",
    hidden: "Oculto",
    home: "Inicio",
    installed: "Instalado",
    isVisible: "Es visible",
    languages: {
      singular: "Idioma",
      plural: "Idiomas",
      arabic: "Árabe",
      czech: "Checo",
      danish: "Danés",
      german: "Alemán",
      greek: "Griego",
      english: "Inglés",
      spanish: "Español",
      estonian: "Estonio",
      finnish: "Finés",
      french: "Francés",
      croatian: "Croata",
      hungarian: "Húngaro",
      icelandic: "Islandés",
      italian: "Italiano",
      japanese: "Japonés",
      lithuanian: "Lituano",
      latvian: "Letón",
      dutch: "Neerlandés",
      norwegian: "Noruego",
      polish: "Polaco",
      portugese: "Portugués",
      russian: "Ruso",
      slovak: "Eslovaco",
      slovenian: "Esloveno",
      swedish: "Sueco",
      turkish: "Turco",
      chinese: "Chino",
      korean: "Coreano",
    },
    translations: "Traducciones",
    lastAccess: "Último acceso",
    lastModified: "Última modificación",
    lastName: "Apellido",
    lastSync: "Última sincronización",
    loading: "Cargamento...",
    location: "Ubicación",
    login: "Iniciar sesión",
    logout: "Cerrar sesión",
    minimumOrderQuantity: "Cantidad mínima de pedido",
    minOrderQuantity: "Cant. mín. pedido",
    myProfile: "Mi perfil",
    name: "Nombre",
    next: "Siguiente",
    never: "nunca",
    no: "No",
    noItemsFound: "No ítems encontrados",
    notFound: "No encontrado",
    offer: "Oferta",
    ok: "Ok",
    onlyImagesAreAllowed: "Solo se permiten imágenes.",
    open: "Abrir",
    optional: "opcional",
    order: "Orden",
    overwrite: "Sobrescribir",
    owner: "Propietario",
    print: "Imprimir",
    public: "Público",
    everyOne: "Todos",
    remove: "Eliminar",
    removeImage: "Eliminar imagen",
    reset: "Restablecer",
    resetToDefault: "Restablecer a los valores predeterminados",
    resetView: "Restablecer vista",
    resourceNotFound: "El recurso que buscas no se ha encontrado.",
    save: "Guardar",
    rothoblaasUsers: "Usuarios de Rothoblaas",
    search: "Buscar",
    select: "Selecciona",
    selectProjectInwhichCreateCopy: "Selecciona el proyecto en el que crear la copia",
    sentAt: "Enviado el",
    sessionExpired: "Sesión expirada",
    settings: "Configuraciones",
    shared: "Compartido",
    sharedByMe: "Compartido por mí",
    sharedWithMe: "Compartido conmigo",
    canEdit: "Puede editar",
    viewOnly: "Solo visualización",
    sharedViaProject: "Compartido a través del proyecto",
    shape: "Forma",
    status: "Estado",
    sync: "Sincronizar",
    testEnvironment: "Entorno de prueba",
    toBeInstalled: "Por instalar",
    toInspect: "Por inspeccionar",
    value: "Valor",
    visible: "Visible",
    visibility: "Visibilidad",
    unitSystem: "Sistema de unidades",
    unitSystemMetric: "Métrico",
    unitSystemImperial: "Imperial",
    yes: "Sí",
    chooseSecondPointDifferentXY: "Elige un segundo punto con x e y diferentes.",
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    youAreNotAllowed: "No estás autorizado para acceder a este recurso.",
    category: "Categoría",
    categories: "Categorías",
    components: "Componentes",
    customer: "Cliente",
    draw: "Dibujar",
    extensions: "Extensiones",
    height: "Altura",
    length: "Longitud",
    screwDiameter: "Diámetro de tornillo",
    screwHeadHeight: "Altura de cabeza de tornillo",
    loop: "Bucle",
    mappings: "Asignaciones",
    max: "Máximo",
    min: "Mínimo",
    minHeight: "Altura mínima",
    minValue: "Valor mínimo",
    minWidth: "Anchura mínima",
    pitch: "Pendiente",
    roof: "Techo",
    rothoblaas: "Rothoblaas",
    span: "Luz",
    stripeLength: "Longitud de tira",
    substructureHeight: "Altura de subestructura",
    substructures: "Subestructuras",
    supports: "Soportes",
    supportHeight: "Altura del soporte",
    type: "Tipo",
    width: "Anchura",
    quantity: "Cantidad",
    default: "Predeterminado",
    degrees: "Grados",
    factor: "Factor",
    gap: "Separación",
    finish: "Finalizar",
    groovingMinHeight: "Altura mín. de ranura",
    groovingMaxHeight: "Altura máx. de ranura",
    groovingThickness: "Espesor de ranura",
    help: "Ayuda",
    hide: "Ocultar",
    intermediate: "Intermedio",
    maxHeight: "Altura máxima",
    show: "Mostrar",
    spans: "Luces",
    support: "Soporte",
    unlock: "Desbloquear",
    update: "Actualizar",
    from: "Desde",
    meters: "Metros",
    to: "Hasta",
    area: "Área",
    units: "Unidades",
    volume: "Volumen",
    preview: "Vista previa",
    summary: "Resumen",

    unitM: "m",
    unitCm: "cm",
    unitGPa: "GPa",
    unitMm: "mm",
    unitInch: "in",
    unitN: "N",
    unitNMm: "N·mm",
    unitDeg: "°",
    unitPcs: "uds",
    unitM2: "m²",
    unitM4: "m^4",
    unitKn: "kN",
    unitKnM2: "kN/m²",
    unitKnM: "kN/m",
    unitMpa: "MPa",
    titlePreview: "Vista previa",
    wasteFactor: "Factor de desperdicio",

    cookiePolicy: "Política de cookies",
    privacyPolicy: "Política de privacidad",
    termsAndConditions: "Términos y condiciones",
    writePermissions: "Permisos de escritura",
    invitationHasBeenSentSuccessfully: "Invitación enviada con éxito.",
    resendInvite: "Reenviar invitación",
    cancelInviteQuestion: "¿Realmente deseas cancelar esta invitación?",
    deleteInviteQuestion: "¿Realmente deseas eliminar esta invitación?",
    invite: "Invitar",
    singleOptionAvailable:
      "Campo deshabilitado: solo hay una opción seleccionable disponible para la configuración elegida.",
  },
  concreteResinModule: {
    addCalculation: "Guardar cálculo",
    anchorMapping: "Asignación de anclaje",
    anchorMappings: "Asignaciones de anclaje",
    cartridges: "Cartuchos",
    chemicalAnchor: "Anclaje químico",
    chemicalAnchors: "Anclajes químicos",
    downloadPdf: "Descargar PDF",
    downloadPdfNoCalculationsSavedHint: "Guarda al menos un cálculo para descargar el PDF.",
    holeDepth: "Profundidad del agujero ( h₁ )",
    holeDiameter: "Diámetro del agujero ( d₀ )",
    holeDiameterImperial: "Broca EE. UU. [in]",
    holeDiameterImperialInfo:
      'Calculada a partir del diámetro del agujero: la broca de 1/64" más pequeña no inferior a él.',
    holeNumbers: "Número de agujeros",
    holes: "agujeros",
    instruction2:
      "Este cálculo estima la cantidad de resina según el factor de exceso seleccionado. El exceso suele depender de la aplicación y de la experiencia del instalador.",
    instructionsSummaryPrefix:
      "La cantidad de resina se estima según el llenado del agujero indicado en el ETA del producto: desde el fondo del agujero limpio, rellénalo de adhesivo hasta aproximadamente dos tercios, retirando lentamente la boquilla de mezcla estática a medida que se llena para evitar bolsas de aire; si la boquilla no alcanza el fondo del agujero, utiliza una boquilla de extensión adecuada. Aplica e inyecta el producto según la documentación de Rothoblaas para el uso previsto. Rothoblaas se reserva el derecho de cambiar métodos de cálculo, diseños o modelos sin previo aviso ni responsabilidad; los recursos de diseño más actualizados están en",
    instructionsSummarySuffix: ".",
    liters: "Litros",
    mlQuantity: "Cantidad ml",
    noCalculationsSaved: "Ningún cálculo guardado",
    overageFactor: "Factor de desperdicio",
    rodDiameter: "Diámetro de la varilla ( d )",
    titleCalculationsSaved: "Cálculos guardados",
    titleHoleGeometry: "Geometría del agujero",
    titleInstructionsAndNotes: "Instrucciones y notas",
    titleSummary: "Resumen",
    totalVolumeRequired: "Volumen total requerido",
    unitOverageFactor: "%",
    unitSystemImperial: "Imperial",
    unitSystemMetric: "Métrico",
    waste: "desperdicio",
    cartridgesShort: "cart.",
    unitLiters: "L",
    errors: {
      anchorInUseByMapping: "El anclaje se usa en una asignación: elimina primero sus asignaciones",
      anchorInUseByEntry: "El anclaje se usa en cálculos guardados: ponlo en Oculto en lugar de eliminarlo",
      anchorNotFound: "No se ha encontrado el anclaje químico.",
      anchorNotAvailable: "El anclaje químico elegido ya no está disponible: elige otro.",
      anchorAlreadyPresent: "El artículo ya es un anclaje químico.",
      mappingAlreadyPresent: "El anclaje químico ya tiene una asignación con estos diámetros.",
      invalidRodDiameter: "El diámetro de la varilla no está disponible para el anclaje químico elegido.",
      invalidHoleDiameter: "El diámetro del agujero no corresponde al diámetro de varilla elegido.",
      mappingNotFound: "No se ha encontrado la asignación de anclaje.",
      entryMissingChemicalAnchor: "Elige un anclaje químico antes de guardar el cálculo",
      entryIncompleteCalculation: "Completa todos los campos antes de guardar el cálculo",
    },
  },

  usLiftingCalculatorModule: {
    calculator: {
      aboveTarget: (target: number) => `El izado está dentro de la capacidad pero supera el objetivo del ${target} %.`,
      allSavedMembersCleared: "Se borraron todos los miembros guardados.",
      allowAnotherDevice: "Permitir otro dispositivo si el seleccionado no ofrece una configuración verificada.",
      alternateDevice: "No existe una configuración verificada con el dispositivo seleccionado.",
      anchorPointsDetail: "Número de puntos de anclaje",
      angleDiagramAlt: "Intervalos del ángulo de izado del dispositivo y del tipo de izado seleccionados",
      angleDiagramTitle: "Intervalos del ángulo de izado",
      angleFromHorizontal: "Ángulo desde la horizontal",
      angleLegendTitle: "Leyenda de los intervalos de ángulo",
      anglePhotoTitle: "Ángulo de izado",
      applicableLifts: "Izados aplicables",
      automatic: "Automático",
      backToModeSelection: "Volver a la selección de modo",
      beamDepth: "Canto de la viga",
      beamDepthDetail: "Canto de la viga",
      beamOrientationAlt: (orientation: string) => `RAPTOR en la viga: ${orientation}`,
      beamSpecifications: (depth: string, width: string) => `canto ${depth} × ancho ${width}`,
      beamWidth: "Ancho de viga",
      beamWidthDetail: "Ancho de viga",
      buildingPdf: "Generando el PDF…",
      capacityTablePreview: "Vista previa de capacidad de un dispositivo de elevación",
      capacityUtilization: "Utilización de capacidad",
      clearSavedMembers: "Borrar miembros guardados",
      clearSavedMembersConfirm:
        "¿Seguro que deseas borrar todos los miembros guardados? Se eliminarán los izados guardados de todos los modos y no se puede deshacer.",
      clearSavedMembersTitle: "¿Borrar los miembros guardados?",
      cltPanelDepthDetail: "Espesor del panel CLT",
      cltThickness: "Espesor de CLT",
      columnSpecifications: (width: string) => `ancho ${width}`,
      columnWidth: "Ancho del pilar",
      columnWidthDetail: "Ancho del pilar",
      completedExcelTemplate: "Plantilla de Excel completada",
      contactTechSupport: "Contactar soporte técnico",
      contactUs: "Contacto",
      copied: "Copiado",
      copyFailed: "Error al copiar",
      copyTable: "Copiar tabla",
      customLiftDesign: "Diseño de elevación personalizado",
      datasheetNoticeText:
        "Consulta la ficha técnica de cada dispositivo de elevación para conocer las condiciones de uso, las instrucciones de instalación, las capacidades de los elementos de fijación, las orientaciones de elevación aprobadas y todos los requisitos y limitaciones aplicables.",
      datasheetNoticeTitle: "Esta herramienta no sustituye a las fichas técnicas aplicables.",
      decreaseByEighth: "Reducir 1/8 de pulgada",
      deviceFamilyUnavailable: (name: string) => `${name} — No disponible`,
      deviceImageAlt: "Dispositivo de elevación seleccionado",
      devices: "Dispositivos de elevación",
      dimensionFormatError: "Introduce la dimensión en pulgadas, p. ej. 4 1/8.",
      downloadExcel: "Descargar Excel",
      downloadTemplate: "Descargar plantilla",
      downloaded: "Descargado",
      draftOpened: "Borrador abierto",
      eServiceTemplateRequired: "Se requiere la plantilla eService aprobada",
      eServiceXlsx: "eService XLSX",
      editingMember: (name: string) => `Editando ${name}`,
      engineeringReviewRequired: "Se requiere revisión técnica",
      enlargeImage: "Ampliar imagen",
      enterLiftName: "Introduce un nombre de izado.",
      enterNameOrSave: "Introduce un nombre de miembro o selecciona Guardar izado para asignarlo en la ventana.",
      enterWeightBeforeSaving: "Introduce el peso del miembro izado antes de guardar.",
      enterWeightForRecommendation: "Introduce el peso del miembro izado para obtener una recomendación.",
      export: "Exportar",
      exportFailed: "Error de exportación",
      exportFilledExcel: "Exportar Excel completado",
      exportHelp: "Elige qué enviar al informe PDF.",
      exportMemberExcel: "Exportar Excel de miembros",
      exportReport: "Exportar informe",
      fastenerTakeoff: "Medición de fijaciones",
      guidanceApplication: "Aplicación",
      guidanceInstallation: (device: string) => `Instalación ${device}`,
      guidanceLabel: "Indicaciones de instalación",
      guidanceLoadDirections: "Direcciones de carga permitidas",
      guidanceMinimumDistances: "Distancias mínimas",
      guidanceMinimumUnavailable: "Introduce un ancho válido para ver las distancias mínimas.",
      howRecommendationsSelected: "Cómo se seleccionan las recomendaciones",
      increaseByEighth: "Aumentar 1/8 de pulgada",
      installation: "Instalación",
      installationIllustrations: "Ilustraciones de instalación",
      installationIllustrationsTitle: "WASP / WASPL - Ilustraciones de instalación",
      installationLine: (installation: string) => `Instalación: ${installation}`,
      installationType: "Tipo de instalación",
      installationTypeColumn: "Tipo de instalación",
      installationTypes: {
        inclined: "Inclinado",
        perpendicular: "Perpendicular con fresado",
        standard: "Estándar",
      },
      issues: {
        angleUnsupported: "Selecciona un ángulo de izado admitido. El ángulo introducido no se ha sustituido.",
        beamDepthMaximum: (maximum: string) => `El canto de la viga no debe superar ${maximum}.`,
        beamDepthMinimum: (minimum: string) =>
          `El canto de la viga debe ser de al menos ${minimum}. La dimensión introducida no se ha modificado.`,
        beamDepthPositive:
          "El canto de la viga debe ser un número positivo. La dimensión introducida no se ha modificado.",
        beamOrientationRestricted:
          "La orientación RAPTOR seleccionada está limitada por el ancho de la viga y el número de tornillos.",
        beamWidthMaximum: (maximum: string) => `El ancho de la viga no debe superar ${maximum}.`,
        beamWidthMinimum: (minimum: string) =>
          `El ancho de la viga debe ser de al menos ${minimum}. El ancho introducido no se ha modificado.`,
        beamWidthPositive: "El ancho de la viga debe ser un número positivo. El ancho introducido no se ha modificado.",
        cltEdgeGrainMinimum: (minimum: string) =>
          `El espesor de CLT debe ser de al menos ${minimum} para izados por el canto y por volteo.`,
        cltFaceGrainMinimum: (minimum: string, device: string) =>
          `El espesor de CLT debe ser de al menos ${minimum} para izados ${device} por la cara.`,
        cltSlabMaximum: (maximum: string) => `El espesor de la losa de CLT no debe superar ${maximum}.`,
        cltThicknessPositive:
          "El espesor de CLT debe ser un número positivo. La dimensión introducida no se ha modificado.",
        columnWidthMinimum: (minimum: string) =>
          `El ancho del pilar debe ser de al menos ${minimum}. La dimensión introducida no se ha modificado.`,
        columnWidthPositive:
          "El ancho del pilar debe ser un número positivo. La dimensión introducida no se ha modificado.",
        memberDepthMaximum: (maximum: string) => `La dimensión del elemento no debe superar ${maximum}.`,
      },
      liftName: "Nombre del izado",
      liftNamePlaceholder: "Introduce un nombre de izado",
      liftNotOverwritten: "No se pudo sobrescribir el izado.",
      liftNotSaved: "No se pudo guardar el izado.",
      liftOverwritten: (name: string) => `${name} se sobrescribió con sus propiedades y recomendación actuales.`,
      liftRecommendation: "Recomendación de elevación",
      liftSaved: (name: string) => `${name} se guardó con sus propiedades y recomendación actuales.`,
      liftType: "Tipo de izado",
      liftTypeFallbackNote:
        "No hay ninguna configuración verificada para el volteo. Se muestra como alternativa el izado vertical por la cara; esto no verifica una operación de volteo.",
      liftedMemberWeight: "Peso del miembro izado",
      liftingDevice: "Dispositivo de elevación",
      liftingPoints: "Puntos de elevación",
      liftingSetup: "Configuración de izado",
      liftingSetupValue: (screws: number | string, devices: number | string) =>
        `${screws} tornillos × ${devices} dispositivos`,
      marginLine: (margin: string) => `Margen: ${margin}`,
      memberLoaded: (name: string) => `${name} se ha cargado.`,
      memberName: "Nombre del miembro",
      memberNamePlaceholder: "Introduce un nombre de miembro",
      memberSpecifications: "Especificaciones del miembro",
      memberType: "Tipo de miembro",
      memberTypes: {
        beam: "Viga",
        "clt-slab": "CLT Slab",
        "clt-wall": "CLT Wall",
        column: "Pilar",
      },
      memberWeight: "Peso del miembro",
      memberWorkspace: "Área de miembros",
      minimumDistancesCaption: (mounting: string, screwCount: number | string) =>
        `${mounting} · Tornillos por dispositivo: ${screwCount}`,
      mode1Description: "Configura y verifica un izado específico.",
      mode1Pill: "Modo 1",
      mode2Description: "Introduce un elemento para encontrar opciones de izado.",
      mode2Eyebrow: "Modo 2: Recomendación de elevación",
      mode2Pill: "Modo 2",
      mode3Description: "Carga varios elementos para obtener recomendaciones de izado.",
      mode3Pill: "Modo 3",
      modeSelection: "Selección de modo",
      moduleTitle: "US Lifting Calculator",
      nameInUse: (name: string) =>
        `Ya hay un izado guardado llamado «${name}». Sobrescríbelo o introduce otro nombre.`,
      newMember: "Nuevo miembro",
      noCapacitySubtitle: "La combinación seleccionada no aparece en las tablas de resumen integradas.",
      noCapacityTable: "No hay una tabla de capacidad para esta configuración.",
      noSavedLifts: "No hay izados guardados disponibles.",
      noSavedMembers: "No hay miembros guardados",
      noSavedMembersToProcess: "Guarda primero un elemento en el Modo 1 o 2.",
      noSavedRecommendationLifts: "No hay izados guardados del Modo 2.",
      noScrews: (filters: string[]) => `No hay tornillos ${filters.join(" y ")}`,
      noScrewsListed: "No hay tornillos indicados",
      notAvailableYet: "Disponible en una versión posterior.",
      numberOfAnchorPoints: "Número de puntos de anclaje",
      numberOfLiftingDevices: "Número de dispositivos de elevación",
      numberOfRaptors: "Número de RAPTOR",
      openEmailEnquiry: "Abrir consulta por correo",
      openInMode1: "Abrir en Modo 1",
      openInMode2: "Abrir en Modo 2",
      openOrCreateMember: "Abrir o crear un miembro",
      openSavedSelect:
        "Abre cada izado guardado, selecciona una opción con capacidad verificada para la medición y vuelve a guardarlo.",
      openSupportMenu: "Abrir el menú de soporte",
      optionAlternativeTitle: (slot: number, label: string) => `Opción ${slot} - ${label} alternativa`,
      optionTitle: (slot: number, label: string) => `Opción ${slot} - ${label}`,
      orientation: "Orientación",
      orientationUnavailable: (orientation: string) => `${orientation} - No disponible con este ancho de viga`,
      overwriteExisting: "Sobrescribir existente",
      overwritten: "Sobrescrito",
      parallel: "Paralela",
      parallelMounting: "Montaje paralelo",
      perpendicular: "Perpendicular",
      perpendicularMounting: "Montaje perpendicular",
      perpendicularWithMillingShort: "Perp. con fresado",
      printCurrentScreen: "Imprimir pantalla actual",
      printCurrentScreenHelp: "El izado en pantalla, con su resultado y sus imágenes de referencia.",
      printPdf: "Imprimir PDF",
      printSavedMembers: "Imprimir miembros guardados",
      printSavedMembersHelp:
        "Cada izado guardado se imprime en su propia página, seguido de una medición de fijaciones combinada cuando hay izados del Modo 2.",
      processList: "Procesar lista",
      processSavedMembers: "Procesar lista de elementos guardados",
      productCode: "Código de producto",
      productPage: "Página del producto",
      properties: "Propiedades",
      quantityRequired: "Cantidad requerida",
      raptors: "RAPTOR",
      readyToSave: "La recomendación actual está lista para guardarse.",
      reasons: {
        noAdditionalVgspl: "No hay otra cantidad o longitud de tornillos VGSPL verificada con estos ajustes.",
        noVgsplAlternative: "No hay ninguna alternativa VGSPL verificada con estos ajustes.",
        notEligible: (family: string) => `No hay ninguna configuración ${family} admisible con estos ajustes.`,
        notWithinCapacity: (family: string) =>
          `Ninguna configuración ${family} está dentro de la capacidad con estos ajustes.`,
        waspNoAlternative: (series: string) =>
          `No hay ninguna alternativa ${series} verificada con WASP con estos ajustes.`,
        waspNotWithinCapacity: (series: string) =>
          `Ninguna configuración ${series} está dentro de la capacidad con estos ajustes.`,
      },
      recommendedConfigurations: "Configuraciones recomendadas",
      recommendedLiftConfiguration: "Configuración de izado recomendada",
      requiredFields: (fields: string) => `necesarios: ${fields}`,
      reset: "Restablecer",
      result: "Resultado",
      review: {
        allSupportedDevices: "Todos los dispositivos compatibles",
        determinism:
          "Es una búsqueda determinista en tablas, no una IA en vivo. Los selectores muestran ángulos tabulados individuales; los diagramas de intervalos son referencias. Esta actualización no constituye aprobación técnica.",
        deviceScope: "Dispositivos incluidos en esta revisión",
        methodChecks:
          "Comprueba dimensiones, orientación, ángulo tabulado, longitud y cantidad de tornillos antes de ordenar. Sin datos obligatorios no se verifica el izado.",
        methodRaptor:
          "Los izados RAPTOR de vigas y por la cara comparan VGSPL, una alternativa VGSPL con otra cantidad o longitud de tornillos y HBSPL. Los izados RAPTOR MINI y los izados RAPTOR por el canto o por volteo comparan solo VGSPL y HBSPL.",
        methodWasp: "Si se permite: WASP → WASPL → RAPTOR MINI → RAPTOR.",
        raptorNote: "Las imágenes de aplicación y de distancias mínimas de RAPTOR se muestran con los resultados.",
        raptorOnly: "Solo RAPTOR",
        scopeNote: "Limitar a RAPTOR restringe la selección y la búsqueda; no implica validación.",
      },
      reviewSettings: "Recomendaciones y ajustes de revisión",
      riggingBasis:
        "Capacidad total del aparejo = número de anclajes x capacidad de un dispositivo en el límite inferior, el más conservador, del intervalo de ángulo seleccionado (o exactamente 90 grados). Se supone un reparto uniforme de la carga, que esta herramienta no confirma.",
      riggingMemo:
        "Esta orientación no está aprobada en la ficha técnica. Son posibles aplicaciones con más puntos de anclaje que las configuraciones publicadas si las diseña un ingeniero de aparejos cualificado externo.",
      saveLift: "Guardar izado",
      saveMember: "Guardar miembro",
      saveMode1Lift: "Guardar izado del Modo 1",
      saveMode2Lift: "Guardar izado del Modo 2",
      saved: "Guardado",
      savedLiftConfigurations: "Configuraciones de izado guardadas",
      savedLiftCount: (count: number) => (count === 1 ? "1 izado guardado" : `${count} izados guardados`),
      savedLiftLoaded:
        "Izado guardado cargado. Cambia cualquier propiedad y selecciona Guardar izado para actualizarlo.",
      savedLifts: "Izados guardados",
      savedLiftsEyebrow: "Izados guardados",
      savedLiftsFileSuffix: "izados-guardados",
      savedMembers: "Miembros guardados",
      savedMembersAvailable: (count: number) => `Miembros guardados disponibles: ${count}`,
      savedMembersDescription:
        "Genere recomendaciones a partir de las propiedades guardadas sin cargar Excel. No se modifican los izados guardados.",
      savedOn: (mode: string, date: string) => `${mode} · Guardado el ${date}`,
      savedPill: "Guardados",
      savedRecommendations: "Recomendaciones guardadas",
      savedResult: "Resultado guardado",
      screw: "Tornillo",
      screwCountBeamWidth: (count: number) => `${count} - No disponible con este ancho de viga`,
      screwCountNotListed: (count: number) => `${count} — no indicado`,
      screwLength: "Longitud del tornillo (in)",
      screwLengthOption: (length: string, code: string) => `${length} — ${code}`,
      screwLengthPhotoAlt: "Longitud del tornillo respecto al espesor del elemento",
      screwLengthReferenceText:
        "En las aplicaciones de izado con la componente de carga perpendicular a la fibra de la madera, para alcanzar la capacidad máxima del sistema de izado se recomienda que la longitud del tornillo empotrada en la madera, sin la placa RAPTOR de 10 mm, supere 0,7·d (L - 10 mm > 0,7·d; d = espesor del elemento de madera) para evitar fisuras. Si no se cumple esta relación, las comprobaciones críticas a cortante y de deformación pueden realizarse según AWC-NDS, capítulos 3 y 5.",
      screwLengthReferenceTitle: "Influencia de la relación entre la longitud del tornillo y el espesor del elemento",
      screwLengthViewerTitle: "Referencia de longitud del tornillo",
      screwType: "Tipo de tornillo",
      screwsAtLeast: (minimum: string) => `de al menos ${minimum}`,
      screwsColumn: "N.º tornillos",
      screwsLongerThan: (minimum: string) => `de más de ${minimum}`,
      screwsPerDevice: "Tornillos por dispositivo",
      screwsPerDeviceDetail: "Tornillos por dispositivo",
      screwsPerRaptor: "Tornillos por RAPTOR",
      screwsPerRaptorFigure: (count: number) => `${count} tornillos por RAPTOR`,
      screwsPerRaptorFigureAlt: (count: number) => `RAPTOR instalado con ${count} tornillos`,
      screwsRow: (count: number) => `${count} tornillos`,
      screwsShorterThan: (maximum: string) => `de menos de ${maximum}`,
      selectCompletedTemplate: "Selecciona una plantilla completada para comenzar.",
      selectListedScrew: "Selecciona una configuración de tornillos de la lista antes de guardar.",
      selectNewMember: "Selecciona Nuevo miembro para crear un izado guardado, o abre un miembro guardado.",
      selectedConfiguration: "Configuración seleccionada",
      shortScrewEmbedment: "La fijación actual no penetra 0,7*d",
      shortScrewRatio: (length: string) =>
        `El tornillo seleccionado es más corto que la longitud recomendada de 0,7d (${length}). Realice las comprobaciones críticas a cortante y de deformación según AWC-NDS, capítulos 3 y 5. Esta herramienta no realiza estas comprobaciones.`,
      specificGravity: "Gravedad específica",
      statusAboveTarget: "Verificado - por encima de la utilización objetivo",
      statusAlternativeLift: "Capacidad verificada - izado alternativo",
      statusDimension: "Dimensión fuera de rango",
      statusEngineeringReview: "Verificado — Revisión de ingeniería necesaria",
      statusNoCapacity: "Capacidad no indicada",
      statusNotVerified: "No verificado",
      statusOrientation: "Orientación no disponible",
      statusPending: "Pendiente",
      statusReview: "Revisión",
      statusUnavailable: "No disponible",
      statusVerified: "Verificado",
      submitFeedback: "Enviar comentarios",
      takeoffEmailBasis: "Base de cálculo: USA - NDS",
      takeoffEmailIncluded: (count: number) => `Izados guardados del Modo 2 incluidos: ${count}`,
      takeoffEmailRequest: "Por favor, revisen la disponibilidad y los precios de las cantidades indicadas.",
      takeoffEmailSubject: "US Lifting Calculator - medición de fijaciones",
      takeoffEmailTotal: (total: string) => `Total de fijaciones: ${total}`,
      takeoffEmailUnits: "Unidades: imperiales (lb / in)",
      takeoffLength: "Longitud",
      takeoffTotal: "TOTAL",
      targetUtilization: "Definir utilización objetivo (%)",
      targetUtilizationShort: "Utilización objetivo",
      techSupportSubject: "US Lifting Calculator - soporte técnico",
      template: "Plantilla",
      templateDownloaded: (device: string) => `Plantilla ${device} descargada en unidades imperiales (lb / in).`,
      totalCapacity: "Capacidad total",
      totalFasteners: "Total de fijaciones",
      totalRiggingCapacity: "Capacidad total del aparejo",
      totalScrews: "Tornillos totales",
      unitIn: "in",
      unitLb: "lb",
      upload: {
        columns: {
          anchors: "N.º anclajes",
          angle: "Ángulo",
          liftingDevice: "Dispositivo de elevación",
          memberName: "Nombre del elemento",
          memberType: "Tipo de miembro",
          screw: "Tornillo",
          screwCount: "N.º tornillos",
          status: "Estado",
          totalCapacity: "Capacidad de elevación total",
          totalScrews: "Número total de tornillos",
          utilization: "Utilización",
          weight: "Peso",
        },
        defaultAngle: "(predeterminado)",
        enteredTableReference: "Ángulo introducido → referencia de tabla",
        externalRigging: "Se requiere un diseño de aparejo externo",
        faceGrainAlternative: "Alternativa vertical por la cara; no verifica el volteo.",
        fields: {
          angle: "Ángulo de izado",
          columnNotWithWasp: "El pilar no está disponible con WASP / WASPL",
          columnRequiresRaptorMini: "El pilar requiere RAPTOR MINI",
          device: "Dispositivo de elevación",
          installationType: "Tipo de instalación",
          liftType: "Tipo de izado",
          memberType: "Tipo de miembro",
          thicknessDepth: "Espesor / canto",
          weight: "Peso",
        },
        firstRows: (shown: number, total: number) =>
          `Se muestran los primeros ${shown} de ${total} elementos. El Excel completado los incluye todos.`,
        issues: {
          angleColumn: "El izado de pilares requiere exactamente 90 grados desde la horizontal.",
          angleNotNumeric:
            "El ángulo de izado debe ser un ángulo numérico desde la horizontal o un intervalo mostrado para el dispositivo seleccionado.",
          angleOutOfRange: (minimum: number) =>
            `El ángulo de izado debe estar entre ${minimum} y 90 grados desde la horizontal para el dispositivo seleccionado. El ángulo introducido no se ha modificado.`,
          angleOutsideBand:
            "El ángulo de eslinga introducido está fuera del intervalo de tabla seleccionado. Vuelve a introducir y calcular el ángulo real; un ángulo de aparejo alternativo no está verificado.",
          angleRiggingMismatch:
            "El ángulo propuesto no coincide con el aparejo introducido. Corrige los datos antes de usar un resultado.",
          beamWidthRequired: "El ancho de viga es obligatorio en las filas de vigas.",
          materialConflict: (text: string) =>
            `El nombre del material y la gravedad específica no coinciden en "${text}". Confirma el material previsto; no se ha sustituido ningún valor.`,
          materialUnrecognized: (text: string) =>
            `Material de madera no reconocido "${text}". Usa un material de la lista, DF, SYP o una gravedad específica numérica admitida.`,
          materialUnsupported: (text: string, choices: string) =>
            `Gravedad específica no admitida "${text}". Selecciona ${choices}; el valor introducido no se ha sustituido.`,
          orientationInvalid: "La orientación debe ser Paralela o Perpendicular.",
          orientationRestricted: "La orientación RAPTOR solicitada está limitada por el ancho de la viga.",
          processingFailed: "No se ha podido procesar este elemento.",
        },
        missing: (fields: string) => `Faltantes o no válidos: ${fields}.`,
        notices: {
          andMore: (names: string, more: number) => `${names} y ${more} más`,
          anglesMapped: (count: number) =>
            `${count === 1 ? "1 ángulo se ha asignado" : `${count} ángulos se han asignado`} a intervalos de la tabla: el valor inferior del intervalo es la referencia de tabla. El ángulo de eslinga introducido no cambia; debe permanecer en ese intervalo.`,
          appliesTo: (names: string) => `Se aplica a: ${names}.`,
          materialDefault: (value: string, names: string) =>
            `Se usó G = ${value} para los materiales en blanco: ${names}. Confirma el material de madera.`,
          vgs13: "Los tornillos VGS13 y sus cargas de trabajo corresponden exclusivamente a WASPL.",
        },
        processed: (members: number, verified: number, saved: number) =>
          `${members} ${members === 1 ? "elemento procesado" : "elementos procesados"}. ${verified} con recomendaciones verificadas. ${saved} guardados como elementos.`,
        processedResults: "Resultados procesados",
        processedSaved: (members: number, verified: number) =>
          `Se procesaron ${members} miembros guardados; ${verified} con recomendaciones verificadas. Los izados guardados no se modificaron.`,
        processing: "Procesando el libro...",
        processingSaved: "Procesando miembros guardados...",
        requestedUsed: "Solicitado → Usado",
        unverified: "No verificado",
        verified: "Verificado",
        wasplOnlyShort: "Solo WASPL",
        workbookExported: "Excel completado exportado.",
      },
      uploadFilledTemplate: "Cargar plantilla completada",
      uploadList: "Cargar lista para recomendaciones",
      uploadListEyebrow: "Cargar lista",
      uploadListFileSuffix: "lista-cargada",
      uploadMemberList: "Cargar lista de miembros",
      useForTakeoff: "Usar esta opción para la medición de los izados guardados",
      utilization: "Utilización",
      verifiedRecommendationRequired: "Se requiere una recomendación con capacidad verificada para guardar este izado.",
      viewSavedLifts: "Ver izados guardados",
      wallPanelLiftType: "Tipo de izado del panel de muro",
      waspOnly: "Solo WASP - esta configuración no está permitida con WASPL.",
      wasplOnly: "Solo WASPL - esta configuración no está permitida con WASP.",
      weight: "Peso",
      weightLabel: "Peso del miembro izado",
      weightPlaceholder: "Escribe el peso, p. ej. 2500",
      woodSpecificGravity: "Tipo de madera / gravedad específica",
      woodSpecificGravityShort: "Madera / gravedad específica",
    },
    generalPrinciples: {
      title: (device: string) => `${device} — Principios generales`,
      introRaptor:
        "De la ficha técnica de RAPTOR suministrada. Estos principios son específicos de RAPTOR; para otros dispositivos de elevación, consulta la ficha técnica correspondiente.",
      introRaptorMini:
        "De la ficha técnica de RAPTOR MINI suministrada. Estos principios son específicos de RAPTOR MINI.",
      introWasp:
        "De la ficha técnica de WASP / WASPL suministrada. Estos principios se aplican tanto a WASP como a WASPL.",
      fastenerLengthPlate:
        "La longitud del conector debe elegirse en cada caso en función de las dimensiones del elemento de madera, de la posición del conector, del ángulo de elevación, del peso de la carga a elevar y de la disposición de la placa de elevación. En todos los casos, se recomienda que los conectores tengan una longitud mayor que la mínima requerida, pero de modo que la punta no sobresalga del elemento a elevar.",
      fastenerLength:
        "La longitud del conector debe elegirse en cada caso en función de las dimensiones del elemento de madera, de la posición del conector, del ángulo de elevación y del peso de la carga a elevar. En todos los casos, se recomienda que los conectores tengan una longitud mayor que la mínima requerida, pero de modo que la punta no sobresalga del elemento a elevar.",
      wllBasis:
        "Los valores de capacidad de elevación (WLL) indicados se basan en cálculos realizados según el ICC-ES Evaluation Report ESR-4645, la AWC-NDS 2018, el AWC Technical Report 12 y en los resultados de los ensayos realizados. La documentación de origen indica un factor de seguridad igual o superior a 5,0. Los requisitos aplicables de la ",
      oshaLink: "sección 1926.251 de la OSHA",
      wllBasisAfterLink:
        " deben confirmarse para cada operación de elevación. Esta calculadora no acredita el cumplimiento de la OSHA ni aprueba el conjunto completo de eslingado.",
      specificGravityBasis: (maxCapacity: string) =>
        `Se consideró como base de cálculo una gravedad específica G = 0,42. Para especies de madera con una gravedad específica diferente se utilizó la siguiente tabla de conversión con los factores de modificación. Los valores nunca superan la capacidad máxima de la placa de ${maxCapacity}.`,
      specificGravityBasisWasp: (waspCapacity: string, wasplCapacity: string) =>
        `Se consideró como base de cálculo una gravedad específica G = 0,42. Para especies de madera con una gravedad específica diferente se utilizó la siguiente tabla de conversión con los factores de modificación. Los valores nunca superan la capacidad máxima de ${waspCapacity} para WASP y de ${wasplCapacity} para WASPL.`,
      capacityFactors:
        "Todos los valores de capacidad de los tornillos se ajustan con un factor de aceleración dinámica de 1/1,1, un factor de duración de la carga CD = 1,25, un factor de temperatura Ct = 1,0 y un factor de servicio húmedo CM = 1,0, y suponen que los tornillos se instalan en obra justo antes de la elevación.",
      brittleFailureElement:
        "Las comprobaciones de posibles reducciones de resistencia por rotura frágil relacionadas con la geometría del elemento elevado deben realizarse por separado según la AWC-NDS, en particular cuando el conector abarca menos del 70 % del canto del elemento elevado.",
      brittleFailureBeam:
        "Las comprobaciones de posibles reducciones de resistencia por rotura frágil relacionadas con la geometría del elemento elevado deben realizarse por separado según la AWC-NDS, en particular cuando el conector abarca menos del 70 % del canto de la viga.",
      loadToGrain:
        "Todos los cálculos de CLT suponen un ángulo entre carga y fibra de 90°, independientemente de la orientación, sin reducciones de la longitud del conector en la elevación por el canto.",
      otherConfigurationsPlate:
        "Para el cálculo de la capacidad de elevación de la placa en configuraciones de instalación distintas de las indicadas aquí, ponte en contacto con el Departamento Técnico de Rothoblaas.",
      otherConfigurations:
        "Para el cálculo de la capacidad de elevación en configuraciones de instalación distintas de las indicadas aquí, ponte en contacto con el Departamento Técnico de Rothoblaas.",
      fatigue: (device: string) =>
        `Para cumplir los límites de fatiga de la Service Class 0 de la ASME BTH01-2023, ningún dispositivo de elevación ${device} puede utilizarse durante más de 20.000 ciclos de carga.`,
      noPreDrilling: (specificGravity: string) =>
        `Los valores de capacidad de elevación indicados se calculan para la placa fijada con tornillos insertados sin pretaladro, según el ICC Evaluation Report ESR-4645. Todas las distancias mínimas deben aumentarse para gravedades específicas superiores a ${specificGravity}.`,
      installation:
        "Los conectores deben instalarse según sus instrucciones de instalación para evitar daños y mantener todas sus prestaciones de acuerdo con sus parámetros de resistencia de cálculo.",
      noImpactWrenches:
        "No está permitido el uso de atornilladores de impulsos/llaves de impacto. Respeta el ángulo de inserción con ayuda de un pretaladro y/o una plantilla de instalación. Evita la flexión. Asegura un apriete correcto. Recomendamos el uso de atornilladores con control de par, p. ej. con TORQUE LIMITER. Como alternativa, aprieta con una llave dinamométrica.",
      hbsPlateValues:
        "Los valores indicados en la ficha técnica para la placa de transporte fijada con tornillos HBS PLATE se han calculado considerando la geometría y los parámetros mecánicos de la versión HBS PL.",
      hbsPContact:
        "Para las capacidades de carga de la placa de transporte fijada con el tornillo HBS P, ponte en contacto con la Oficina Técnica para obtener más asistencia.",
      singleUse: (screws: string) =>
        `${screws} solo pueden utilizarse una vez. Una vez apretados y cargados, los tornillos no deben aflojarse ni utilizarse una segunda vez para fijar la placa de transporte. En cuanto el elemento de madera que se transporta se haya elevado a su posición final y la placa de transporte ya no sea necesaria para este fin, los tornillos deben desatornillarse y desecharse correctamente.`,
      screwsHbsPlateVgs: "Los tornillos HBS PLATE y VGS",
      screwsVgs: "Los tornillos VGS",
      vgsPlateReuse: (device: string) =>
        `VGS PLATE es el único conector apto para su reutilización. Para su uso con el dispositivo ${device}, consulta las fichas técnicas y los manuales de instrucciones de VGS PLATE y del JIG REUSE correspondiente.`,
      qualifiedPersonnelPlate:
        "La placa de elevación solo puede ser utilizada por personal cualificado. El manual de usuario (suministrado como código QR en el producto y disponible en www.rothoblaas.com) debe leerse y comprenderse antes del uso. Deben seguirse la información y las instrucciones que contiene. En caso de duda, ponte en contacto con el Departamento Técnico de Rothoblaas antes del uso.",
      qualifiedPersonnelDevice:
        "El dispositivo de elevación solo puede ser utilizado por personal cualificado. El manual de usuario (suministrado como código QR en el producto y disponible en www.rothoblaas.com) debe leerse y comprenderse antes del uso. Deben seguirse la información y las instrucciones que contiene. En caso de duda, ponte en contacto con el Departamento Técnico de Rothoblaas antes del uso.",
      specificGravityColumn: "Gravedad específica [G]",
      modificationFactorColumn: "Factor de modificación",
    },
    admin: {
      family: "Familia",
      devicesAndScrews: "Dispositivos y tornillos",
      lifts: "Elevaciones",
      anchorCount: "Número de anclajes",
      anchorCounts: "Números de anclajes",
      angle: "Ángulo",
      capacities: "Capacidades",
      capacity: "Capacidad",
      capacityFactor: "Factor de capacidad",
      capacityLb: "Capacidad [lb]",
      device: "Dispositivo",
      deviceFamilies: "Familias de dispositivos",
      deviceFamily: "Familia de dispositivos",
      deviceFamilyLiftType: "Tipo de elevación de la familia",
      deviceFamilyLiftTypes: "Límites dimensionales",
      deviceScrewMappings: "Asignaciones dispositivos-tornillos",
      deviceScrewMapping: "Asignación dispositivo-tornillo",
      devices: "Dispositivos",
      diameter: "Diámetro",
      installationType: "Tipo de instalación",
      length: "Longitud",
      liftType: "Tipo de elevación",
      liftTypes: "Tipos",
      maxCapacity: "Capacidad máxima",
      maxMemberDepth: "Canto máximo del elemento",
      maxMemberWidth: "Ancho máximo del elemento",
      memberType: "Tipo de elemento",
      minMemberDepth: "Canto mínimo del elemento",
      minMemberWidth: "Ancho mínimo del elemento",
      plateThickness: "Espesor de la placa",
      productUrl: "URL del producto",
      requiresRiggingReview: "Requiere revisión del aparejo",
      screw: "Tornillo",
      screwCount: "Tornillos por dispositivo",
      screwFamilies: "Familias de tornillos",
      screwFamily: "Familia de tornillos",
      screws: "Tornillos",
      specificGravities: "Gravedades específicas",
      specificGravity: "Gravedad específica",
      washer: "Arandela",
    },
    errors: {
      calculationInvalidInput: "El cálculo contiene un valor no válido.",
      calculationNotAvailable: "Un valor seleccionado ya no está disponible. Revisa la selección.",
      capacityAlreadyExists:
        "Ya existe una capacidad con el mismo dispositivo, tornillo, tipo de elevación, tipo de instalación, tornillos por dispositivo y ángulo.",
      capacityNotFound: "No se ha encontrado la capacidad.",
      deviceAlreadyExists: "El artículo ya es un dispositivo.",
      deviceFamilyNameAlreadyExists: "Ya existe una familia de dispositivos con este nombre.",
      deviceFamilyLiftTypeAlreadyExists: "La familia de dispositivos ya tiene este tipo de elevación.",
      deviceFamilyLiftTypeInvalidLimits:
        "Los límites del elemento deben ser mayores que cero y cada máximo no inferior a su mínimo.",
      deviceFamilyLiftTypeNotFound: "No se ha encontrado el tipo de elevación de la familia.",
      deviceScrewMappingAlreadyExists: "La familia de dispositivos ya tiene esta familia de tornillos.",
      deviceScrewMappingNotFound: "No se ha encontrado la familia de tornillos de la familia de dispositivos.",
      deviceInUseByLift: "El dispositivo se usa en elevaciones guardadas: ponlo como Oculto en lugar de eliminarlo.",
      deviceNotFound: "No se ha encontrado el dispositivo.",
      liftTypeAnchorCountAlreadyExists: "El tipo de elevación ya tiene este número de anclajes.",
      liftTypeAnchorCountNotFound: "No se ha encontrado el número de anclajes.",
      screwAlreadyExists: "El artículo ya es un tornillo.",
      screwInUseByLift: "El tornillo se usa en elevaciones guardadas: ponlo como Oculto en lugar de eliminarlo.",
      screwNotFound: "No se ha encontrado el tornillo.",
      specificGravityAlreadyExists: "Ya existe una gravedad específica con este valor.",
      specificGravityInUseByLift:
        "La gravedad específica se usa en elevaciones guardadas: ponla como Oculta en lugar de eliminarla.",
      specificGravityNotFound: "No se ha encontrado la gravedad específica.",
      uploadFileTooLarge: "El archivo supera los 10 MB.",
      uploadInvalidWorkbook: "El archivo cargado no es un libro .xlsx válido.",
      uploadMetricTemplate:
        "Usa la plantilla USA - NDS con pesos en lb y dimensiones en pulgadas. No se aceptan plantillas métricas o europeas.",
      uploadMissingColumns: "Faltan columnas obligatorias en la plantilla.",
      uploadNoHeader: "El libro debe incluir una fila de encabezado con Member Name y Member Type.",
      uploadNoRows: "No se encontraron filas de elementos.",
      uploadNoWorksheet: "No se encontró ninguna hoja en el libro cargado.",
    },
  },

  deckDesignerModule: {
    configurations: {
      singular: "Configuración",
      plural: "Configuraciones",
      errors: {
        connectorFamilyNotAvailable: "La familia de conectores elegida ya no está disponible: elige otra.",
        connectorNotAvailable: "El conector elegido ya no está disponible: elige otro.",
        screwNotAvailable: "Un tornillo elegido ya no está disponible: elige otro.",
        supportFamilyNotAvailable: "La familia de soportes elegida ya no está disponible: elige otra.",
        supportNotAvailable: "El soporte elegido ya no está disponible: elige otro.",
        substructureNotAvailable: "La subestructura elegida ya no está disponible: elige otra.",
        invalidTotalHeight: "Altura máxima extradós terraza (H) no es válida para la composición seleccionada.",
        noSupportForRequiredHeightRange: (min: number, max: number) =>
          `Con la configuración actual el soporte debe cubrir una altura (h) ${min === max ? `de ${min} mm` : `de ${min} a ${max} mm`}: ningún soporte disponible cubre este rango. Modifica H o la pendiente.`,
        noSupportForSpacing:
          "La separación entre soportes a lo largo de los rastreles es demasiado pequeña para los soportes disponibles (se necesita al menos 1,5 veces su anchura). Reduce la separación entre rastreles.",
        noSupportFamilyOptions: "No hay opciones disponibles con la configuración actual.",
        noSupportForSlopeDrop: {
          title: "Ningún soporte disponible puede cubrir el desnivel del terreno, sea cual sea H.",
          requiredRange: (min: number, max: number) =>
            `Altura de soporte necesaria (h): de ${min} a ${max} mm (desnivel ${max - min} mm)`,
          widestSupport: (supportCode: string, rangeMm: number) =>
            `Desnivel máximo cubierto por un soporte: ${rangeMm} mm (${supportCode})`,
          fix: (maxSlope: number | null) =>
            `${maxSlope != null ? `Reduce la pendiente a un máximo de ${maxSlope}°` : "Reduce la pendiente"}, luego elige H entre los valores compatibles.`,
        },
        noCompatibleConnectorSubstructureScrew:
          "No hay ningún tornillo compatible con el conector y la subestructura seleccionados.",
        noCompatibleSupportScrew: "No hay ningún tornillo compatible con el soporte seleccionado.",
        noCompatibleConnectorBoardScrew: "No hay ningún tornillo compatible con el espesor de tabla seleccionado.",
        noCompatibleConnectorFamilyMapping:
          "Ninguna familia de conectores es compatible con la subestructura, el tipo de fijación, el tipo de fresado y la separación seleccionados.",
        noCompatibleConnectorFamilyBoardThickness:
          "Ninguna familia de conectores compatible se ajusta al espesor de tabla actual.",
        noCompatibleConnectorFamilyBoardThicknessAndGroovingHeight:
          "Ninguna familia de conectores compatible se ajusta al espesor de tabla y a la altura de fresado actuales.",
        noCompatibleConnector: "Ningún conector es compatible con la configuración actual.",
        noCompatibleSupportFamilyMapping:
          "Ninguna familia de soportes es compatible con la clase de material de subestructura seleccionada.",
      },
      calculation: {
        shapeAndGeometricData: "Forma y dimensiones",
        completeFieldsToContinue: "Rellena todos los campos de esta página para continuar",
        showBillOfQuantities: "Mostrar lista de materiales",
        boards: "Tablas",
        billOfQuantities: "Lista de cantidades",
        downloadPdf: "Descargar PDF",
        downloadPdfDescription:
          "Descarga el PDF con el resumen completo de la configuración: datos geométricos, tablas, subestructura, soportes, vistas de la terraza, reacciones en los apoyos y lista de productos.",
        downloadPdfDisabledHint: "Completa la configuración para descargar el PDF.",
        downloadPdfDisabledBoardFitHint: "Resuelva el aviso sobre el ancho de tabla y la junta para descargar el PDF.",
        billOfQuantitiesEmpty: "Selecciona los datos de la terraza para calcular la lista de cantidades.",
        calculationDetailsEmpty: "Selecciona los datos de la terraza para ver los detalles del cálculo.",
        showCompatibleValues: "Ver valores compatibles",
        stratigraphySummaryTitle: "Estratigrafía actual",
        availableHeightForConnectorAndSupport: "Altura disponible para conector y soporte",
        connectorThickness: "Espesor del conector",
        remainingHeightForSupport: "Altura restante para el soporte",
        billOfQuantitiesProduct: "Producto",
        billOfQuantitiesRequiredQuantity: "Cantidad necesaria",
        billOfQuantitiesQuantityToOrder: "Cantidad a pedir",
        calculationDetails: "Detalles del cálculo",
        deckArea: "Área de terraza",
        totalBoards: "Tablas",
        joists: "Rastreles",
        joistsLegend: "Rastreles de subestructura",
        layoutCalculation: "Cálculo de disposición",
        longJoists: "Rastreles largos",
        shortJoists: "Rastreles cortos",
        longJoist: "Rastrel largo",
        shortJoist: "Rastrel corto",
        supportCalculation: "Cálculo de soportes",
        joistsSpacing: "Separación entre rastreles",
        maxSupportSpacing: "Separación máx. de soportes",
        supportsPerLongJoist: "Soportes por rastrel largo",
        supportsPerShortJoist: "Soportes por rastrel corto",
        supportSpacing: "Separación de soportes",
        supportsCount: "N.º de soportes",
        lShaped: "Forma en L",
        horizontal: "Horizontal",
        vertical: "Vertical",
        rectangular: "Forma rectangular",
        boardDirection: "Dirección de las tablas",
        fasteningType: "Tipo de fijación",
        hiddenFastening: "Oculta",
        hiddenFasteningDescription: "Las tablas se fijan sin tornillos visibles, mediante conectores o clips ocultos.",
        visibleFastening: "Visible",
        visibleFasteningDescription: "Las tablas se fijan con tornillos visibles en la superficie de la terraza.",
        groovingType: "Tipo de ranura",
        smoothGrooving: "Lisa",
        groovedGrooving: "Ranurada",
        maxBase: "Anchura máx. (L1)",
        maxHeight: "Longitud máx. (L2)",
        substructureSection: (unit: string) => `Sección del elemento bs x hs [${unit}]`,
        secondaryBase: "Anchura secundaria (l1)",
        secondaryHeight: "Longitud secundaria (l2)",
        totalHeight: "Altura máxima extradós terraza (H)",
        slope: "Pendiente",
        slopeDirectionTitle: "Pendiente del terreno",
        slopeDirectionHint:
          "Arrastra sobre el dibujo para trazar la dirección en la que baja el terreno. Sin una dirección el terreno queda plano.",
        slopeIncompleteWarning:
          "Se necesitan tanto la pendiente como su dirección: los cambios en la pendiente no se guardarán. ¿Cerrar de todos modos?",
        totalHeightTooLittle: (height: string) =>
          `La composición seleccionada requiere una altura total de al menos ${height}.`,
        secondaryItemDoesNotFitTotalHeight: (code: string) =>
          `Al activar ${code}, la familia de conectores seleccionada dejaría de ser compatible con la altura total (H).`,
        totalHeightIncompatible: (minHeight: string, maxHeight: string) =>
          `La altura total para esta composición debe estar entre ${minHeight} y ${maxHeight}.`,
        planeInteractionHint: "Puedes dibujar y modificar la terraza directamente desde aquí.",
        planeInteractionStart: "Empezar a dibujar",
        // the button of the tutorial reopened from its icon
        planeInteractionContinue: "Continuar dibujando",
        showPlaneInteractionHint: "Mostrar el tutorial",
        planeInteractionFeatureDraw: "Haz clic en el plano para trazar el perímetro de la terraza.",
        planeInteractionFeatureLength:
          "Después de seleccionar un punto, escribe la longitud del lado (Retroceso para corregirla) y pulsa Intro para confirmarla.",
        planeInteractionFeatureDrag: "Arrastra los lados de la terraza para cambiar sus dimensiones.",
        planeInteractionFeatureEditLabel: "Haz clic en una etiqueta para introducir la dimensión deseada.",
        planeInteractionFeatureDifferentBoards:
          "Arrastra de un lado a otro las tablas de dimensiones diferentes, si están presentes.",
        planeInteractionFeatureClear:
          "Usa el icono de la papelera para borrar los puntos y volver a empezar el dibujo.",
        liveLoad: "Carga de uso",
        deflection: "Deflexión (EN1995-1-1 §7 / EN1999-1-1 §4)",
        gap: "Anchura de separación compatible",
        gapIncompatible: (minimumGap: string, maximumGap: string) =>
          `La separación debe estar entre ${minimumGap} y ${maximumGap}.`,
        joistsSpacingIncompatible: (minimumSpacing: number, maximumSpacing: number, unit: string) =>
          `La separación entre rastreles debe estar entre ${minimumSpacing} ${unit} y ${maximumSpacing} ${unit}.`,
        material: "Material",
        connectorMaterial: "Material del conector",
        timberMaterial: "Madera",
        wpcMaterial: "WPC",
        boardWidth: "Anchura de tabla",
        differentBoardWidthDragHint: "Puedes arrastrar las tablas de un lado a otro de la terraza.",
        differentBoard: "Tabla de ancho diferente",
        differentBoardsColumn: "Tablas de ancho diferente",
        longDifferentBoardWidth: "Tabla larga",
        shortDifferentBoardWidth: "Tabla corta",
        boardThickness: "Espesor de tabla",
        boardThicknessMustNotExceedBoardWidth: "El espesor de la tabla no debe superar su anchura.",
        boardThicknessExceedsConnectorFamilyMaxThickness: (maxValue: string) =>
          `El espesor de la tabla supera el máximo permitido por la familia de conectores seleccionada (${maxValue}).`,
        boardThicknessBelowAsymmetricalGroovingMinimum: (minValue: string) =>
          `El espesor de la tabla es demasiado pequeño para la ranura asimétrica seleccionada (mínimo ${minValue}).`,
        boardThicknessBelowGroovingMinimum: (minValue: string) =>
          `El espesor de la tabla debe ser de al menos ${minValue} para el tipo de ranura seleccionado.`,
        boardThicknessOutOfSymmetricalGroovingRange: (minValue: string, maxValue: string) =>
          minValue === maxValue
            ? `El espesor de la tabla debe ser ${minValue} para la configuración actualmente seleccionada.`
            : `El espesor de la tabla debe estar entre ${minValue} y ${maxValue} para la ranura simétrica con la familia de conectores seleccionada.`,
        groovingHeight: "Altura de la ranura",
        groovingHeightOutOfConnectorFamilyRange: (minValue: string, maxValue: string) =>
          `La altura de la ranura debe estar entre ${minValue} y ${maxValue} para la familia de conectores seleccionada.`,
        groovingHeightBelowConnectorMinimum: (minValue: number, unit: string) =>
          `La altura de la ranura debe ser de al menos ${minValue} ${unit} para el conector seleccionado.`,
        boardWidthMustNotExceedDeckDimension: (maxValue: string) =>
          `La anchura de tabla no debe superar ${maxValue} para la dirección seleccionada.`,
        sideNarrowerThanBoardWidth: "Este lado no puede ser más estrecho que la anchura de tabla seleccionada.",
        boardFitWarning:
          "La anchura de tabla y la separación seleccionadas no se ajustan exactamente a las dimensiones de la terraza. Para optimizar, elige una de las 3 opciones siguientes:",
        boardFitApplied: "Cambio aplicado. Si no te convence, pulsa «Deshacer» para restaurar los valores anteriores.",
        appliedFitParameter: "Parámetro",
        appliedFitPrevious: "Anterior",
        appliedFitCurrent: "Actual",
        setBoardWidth: "Ajustar anchura de tabla",
        setBoardWidthTo: "Ajustar anchura de tabla a:",
        setDeckDimensions: "Ajustar dimensiones",
        setDeckDimensionsTo: "Ajustar dimensiones a:",
        setDifferentBoardWidth: "Ajustar anchuras de tablas diferentes",
        setDifferentBoardWidthTo: "Ajustar anchuras de tablas diferentes a:",
        topView: "Vista superior",
        sideView: "Vista lateral",
        bottomView: "Vista inferior",
        slopeView: "Pendiente",
        slopeViewSide: (from: number, to: number) => `Lado ${from}–${to}`,
        slopeViewLegendDeck: "Terraza",
        slopeViewLegendTerrain: "Terreno",
        slopeViewExaggeration: "Exageración de la pendiente (solo visualización)",
        slopeViewShowArrow: "Mostrar flecha de pendiente",
        unitSystemMetric: "Métrico",
        unitSystemImperial: "Imperial",
        unitSystemChangeTitle: "Cambiar unidad de medida",
        unitSystemChangeWarning:
          "Al cambiar la unidad de medida se mantendrán la forma y las dimensiones; todos los demás campos se restablecerán. ¿Deseas continuar?",
        slopeViewExaggerationHelp:
          "Acentúa la pendiente en la vista 3D para verla mejor. Es solo un efecto visual: los valores y la configuración no cambian.",
        slopeViewClickHint: "Haz clic en una etiqueta para ver h, t y H en ese punto.",
        slopeViewNotToScale: "Esquema ilustrativo, sin escala.",
        slopeViewUnreachable: "No alcanzable",
        slopeViewLegendTotalHeight: "H — altura máxima extradós terraza",
        slopeViewLegendSupportHeight: "h — altura disponible para el soporte",
        slopeViewLegendTerrainHeight: "t — altura del terreno respecto al vértice más bajo, donde se mide H",
        supportCompatibleHRange: "H compatible",
        supportMinHeight: "Altura soporte mín",
        supportMaxHeight: "Altura soporte máx",
        supportSlopeUnchanged: "La pendiente coincide con la introducida.",
        supportSlopeReduced: "La pendiente se reducirá respecto a la introducida.",
        supportSlopeIncreased: "La pendiente se aumentará respecto a la introducida.",
        supportTotalHeightUnchanged: "H coincide con el valor introducido.",
        supportTotalHeightReduced: "H se reducirá respecto al valor introducido.",
        supportTotalHeightIncreased: "H se aumentará respecto al valor introducido.",
        supportFlatTerrainOnly: "Solo con ambas pendientes a 0°",
        supportSuggestedSlope: "Pendiente",
        supportWidth: "Anchura del soporte",
        suggestedValueUnchanged: "El valor coincide con el introducido.",
        suggestedValueReduced: "El valor se reducirá respecto al introducido.",
        suggestedValueIncreased: "El valor se aumentará respecto al introducido.",
        supportSpacingSuggestionsInfo:
          "Para cada soporte, la configuración más cercana que separa los soportes al menos 1,5 veces su anchura. Primero cambian la sección del elemento y la separación entre rastreles; la flecha y la carga de uso solo si no basta.",
        noSupportSpacingSuggestion: "Ninguna configuración separa los soportes al menos 1,5 veces su anchura.",
        supportApplySuggestion: "Establecer valores",
        noSupportCompatibleWithSlope:
          "Ningún soporte es compatible con la pendiente introducida: sin pendiente existiría al menos uno. Intenta reducirla, aumentar H, o cambiar las dimensiones de la terraza.",
        noSupportCompatibleGeneric:
          "Ningún soporte es compatible con la configuración actual. Intenta cambiar la altura total H, el soporte elegido, u otros elementos de la terraza (tabla, conector, elemento secundario).",
        supportScrew: "Tornillo de soporte",
        connectorSubstructureScrew: "Tornillo de conector a subestructura",
        connectorBoardScrew: "Tornillo de conector a tabla",
        regularLongBoards: "Tablas largas regulares",
        regularShortBoards: "Tablas cortas regulares",
        differentLongBoards: "Tablas largas con ancho diferente",
        differentShortBoards: "Tablas cortas con ancho diferente",
      },
    },
    connectorFamilies: {
      singular: "Familia de conectores",
      plural: "Familias de conectores",
      isScrew: "Es tornillo",
      requiresBoardScrew: "Requiere tornillo de tabla",
      maxBoardThickness: "Espesor máx. de tabla",
      description: "Descripción",
      errors: {
        nameAlreadyPresent: "Ya existe una familia de conectores con el mismo nombre.",
      },
    },
    connectorMappings: {
      singular: "Mapeo de conectores",
      plural: "Mapeos de conectores",
      minimumGap: "Separación mínima",
      maximumGap: "Separación máxima",
    },
    connectors: {
      singular: "Conector",
      plural: "Conectores",
      minGroovingHeight: "Altura mínima de ranura",
      minBoardWidth: "Ancho mínimo de tabla",
      errors: {
        inUseByConfiguration: "El conector se usa en un cálculo guardado y no se puede eliminar.",
        itemAlreadyPresent: "Ya existe un conector con el mismo elemento.",
        screwAlreadyPresent: "Ya existe un conector con el mismo tornillo.",
      },
    },
    fasteningTypes: {
      singular: "Tipo de fijación",
      plural: "Tipos de fijación",
      errors: {
        nameAlreadyPresent: "Ya existe un tipo de fijación con el mismo nombre.",
      },
    },
    groovingTypes: {
      singular: "Tipo de ranura",
      plural: "Tipos de ranura",
      errors: {
        nameAlreadyPresent: "Ya existe un tipo de ranura con el mismo nombre.",
      },
    },
    connectorMaterials: {
      singular: "Material del conector",
      plural: "Materiales del conector",
      errors: {
        inUseByConnector: "El material lo usa un conector: modifica o elimina primero el conector.",
        nameAlreadyPresent: "Ya existe un material del conector con el mismo nombre.",
      },
    },
    secondaryItems: {
      singular: "Elemento secundario",
      plural: "Elementos secundarios",
      use: "Usar elemento secundario",
      canUse: "Puede usar elemento secundario",
      errors: {
        inUseByConfiguration: "El elemento secundario se usa en un cálculo guardado y no se puede eliminar.",
        inUseByConnectorMapping:
          "El elemento secundario lo usa una asignación de conectores: elimina primero la asignación.",
      },
    },
    screwFamilies: {
      singular: "Familia de tornillos",
      plural: "Familias de tornillos",
      errors: {
        inUseByScrew: "La familia de tornillos la usa un tornillo: elimina primero sus tornillos.",
        inUseByConnectorFamily:
          "La familia de tornillos está asignada a una familia de conectores: quítala primero de allí.",
        inUseBySupportFamily:
          "La familia de tornillos está asignada a una familia de soportes: quítala primero de allí.",
        nameAlreadyPresent: "Ya existe una familia de tornillos con el mismo nombre.",
      },
    },
    screws: {
      singular: "Tornillo",
      plural: "Tornillos",
      errors: {
        inUseByConfiguration: "El tornillo se usa en un cálculo guardado y no se puede eliminar.",
        inUseByConnector: "El tornillo se usa como conector: elimina primero ese conector.",
        itemAlreadyPresent: "Ya existe un tornillo con el mismo elemento.",
      },
    },
    substructures: {
      singular: "Subestructura",
      plural: "Subestructuras",
      base: "Base",
      secondMomentOfArea: "Momento de inercia",
      countries: "Países",
      countryOptions: {
        eu: "Europa",
        us: "Estados Unidos",
        all: "Todos",
      },
      errors: {
        inUseByConfiguration: "La subestructura se usa en un cálculo guardado y no se puede eliminar.",
        itemAlreadyPresent: "Ya existe una subestructura con el mismo elemento.",
        baseHeightAlreadyPresent: "Ya existe una subestructura con la misma base y altura.",
      },
    },
    substructureFamilies: {
      singular: "Familia de subestructuras",
      plural: "Familias de subestructuras",
      errors: {
        nameAlreadyPresent: "Ya existe una familia de subestructuras con el mismo nombre.",
      },
    },
    substructureMaterialClasses: {
      singular: "Clase de material de subestructura",
      plural: "Clases de material de subestructura",
      modulusOfElasticity: "Módulo elástico",
      errors: {
        inUseByConfiguration: "La clase de material se usa en un cálculo guardado y no se puede eliminar.",
        inUseBySupport: "La clase de material está asignada a un soporte: quítala primero de allí.",
        nameAlreadyPresent: "Ya existe una clase de material de subestructura con el mismo nombre.",
      },
    },
    substructureMaterials: {
      singular: "Material de subestructura",
      plural: "Materiales de subestructura",
      errors: {
        nameAlreadyPresent: "Ya existe un material de subestructura con el mismo nombre.",
      },
    },
    supportFamilies: {
      singular: "Familia de soportes",
      plural: "Familias de soportes",
      maxLiveLoad: "Carga de uso máx.",
      errors: {
        nameAlreadyPresent: "Ya existe una familia de soportes con el mismo nombre.",
      },
    },
    supports: {
      singular: "Soporte",
      plural: "Soportes",
      errors: {
        inUseByConfiguration: "El soporte se usa en un cálculo guardado y no se puede eliminar.",
        itemAlreadyPresent: "Ya existe un soporte con el mismo elemento.",
      },
    },
    substuructures: {
      singular: "Subestructura",
      plural: "Subestructuras",
    },
    substuructureMaterials: {
      singular: "Material de subestructura",
      plural: "Materiales de subestructura",
    },
    substuructureFamilies: {
      singular: "Familia de subestructuras",
      plural: "Familias de subestructuras",
    },
  },

  lockCalculatorModule: {
    preview3d: "Vista previa 3D",
    preDrillingLegend: {
      header: "Pretaladrado viga principal",
      joist: "Pretaladrado vigueta",
      yes: "sí",
      no: "no",
    },
    tabs: {
      codeNorm: "Normativa",
      structure: "Estructura",
      connectors: "Conectores",
      loading: "Cargas",
      designInput: "Datos de Entrada",
      configuration: "Configuración",
      loads: "Cargas",
      results: "Resultados",
      calculationComparison: "Comparación de cálculos",
      bom: "Lista de materiales",
      report: "Informe PDF",
    },
    env: {
      title: "Configuración Ambiental",
      standard: "Normativa",
      serviceClass: "Clase de Servicio",
      loadDurationClass: "Clase de Duración de Carga",
      continuousCtrl: "Control Continuo",
      continuousCtrlLabel: "Producción sujeta a control continuo (COV < 15%)",
      coefficients: "Coeficientes de cálculo",
      kmod: "Factor de modificación (duración de la carga y clase de servicio)",
      gammaM: "Coeficiente parcial de seguridad de la unión (γM)",
      gammaMAluminium: "Coeficiente parcial de seguridad del aluminio (γM)",
      customCoefficients: "Personalizado",
      customCoefficientsHint: "Modificar kmod y γM solo para este cálculo, en lugar de los valores normativos",
      normativeValue: "Valor normativo",
      restoreNormative: "Restablecer",
    },
    structural: {
      title: "Configuración Estructural",
      configType: "Resistencia lateral",
      connectionPosition: "Posición del conector",
      upliftResistanceConfig: "Configuración de resistencia al levantamiento",
      inclinedFastener: "Fijación inclinada",
      headerOrientation: "Orientación de la viga principal",
      lateralResistance: {
        columnNotch: "Column notch",
        headerBeamNotch: "Entalladura de la viga principal",
        joistBeamNotch: "Entalladura de la vigueta",
        inclinedFasteners: "Fijaciones inclinadas",
        lockstop: "LOCK STOP",
        exposed: "Expuesta",
      },
      headerOrientationOptions: { beam: "Viga", column: "Pilar" },
      generateAllConfigurations: "Generar todas las configuraciones",
      goToManualConfiguration: "Ir a configuración manual/semimanual",
    },
    connection: {
      title: "Configuración de Conexión",
      standard: "Normativa",
      serviceClass: "Clase de Servicio",
      loadDurationClass: "Clase de Duración de Carga",
      connector: "Conector",
      finish: "Acabado",
      connectorOne: "Conector 1",
      connectorTwo: "Conector 2",
      productType: "Tipo de Producto",
    },
    fasteners: {
      headerFastener: "Fijación de viga principal",
      joistFastener: "Fijación de vigueta",
      upliftFastener: "Fijación Antilevantamiento",
      lockstop: "Lockstop",
    },
    customMaterial: {
      create: "Crear material personalizado",
      edit: "Editar material personalizado",
      remove: "Eliminar material",
      badge: "personalizado",
      name: "Nombre",
      baseGrade: "Basado en",
      keepCurrentBase: "Mantener la base actual",
      baseGradeHint: "La categoría, la norma y la familia estructural se heredan del material base",
      inherited: "Heredado de la base",
      family: "Familia estructural",
      category: "Categoría",
      normative: "Norma",
      properties: "Propiedades",
      advanced: "Avanzadas",
      rhoK: "Densidad característica ρk",
      rhoM: "Densidad media ρm",
      fc0K: "Compresión paralela a la fibra fc,0,k",
      fc90K: "Compresión perpendicular a la fibra fc,90,k",
      fvK: "Cortante fv,k",
      frK: "Cortante por rodadura fr,k",
      faxK: "Extracción axial fax,k",
      usedInCalculations: (n: number) => `Usado en ${n} cálculos: se actualizarán en el próximo recálculo.`,
      deleteConfirm: "¿Seguro que deseas eliminar este material personalizado?",
      deleteBlocked: "No se puede eliminar: el material se usa en otros cálculos.",
    },
    timber: {
      sectionTitle: "Configuración Geométrica",
      title: "Elementos de Madera",
      header: "Viga principal",
      joist: "Vigueta",
      headerMaterialGrade: "Clase resistente de la viga principal",
      headerWidth: "Ancho de la viga principal (b)",
      headerHeight: "Alto de la viga principal (h)",
      joistMaterialGrade: "Clase resistente de la vigueta",
      joistWidth: "Ancho de la vigueta (b)",
      joistHeight: "Alto de la vigueta (h)",
      joistAngle: "Ángulo de la vigueta α",
      joistAngleBeta: "Ángulo de la vigueta β",
      connectorTopDistance: "Distancia del conector al borde superior de la vigueta",
      joistTopOffset: "Desplazamiento del borde superior de la vigueta respecto a la viga principal",
      joistTopAlignment: "Posición del borde superior de la vigueta",
      joistTopAligned: "Alineado con el borde superior del pilar",
      joistTopOffsetOption: "Desplazado respecto al pilar",
    },
    connector: {
      sectionTitle: "Conector & Tornillería",
      setupTitle: "Configuración Conexión",
      selectionTitle: "Selección Conector",
      fastenersTitle: "Tornillería",
      connectorPairCount: "Número de pares de conectores",
      noCompatibleConnector: "Ningún conector LOCK-T compatible con las secciones introducidas.",
      noCompatibleSecondConnector: "Ningún segundo conector compatible con el seleccionado",
      generateFromData: "Generar configuraciones a partir de los datos introducidos",
      generateManual: "Generar solución manual",
    },
    loads: {
      sectionTitle: "Acciones de Diseño (Ed)",
      title: "Cargas (Ed)",
      fvD: "Fuerza Cortante (Fv,d)",
      faxD: "Fuerza Axial (Fax,d)",
      flatD: "Fuerza Lateral (Flat,d)",
      fupD: "Fuerza de Levantamiento (Fup,d)",
      alphaDeg: "Ángulo α",
    },
    combined: {
      title: "Verificación Combinada",
      description: "Selecciona las direcciones a incluir en la verificación combinada:",
      includeFv: "Fv,d (Cortante)",
      includeFup: "Fup,d (Levantamiento)",
      includeFax: "Fax,d (Extracción)",
      includeFlat: "Flat,d (Lateral)",
    },
    configuration: {
      title: "Configuración",
      configType: "Tipo de Configuración",
      connectionCount: "Número de Conexiones",
    },
    errors: {
      fillRequiredFields: "Por favor, completa todos los campos obligatorios.",
    },
    selectPlaceholder: "Select...",
    helpTexts: {
      standard: "Normativa de diseño a aplicar (Eurocódigo 5 o NTC 2018)",
      serviceClass: "Condiciones de humedad y temperatura en el sitio (EC5 §2.3.1.3)",
      loadDurationClass: "Duración de la carga aplicada (permanente, larga, media, corta, instantánea)",
      continuousCtrl: "Producción certificada con COV < 15% permite γM reducido (EC5 §2.4.1)",
      lateralResistanceConfig: "Tipo de configuración para verificación de resistencia lateral",
      upliftResistanceConfig:
        "Elige si usar la fijación inclinada para el cálculo de resistencia al levantamiento. Comparte la misma fijación seleccionada en el paso Connectors para la configuración lateral.",
      headerOrientation: "Orientación de la viga principal: horizontal (viga) o vertical (pilar).",
      headerMaterialGrade: "Calidad de madera de la viga principal (GL24h, GL28h, LVL, C24...)",
      headerWidth:
        "Ancho de la viga principal (dimensión b). Con orientación pilar, el ancho propio del conector debe caber en esta medida menos una tolerancia (17,5mm para Mini, 24mm para Midi).",
      headerHeight:
        "Altura de la viga principal (dimensión h). Con orientación viga, limita la altura de montaje máxima (assembly_height) del módulo conector (≤ altura viga principal − 15mm).",
      joistMaterialGrade:
        "Calidad de madera de la vigueta. Una densidad (rho_k) superior a 420 kg/m³ requiere pretaladrado, lo que reduce el ancho máximo admitido para el conector (ver ancho de la vigueta).",
      joistWidth:
        "Ancho de la vigueta (sección b). El ancho propio del conector debe caber en esta medida — exactamente si la madera requiere pretaladrado, o con una tolerancia adicional de 8mm (Mini) / 6mm (Midi) si no lo requiere.",
      joistHeight:
        "Altura de la vigueta (sección h). Limita la altura de montaje máxima (assembly_height) del módulo conector (≤ altura vigueta), sin tolerancia.",
      angleAlpha: "",
      joistTopAlignment:
        "Viga principal en pilar. Alineado: el borde superior de la vigueta queda a ras con el del pilar y el c_min se verifica sobre la distancia del conector al borde superior de la vigueta. Desplazado: la vigueta queda por debajo del pilar (campo de desplazamiento) y el c_min se verifica sobre la suma del desplazamiento y la distancia del conector.",
      joistTopOffset:
        "Cuánto queda el borde superior de la vigueta por debajo del borde superior del pilar. Si está vacío se usa el c_min del LOCK elegido.",
      connectorTopDistance:
        "Distancia vertical desde el borde superior de la vigueta hasta el conjunto de conectores (la viga principal se mueve con él). Si se deja vacío: con la vigueta alineada al pilar se usa el c_min del LOCK elegido; con la vigueta desplazada 0 (conector a ras del borde de la vigueta). Para LOCKT135 sin pretaladro el mínimo es 5 mm. Con viga principal en pilar debe alcanzarse el c_min: solo con esta distancia si la vigueta está alineada, con su suma al desplazamiento si está desplazada.",
      angleBeta:
        "Ángulo de cizalla de la vigueta en el plano X-Z, independiente del ángulo α. Determina la longitud máxima admitida para la fijación de la vigueta: hasta 20° el límite es 48,35mm (Mini) / 65,7mm (Midi); por encima, ninguna fijación es compatible (límite 0).",
      connectorPairCount: "Número de pares de conectores LOCK en la unión",
      headerLock:
        "Un lock más ancho en el lado de la viga principal reduce a la mitad los conectores de la lista de materiales y actualiza los tornillos de ese lado. Elegirlo o cambiarlo puede quitar el LOCK STOP de la configuración: el resultado debe recalcularse. El lock más ancho y el LOCK STOP se excluyen: elegir uno quita el otro.",
      connectionCount: "Número total de uniones idénticas en la estructura",
      connectorOneLock: "Modelo LOCK del conector 1",
      connectorOneFinish: "Acabado superficial del conector 1",
      connectorTwoLock: "Modelo LOCK del conector 2 (con dos pares de conectores)",
      connectorTwoFinish: "Acabado superficial del conector 2",
      headerFastener: "Fijación del lado de la viga principal",
      joistFastener: "Fijación del lado de la vigueta",
      upliftFastener: "Fijación para verificación de levantamiento",
      lockstop: "Elemento LOCK STOP para resistencia lateral",
      lockstopWidth: "Ajuste de ancho para elemento LOCK STOP (mm)",
      fvD: "Fuerza de corte vertical (valor de diseño)",
      fupD: "Fuerza de levantamiento (valor de diseño)",
      faxD: "Fuerza axial (valor de diseño)",
      flatD: "Fuerza lateral perpendicular a la viga (valor de diseño)",
      combined:
        "Verificación combinada: (Fax,Ed / Rax,d)² + (Fv,Ed / Rv,d)² ≤ 1. Selecciona las direcciones de carga a incluir.",
    },
    notices: {
      title: "Avisos",
      goToField: "Ir al campo",
      goToAction: "Ir al botón",
      count: (n: number) => (n === 1 ? "1 aviso" : `${n} avisos`),
      severity: { error: "Error", warning: "Atención", info: "Info" },
      axes: {
        vertical: "vertical (Fv)",
        uplift: "levantamiento (Fup)",
        axial: "axial (Fax)",
        lateral: "lateral (Flat)",
      },
      members: { header: "viga principal", joist: "vigueta" },
      reasons: {
        columnWidth: "el ancho del pilar",
        topDistance: "la distancia del conector al borde superior",
        joistWidth: "el ancho de la vigueta",
        assemblyHeight: "las alturas de las secciones",
        evoRequired: "el requisito EVO de la clase de servicio 3",
      },
      coefficients: { kmod: "kmod", gammaM: "γM (unión)", gammaMAluminium: "γM (aluminio)" },
      slots: {
        header: "tornillo de la viga principal",
        joist: "tornillo de la vigueta",
        inclined: "tornillo inclinado",
      },
      inputs: {
        standardId: "norma",
        serviceClassId: "clase de servicio",
        loadDurationClassId: "clase de duración de la carga",
        "connectors[0].lockItemId": "primer conector",
        "connectors[1].lockItemId": "segundo conector",
        headerFastenerItemId: "tornillo de la viga principal",
        joistFastenerItemId: "tornillo de la vigueta",
        lockstopItemId: "LOCK STOP",
        headerMaterialGradeId: "material de la viga principal",
        joistMaterialGradeId: "material de la vigueta",
        headerOrientation: "orientación de la viga principal",
        alphaDeg: "ángulo α",
        headerH: "altura de la viga principal",
        headerB: "ancho de la viga principal",
        joistB: "ancho de la vigueta",
        joistH: "altura de la vigueta",
        anyLoad: "al menos una carga",
        inclinedFastenerItemId: "tornillo inclinado",
        lateralResistanceConfig: "resistencia lateral",
        kmod: "kmod",
        gammaM: "γM",
      },
      messages: {
        topDistanceBelowCMin: (p: NoticeParams) =>
          `La distancia del conector ${p.distance} mm + el desfase de la vigueta ${p.offset} mm están por debajo del c_min mínimo del LOCK (${p.cMin} mm): la configuración no está verificada. Aumenta la distancia o el desfase.`,
        topDistanceBelowLock135Min: (p: NoticeParams) =>
          `El LOCKT135 en una vigueta sin pretaladro requiere al menos ${p.min} mm desde el borde superior (ahora ${p.distance} mm): la configuración no está verificada.`,
        serviceClassNotAllowed: (p: NoticeParams) =>
          `La clase de servicio ${p.classNumber} no está permitida para el material de la viga principal (permitidas: ${p.allowed}): la configuración no está verificada.`,
        axisInputsMissing: (p: NoticeParams) =>
          `El eje ${p.axis} tiene carga pero no se puede calcular: falta ${p.missing}.`,
        noCompatibleLock: (p: NoticeParams) =>
          `Ningún LOCK encaja en esta estructura: ${p.rejected} de ${p.total} quedaron excluidos por ${p.reason}.`,
        edgeDistanceUnavailable: (p: NoticeParams) =>
          `Las distancias mínimas a los bordes (§11) no están disponibles para la ${p.member}: la densidad (${p.rhoK} kg/m³) supera el límite de 500 del EC5.`,
        customCoefficientsEnabled: () =>
          "Ahora puedes modificar kmod y γM solo para este cálculo: mientras no cambies un valor, se mantiene el normativo.",
        coefficientOverrideDiffers: (p: NoticeParams) =>
          `${p.coefficient} vale ${p.value}; el valor normativo es ${p.normative}.`,
        topDistanceEqualsDefault: () => "Se usa el valor por defecto: el c_min del LOCK elegido.",
        resultStale: (p: NoticeParams) =>
          `Los datos han cambiado desde el último cálculo: el resultado de la configuración final puede estar desactualizado. Pulsa "${p.button}" para recalcularlo con los datos actuales.`,
        reportStale: (p: NoticeParams) =>
          `Los datos cambiaron después del último informe: el PDF mostrado no está actualizado. Pulsa "${p.generateReport}" en la pestaña "${p.reportTab}".`,
        draftsStale: (p: NoticeParams) =>
          `Los datos han cambiado desde que se generaron las configuraciones: pulsa "${p.button}" para actualizar la lista.`,
        noDraftWithinLimits: (p: NoticeParams) =>
          `Los LOCK encajan en la estructura, pero ninguna configuración generada cumple las cargas. Revisa las cargas o los materiales y vuelve a pulsar "${p.button}".`,
        generationInputsMissing: (p: NoticeParams) =>
          `"${p.generateAll}" y "${p.generateFromData}" no están disponibles: faltan ${p.missing}.`,
        manualGenerationInputsMissing: (p: NoticeParams) =>
          `"${p.generateManual}" no está disponible: faltan ${p.missing}.`,
        noCompatibleFastener: () =>
          `Los LOCK encajan en la estructura, pero ningún tornillo es compatible con ellos: revisa las alturas de las vigas, el ángulo y los materiales.`,
        noCompatibleLockstop: () =>
          `Los LOCK encajan en la estructura, pero ningún LOCK STOP es compatible con ellos: elige otra resistencia lateral.`,
        combinedNeedsTwoAxes: (p: NoticeParams) =>
          `La verificación combinada requiere al menos dos ejes: solo está seleccionado el eje ${p.axis}. Selecciona otro.`,
        lateralConfigCorrected: (p: NoticeParams) =>
          `La resistencia lateral ha pasado de "${p.fromConfig}" a "${p.toConfig}": la primera no está disponible con la orientación "${p.orientation}" de la viga principal.`,
        continuousControlCleared: () =>
          "El control continuo de producción se ha borrado: no se aplica al Eurocódigo 5.",
        secondConnectorCleared: () => "Se ha quitado el segundo conector: hay un solo par de conectores seleccionado.",
        lockstopClearedByHeaderLock: () =>
          "Se han quitado el LOCK STOP y el tipo de resistencia lateral porque se ha elegido un lock más ancho en la viga principal: elige un nuevo tipo de resistencia lateral y recalcula el resultado.",
        headerLockClearedByLockstop: () =>
          "Se ha quitado el lock más ancho de la viga principal porque se ha elegido un LOCK STOP: recalcula el resultado.",
        lockstopCleared: () => "Se ha quitado el LOCK STOP: la resistencia lateral ya no está en LOCK STOP.",
        combinedAxisDisabled: (p: NoticeParams) =>
          `El eje ${p.axis} se ha quitado de la verificación combinada: no tiene carga.`,
        upliftConfigSetByLateral: () =>
          "La resistencia al levantamiento se ha fijado en tornillos inclinados, como en la resistencia lateral.",
        upliftConfigSetByLoad: () =>
          "La resistencia al levantamiento se ha fijado en tornillos inclinados porque se ha introducido una carga de levantamiento.",
        inclinedFastenerCleared: () =>
          "Se ha quitado el tornillo inclinado: ni la resistencia lateral ni la de levantamiento usan tornillos inclinados.",
        inclinedFastenerNotAllowed: () =>
          "Se ha quitado el tornillo inclinado: el LOCK elegido no admite tornillos inclinados.",
        fastenerCleared: (p: NoticeParams) => `Se ha quitado el ${p.slot}: no es compatible con la selección actual.`,
        finishForced: () => "El acabado se ha cambiado a aluminio: el LOCK elegido no tiene versión EVO.",
        finishSetByServiceClass: () => "El acabado se ha fijado en EVO: la clase de servicio 3 lo exige.",
        secondLockCleared: () => "Se ha quitado el segundo LOCK: ya no encaja con el primero.",
        pairCountReduced: () => "Se ha mantenido un solo par de conectores: ningún segundo LOCK encaja con el primero.",
        topDistanceFollowedDefault: (p: NoticeParams) =>
          `La distancia del conector ha seguido el nuevo valor por defecto del LOCK elegido: ${p.before} mm → ${p.after} mm.`,
        topOffsetFollowedDefault: (p: NoticeParams) =>
          `El desfase de la vigueta ha seguido el nuevo valor por defecto del LOCK elegido: ${p.before} mm → ${p.after} mm.`,
        connectorsInvalidated: () =>
          "Se han borrado los LOCK elegidos: se eligieron para otra distancia al borde superior.",
      },
    },
    wizard: {
      stepOf: (current: number, total: number) => `Step ${current} of ${total}`,
      completeFieldsToContinue: "Rellena todos los campos de esta página para continuar",
    },
    finish: {
      aluminium: "Aluminium",
      evo: "EVO",
      aluminiumShort: "ALU",
      evoShort: "EVO",
    },
    continuousCtrl: {
      active: "Active",
      inactive: "Inactive",
      standard: "Standard",
      cov: "COV",
      std: "STD",
    },
    results: {
      title: "Resultados de Verificación",
      verified: "Verificado",
      utilizationRatio: "Tasa de Utilización",
      notYetCalculated: "Completa el formulario para realizar la verificación.",
      notVerified: "No verificado",
      verifiedOk: "Verificación superada",
      axes: {
        vertical: "Vertical (Fv)",
        uplift: "Uplift (Fup)",
        axial: "Axial (Fax)",
        lateral: "Lateral (Flat)",
      },
      load: "Load",
      resistance: "Resistance",
      eta: "η",
      failed: "FAILED",
      noLoad: "NO LOAD",
      formulaAltSingle: "η = Ed / Rd ≤ 1",
      formulaAltCombined: "Σ (Ed/Rd)² ≤ 1",
      finalConfiguration: "Configuración final",
      removeComparison: "Quitar de la comparación",
      slipModulus: "Módulo de deslizamiento",
    },
    generatedConfigurations: {
      title: "Configuraciones generadas",
      empty: "Aún no se ha generado ninguna configuración — completa Código y Norma, Cargas y Estructura.",
      lock: "Lock",
      fastener: "Tornillo",
      combined: "Combinada",
      chosen: "Elegida",
      compare: "Comparar",
      downloadData: "Descargar datos",
      load: "Cargar",
      selectAll: "Seleccionar todas las configuraciones",
      selectRow: "Seleccionar esta configuración",
      search: "Buscar configuraciones",
    },
    bom: {
      title: "Lista de materiales",
      noComponents: "No hay componentes configurados.",
      component: "Componente",
      code: "Código",
      quantity: "Cant",
      headerLock: "Lock viga principal más ancho",
    },
    report: {
      title: "Informe PDF",
      empty: "Elige una configuración para generar el informe de cálculo.",
      error: "No se pudo generar el informe.",
      retry: "Reintentar",
      generate: "Generar PDF",
      download: "Descargar PDF",
      fullscreen: "Pantalla completa",
      exitFullscreen: "Salir de pantalla completa",
      savingChanges: "Guardando los cambios: el informe se genera a partir de los datos guardados.",
      notGenerated: "Haz clic en «Generar PDF» para generar el informe de cálculo.",
    },
    helpModal: {
      illustrationAlt: "Help illustration",
    },
    aria: {
      calculationSteps: "Calculation steps",
      calculationResults: "Calculation Results",
    },
    admin: {
      tabs: {
        reference: "Reference",
        catalog: "Catalog",
        coefficients: "Coefficients",
        connectors: "Lock",
      },
      catalog: {
        fasteners: "Fasteners",
        materialGrades: "Material Grades",
      },
      coefficients: {
        kmodValues: "Kmod Values",
        gammaMValues: "γM Values",
      },
      reference: {
        standards: "Standards",
        serviceClasses: "Service Classes",
        loadDurationClasses: "Load Duration Classes",
      },
      fasteners: {
        singular: "Fastener",
        diameter: "Ø (mm)",
        length: "Length (mm)",
        threadLength: "Thread Length (mm)",
        myRk: "My,Rk (kNm)",
        ftensK: "Ftens,K (kN)",
        headType: "Head Type",
      },
      materialGrades: {
        singular: "Material Grade",
        rhoK: "ρk (kg/m³)",
        rhoM: "ρm (kg/m³)",
      },
      standards: {
        singular: "Standard",
        jurisdictionCode: "Región",
        normativeType: "Tipo de norma",
        normativeTypes: {
          EUROCODE: "Eurocódigo",
          NATIONAL_CODE: "Código nacional",
          HARMONIZED_STANDARD: "Norma armonizada",
          ETA: "ETA",
          DOP: "DoP",
        },
      },
      serviceClasses: {
        singular: "Service Class",
        classNumber: "Class #",
        nameIt: "Name IT",
        nameDe: "Name DE",
        nameFr: "Name FR",
        nameEs: "Name ES",
        description: "Description",
        typicalExamples: "Typical Examples",
        order: "Order",
      },
      loadDurationClasses: {
        singular: "Load Duration Class",
      },
      kmodValues: {
        singular: "Kmod Value",
        serviceClass: "Service Class",
        loadDurationClass: "Load Duration Class",
        kmod: "kmod",
      },
      gammaMValues: {
        singular: "γM Value",
        standard: "Standard",
        continuousCtrl: "Continuous Ctrl",
        notApplicable: "Not applicable (EC5)",
        gammaM: "γM",
      },
      connectors: {
        singular: "Lock Connector",
        family: "Family",
        nModules: "N. Modules",
        nInclinedUplift: "Inclined Fasteners (Uplift)",
        nInclinedLateral: "Inclined Fasteners (Lateral)",
        width: "Width (mm)",
        height: "Height (mm)",
        thickness: "Thickness (mm)",
        screwEdgeThickness: "Screw edge thickness (mm)",
        evoAvailable: "EVO Available",
        lockModule: "Lock Module",
        edgeDistance: "Edge Distance (mm)",
        assemblyHeight: "Assembly Height (mm)",
      },
      lockModules: {
        singular: "Lock Module",
        assemblyHeight: "Assembly height (mm)",
        sideHeight: "Side height (mm)",
        thickness: "Thickness (mm)",
        moduleWidth: "Module Width (mm)",
        joistFastenersPerModule: "Fijaciones de vigueta / módulo",
        headFastenersPerModule: "Fijaciones de cabeza / módulo",
        kLockFactor: "kLock",
        f1AluRk: "F1,alu,Rk (kN)",
        f2AluRkPerModule: "F2,alu,Rk / module (kN)",
        kh2PerModule: "kh2 / módulo",
        contactAreaALock: "Contact Area ALock (mm²)",
        cMin: "c_min (mm)",
        coefficients: "Lateral/Vertical Coefficients",
      },
      lockStops: {
        singular: "Lock Stop",
        fyK: "fy,k (MPa)",
        area: "Area (mm²)",
        fLockstopRk: "FLockstop,Rk (kN)",
        isSingle: "Single",
      },
      connectorAccessories: {
        singular: "Connector Accessory",
        quantity: "Qty",
        notes: "Notes",
        accessorySku: "Accessory SKU",
        connector: "Connector",
      },
      fastenerUsages: {
        singular: "Uso de tornillos",
        fastener: "Tornillo",
        usage: "Uso",
        usageStandard: "Estándar (viga principal / vigueta)",
        usageInclined: "Inclinado",
        scope: "Seleccionar por",
        scopeFamilies: "Familias completas",
        scopeFasteners: "Tornillos individuales",
        families: "Familias",
        fasteners: "Tornillos",
        exceptions: "Excepciones (no añadir)",
        resultAlreadyPresent: "Ya añadidos anteriormente",
        resultAdded: "Añadidos ahora",
        resultNothingToDo: "No hay elementos que añadir o eliminar",
        resultRemoved: "Existían y se han eliminado por ser excepciones",
      },
      lockLockstopMappings: {
        singular: "Lock → Lockstop",
        lock: "Lock",
        lockstop: "Lockstop",
        quantity: "Quantity",
        installationLevels: "Niveles de instalación",
      },
      lockPairLockstopMappings: {
        singular: "Pareja → Lockstop",
        lockA: "Lock A",
        lockB: "Lock B",
        lockstop: "Lockstop",
        quantity: "Cantidad total",
        installationLevels: "Niveles de instalación",
      },
      model3d: {
        singular: "Modelo 3D",
        quantity: "Cantidad",
      },
      emptyValue: "--",
    },
  },
  beamReinforcementModule: {
    tabs: {
      generalData: "Datos generales y cargas",
      beams: "Vigas",
      screw: "Tornillo y conexión",
    },
    screwFamilies: {
      singular: "Familia de tornillos",
      plural: "Familias de tornillos",
      errors: {
        nameAlreadyPresent: "Ya existe una familia de tornillos con el mismo nombre.",
      },
    },
    screws: {
      singular: "Tornillo",
      plural: "Tornillos",
      errors: {
        nameAlreadyPresent: "Ya existe un tornillo con el mismo nombre.",
      },
      fields: {
        family: "Familia",
        headDiameter: "Diámetro de cabeza",
        threadedLength: "Longitud roscada",
        yieldMoment: "Momento de fluencia",
        tensileCapacity: "Capacidad a tracción",
      },
    },
    timbers: {
      singular: "Clase de madera",
      plural: "Clases de madera",
      errors: {
        nameAlreadyPresent: "Ya existe una clase de madera con el mismo nombre.",
      },
      fields: {
        density: "Densidad rho",
        bendingStrength: "Resistencia a flexión fmk",
        shearStrength: "Resistencia a cortante fvk",
        modulusOfElasticity: "Módulo elástico E0,mean",
        isGlulam: "Madera laminada",
      },
    },
    drawing: {
      view2D: "Vista 2D",
      view3D: "Vista 3D",
      enable3DHint: "Introduce ambas longitudes de viga para habilitar la vista 3D.",
    },
    calculation: {
      serviceClass: "Clase de servicio",
      serviceClass1: "1 — u ≤ 10%",
      serviceClass2: "2 — 10% < u ≤ 16%",
      serviceClass3: "3 — u ≥ 16%",
      loadDuration: "Duración de la carga",
      loadDurationPerm: "Permanente",
      loadDurationLong: "Larga duración",
      loadDurationMedium: "Media duración",
      loadDurationShort: "Corta duración",
      loadDurationInst: "Instantánea",
      tipTolerance: "Tolerancia de punta",
      permanentLoad: "Carga permanente",
      liveLoad: "Carga variable",
      beamLength: "Luz de la viga",
      existingBeamTitle: "Viga existente",
      reinforcementBeamTitle: "Viga de refuerzo",
      strengthClass: "Clase resistente",
      base: "Base",
      height: "Altura",
      lengthOptionalHint:
        "Opcional. Si se definen ambas longitudes de viga, la vista previa podrá alternar entre 2D y 3D.",
      existingLength: "Longitud de la viga existente",
      reinforcementLength: "Longitud del refuerzo",
      reinforcementSides: "Lados de refuerzo",
      reinforcementSidesOne: "1 — refuerzo en un lado",
      reinforcementSidesTwo: "2 — refuerzo en ambos lados",
      screw: "Tornillo",
      screwSpacing: "Separación de tornillos",
      preDrilled: "Orificio pretaladrado",
    },
    results: {
      summaryTitle: "Resumen de verificaciones",
      bendingUtilization: "Grado de utilización a flexión",
      shearUtilization: "Grado de utilización a cortante",
      connectionUtilization: "Grado de utilización de la conexión",
      screwAdmissible: "Tornillo admisible",
      screwNotAdmissible: "Tornillo NO admisible",
      reasonGeometry: "geometría (l_ef / longitud / t₂)",
      reasonHeight: "h₂ < a₄,t + a₄,c",
      reasonSpacing: "separación < mínima a₁",
      reasonOverload: "η > 100%",
      incompleteWarning: "Completa todos los campos para calcular la verificación.",
      warningInsufficientPenetration:
        "Penetración de rosca insuficiente en la viga existente (l_ef = 0). Elige un tornillo más largo.",
      warningScrewTooLong: (max: number) =>
        `El tornillo supera el ancho disponible b₁+b₂−10 mm = ${max} mm: la punta sale de la viga existente.`,
      warningHeightInsufficient: (height: number, min: number) =>
        `Altura insuficiente: h₂ = ${height} mm < a₄,t + a₄,c = ${min} mm. Aumenta h₂ o reduce el diámetro del tornillo.`,
      warningSpacingInsufficient: (spacing: number, min: number) =>
        `La separación adoptada (${spacing} mm) es inferior a la separación mínima a₁ = ${min} mm.`,
      warningConnectionOverload: (eta: number) =>
        `Conexión no verificada: η = ${eta}% > 100%. Reduce la separación o aumenta el tornillo.`,
    },
    details: {
      title: "Detalle del cálculo",
      loadsSection: "Coeficientes, cargas y profundidad de penetración",
      kmod: "k_mod",
      selfWeight: "Peso propio g₁",
      designLoad: "Carga de cálculo q_d",
      spacingSection: "Separaciones mínimas y efectivas (EC5 §8.3)",
      minimum: "mínimo",
      effective: "efectivo",
      outcome: "resultado",
      stiffnessSection: "Distribución de rigideces",
      bendingSection: "Verificación a flexión",
      shearSection: "Verificación a cortante",
      axialSection: "Resistencia axial del tornillo",
      johansenSection: "Resistencia de la conexión (Johansen)",
      connectionSection: "Verificación de la conexión",
      existingBeamLabel: "Viga existente (1)",
      reinforcementBeamLabel: "Viga de refuerzo (2)",
      designMoment: "Momento de cálculo M_d",
      designShear: "Cortante de cálculo V_d",
      bendingStress: "Tensión a flexión σ_m,d",
      bendingStrength: "Resistencia a flexión f_m,d",
      shearStress: "Tensión a cortante τ_d",
      shearStrength: "Resistencia a cortante f_v,d",
      screwCount: "Tornillos a lo largo de la luz",
      designShearForce: "Cortante de cálculo por tornillo V_Ed",
      connectionResistance: "Resistencia de la conexión F_v,Rd",
      yes: "Sí",
      no: "No",
    },
  },
  screwConnectorModule: {
    tabs: {
      screwFamilies: "Familias de tornillos",
      screwDiameters: "Diámetros de tornillo",
      screws: "Tornillos",
      washers: "Arandelas",
      timberMaterials: "Materiales de madera",
      normativeTables: "Tablas normativas",
    },
    screwFamilies: {
      singular: "Familia de tornillos",
      plural: "Familias de tornillos",
      errors: {
        nameAlreadyPresent: "Ya existe una familia de tornillos con el mismo nombre.",
      },
      fields: {
        description: "Descripción",
        threadType: "Tipo de rosca",
        material: "Material",
        coating: "Recubrimiento",
        ccCode: "Código CC",
        tcCode: "Código TC",
        scEuCode: "Código SC (EU)",
        ecUsCode: "Código ESR / EC (US)",
        wdUsCode: "Código WD (US)",
        standardReference: "Referencia normativa",
        connectionTypes: "Tipos de conexión",
        substrates: "Sustratos",
      },
    },
    threadTypes: {
      partial: "Rosca parcial",
      full: "Rosca total",
    },
    headTypes: {
      countersunk: "Cabeza avellanada",
      cylindrical: "Cabeza cilíndrica",
      hexagonal: "Cabeza hexagonal",
      round_plate: "Cabeza redonda (fijación de placas)",
      pan_plate: "Cabeza troncocónica (fijación de placas)",
      reinforced_pan_plate: "Cabeza troncocónica reforzada (fijación de placas)",
      hexagonal_pan_plate: "Cabeza troncocónica hexagonal (fijación de placas)",
    },
    tipTypes: {
      three_thorns: "3 THORNS",
      self_drilling: "Punta autoperforante",
      standard: "Punta estándar",
      hardwood: "Punta para maderas duras",
    },
    connectionTypes: {
      shear_screws: "Tornillos a cortante",
      steel_to_timber: "Acero-madera",
      timber_to_timber: "Madera-madera",
    },
    substrateCategories: {
      solidwood_softwood: "Madera maciza - conífera",
      glulam_softwood: "Madera laminada encolada - conífera",
      clt_softwood: "CLT - conífera",
      lvl_softwood: "LVL - conífera",
      solidwood_hardwood: "Madera maciza - frondosa",
      lvl_hardwood: "LVL - frondosa",
      panels: "Paneles",
      steel: "Acero",
    },
    compatibilityLevels: {
      notCompatible: "No compatible",
      restricted: "Compatibilidad restringida",
      compatible: "Compatible",
    },
    washers: {
      singular: "Arandela",
      plural: "Arandelas",
      fields: {
        code: "Código",
        innerDiameter: "Diámetro interior",
        outerDiameter: "Diámetro exterior",
        height: "Altura",
      },
    },
    screwDiameters: {
      singular: "Diámetro de tornillo",
      plural: "Diámetros de tornillo",
      filterByFamily: "Filtrar por familia de tornillos",
      selectFamilyHint: "Selecciona una familia de tornillos para gestionar sus diámetros.",
      fields: {
        screwFamily: "Familia de tornillos",
        diameter: "Diámetro",
        coreDiameter: "Diámetro del núcleo",
        threadOuterDiameter: "Diámetro exterior de rosca",
        headDiameter: "Diámetro de cabeza",
        headType: "Tipo de cabeza",
        tipType: "Tipo de punta",
        bit: "Puntas",
        yieldMoment: "Momento de fluencia",
        tensileCapacity: "Capacidad a tracción",
        washers: "Arandelas compatibles",
        resistanceValues: "Valores de resistencia",
      },
    },
    resistanceValues: {
      fields: {
        resistanceParam: "Parámetro",
        substrateCategory: "Categoría del sustrato",
        substrateSpecies: "Especie del sustrato",
        preDrilled: "Pretaladrado",
        valueNMm2: "Valor",
        referenceDensity: "Densidad de referencia",
        densityRange: "Rango de densidad",
      },
      params: {
        fax_k: "f_ax,k",
        fhead_k: "f_head,k",
      },
      addRow: "Añadir valor de resistencia",
    },
    screws: {
      singular: "Tornillo",
      plural: "Tornillos",
      filterByDiameter: "Filtrar por diámetro de tornillo",
      selectDiameterHint: "Selecciona un diámetro de tornillo para gestionar sus tornillos.",
      fields: {
        screwDiameter: "Diámetro de tornillo",
        length: "Longitud",
        partialThreadLength: "Longitud de rosca parcial",
        fullThreadExtraLength: "Longitud extra de rosca total",
        tipLength: "Longitud de punta",
      },
    },
    timberMaterials: {
      singular: "Material de madera",
      plural: "Materiales de madera",
      fields: {
        materialCategory: "Categoría del material",
        species: "Especie",
        class: "Clase",
        characteristicDensity: "Densidad característica ρk",
        meanDensity: "Densidad media ρmean",
        compressionStrength0: "Resistencia a compresión f_c,0,k",
        compressionStrength90: "Resistencia a compresión f_c,90,k",
        rollingShearStrength: "Resistencia a cortante rodante f_r,k",
        shearStrength: "Resistencia a cortante f_v,k",
        standardReference: "Referencia normativa",
      },
    },
    normativeTables: {
      title: "Tablas normativas",
      loadDurationClasses: {
        singular: "Clase de duración de carga",
        plural: "Clases de duración de carga",
        fields: {
          code: "Código",
          name: "Nombre",
          order: "Orden",
        },
      },
      serviceClasses: {
        singular: "Clase de servicio",
        plural: "Clases de servicio",
        fields: {
          code: "Código",
          name: "Nombre",
        },
      },
      kmod: {
        title: "Valores k_mod",
        hint: "Factor de modificación k_mod según la clase de servicio y la clase de duración de carga.",
      },
      gammaM: {
        title: "Coeficientes parciales de seguridad γM",
        fields: {
          standard: "Normativa",
          columnA: "Columna A",
          columnB: "Columna B",
        },
      },
    },
    errors: {
      familyAlreadyExists: "Ya existe una familia de tornillos con este nombre.",
      familyMissingName: "El nombre de la familia de tornillos es obligatorio.",
      familyNotFound: "No se ha encontrado la familia de tornillos.",
      gammaMValueNotFound: "No se ha encontrado el valor γM.",
      kmodValueNotFound: "No se ha encontrado el valor kmod.",
      loadDurationClassAlreadyExists: "Ya existe una clase de duración de carga con este código.",
      loadDurationClassMissingCode: "El código de la clase de duración de carga es obligatorio.",
      loadDurationClassNotFound: "No se ha encontrado la clase de duración de carga.",
      screwAlreadyExists: "El artículo ya es un tornillo.",
      screwDiameterNotFound: "No se ha encontrado el diámetro de tornillo.",
      screwNotFound: "No se ha encontrado el tornillo.",
      serviceClassAlreadyExists: "Ya existe una clase de servicio con este código.",
      serviceClassNotFound: "No se ha encontrado la clase de servicio.",
      timberMaterialAlreadyExists: "Ya existe un material de madera con esta categoría y clase.",
      timberMaterialNotFound: "No se ha encontrado el material de madera.",
      washerAlreadyExists: "Ya existe una arandela con este código.",
      washerMissingCode: "El código de la arandela es obligatorio.",
      washerNotFound: "No se ha encontrado la arandela.",
    },
    units: {
      unitMm2: "mm²",
      unitNMm2: "N/mm²",
      unitKgM3: "kg/m³",
    },
    calculation: {
      shell: {
        navLabel: "Secciones del módulo",
        openMenu: "Abrir menú",
        closeMenu: "Cerrar menú",
        inputsGroup: "Datos del proyecto",
        resultsGroup: "Resultados",
        toolsGroup: "Herramientas",
        resultsView: "Vista 3D y verificaciones",
        resultsSolutions: "Soluciones compatibles",
        resultsPositioning: "Posicionamiento de los tornillos",
        generateReport: "Generar informe PDF",
        reportQuestion: "¿Qué versión del informe?",
        reportCompact: "Informe compacto",
        reportCompactHint: "sin notas metodológicas",
        reportFull: "Informe completo",
        reportFullHint: "con notas metodológicas",
        exportDxf: "Exportar DXF (detalle)",
        dotOk: "Completo",
        dotBad: "A verificar",
        dotPending: "Pendiente",
        tutorial: "Tutorial",
      },
      tutorial: {
        dialogLabel: "Tutorial",
        stepOf: (step: number, total: number) => `Paso ${step} de ${total}`,
        done: "Hecho",
        redo: "Repetir",
        skip: "Saltar el tutorial",
        back: "Atrás",
        next: "Siguiente",
        finish: "Finalizar",
        confirmTitle: "¿Iniciar el tutorial?",
        confirmText:
          "Una breve demostración de las funciones principales sobre un ejemplo: cada paso se ejecuta por ti, continúa con Siguiente. Los campos se vacían para la demostración, que solo usa datos de ejemplo y muestra una solución verificada; al cerrarla tus datos se restablecen tal como estaban. Durante el tutorial el cálculo no se guarda.",
        cancel: "Cancelar",
        start: "Iniciar el tutorial",
        steps: {
          intro: {
            title: "Cómo rellenar el cálculo",
            text: "Avanza de arriba abajo: en cada paso verás el ratón moverse por los campos del panel e introducir un ejemplo. Al inicio θ, b1, d1, t1, b2, d2, t2, Fn, Fv y Fa están vacíos: hasta que los rellenes, el cálculo no se ejecuta.",
            note: "Los campos se han vaciado: al cerrar el tutorial se restauran tus datos.",
          },
          connectionType: {
            title: "2 · Tipo de conexión y ángulo",
            text: "Elige el tipo de unión haciendo clic en uno de los ocho iconos, cada uno con su nombre debajo (madera-madera, madera-madera / cortadura simple en dos lados (3 elementos), madera-madera / cortadura doble (3 elementos), acero-madera con la chapa como elemento 1 o acero-madera / cortadura simple en dos lados, con dos chapas y la madera en el centro, tablero-madera / cortadura simple, con un tablero derivado de la madera como elemento 1, tablero-madera / cortadura simple en dos lados, con dos tableros y la madera en el centro, o tablero-madera / cortadura doble, con el tablero en el centro y la madera a los lados) e introduce el ángulo θ entre los dos elementos (de 1° a 180°).",
          },
          element1: {
            title: "3 · Datos elemento 1",
            text: "(En el caso de 3 elementos es el elemento central.) Elige material, especie y clase (en tablero-madera solo tableros derivados de la madera: contrachapado, OSB/3-OSB/4, tableros de partículas; para los paneles CLT también capas y lado de inserción: el elemento 1 solo lado wide). Introduce el ancho b1, la longitud d1 (o marca «Elemento continuo» si no hay un extremo) y el espesor t1, en mm.",
          },
          element2: {
            title: "4 · Datos elemento 2",
            text: "Los mismos datos para el elemento 2 (en el caso de 3 elementos valen también para el elemento 3): material, b2, d2 y espesor t2 en mm. Para los paneles CLT puedes elegir el lado wide o narrow (narrow: una sola fila de tornillos en el eje).",
          },
          threeMembers: {
            title: "5 · Conexión de 3 elementos",
            text: "Con «madera-madera / cortadura simple en dos lados» o «madera-madera / cortadura doble» el elemento 1 pasa a ser el central (cargado, girado θ); los elementos 2 y 3 son los dos laterales, fijos e idénticos: los datos del elemento 2 valen también para el 3. En el primer caso los tornillos entran por los dos lados (cabeza en 2 y en 3, punta en el central) y la resistencia es doble (dos conexiones a cortadura simple, una por lado); las puntas de los dos lados pueden solaparse o, con la opción de escalonar 1d los tornillos de los dos lados, el lado 3 se escalona. En el segundo un solo tornillo atraviesa los tres elementos (cabeza en el elemento 2, punta en el elemento 3): cortadura doble EC5 ec. 8.7 y una sola retícula de tornillos. En ambos aparece la comprobación a hendimiento del elemento 1. Con «acero-madera / cortadura simple en dos lados» las dos chapas (elementos 1 y 3) están a los lados de la madera central (elemento 2): tornillos por los dos lados como en el primer caso, hendimiento en la madera. El CLT (siempre en el lado wide) se admite en una sola posición en madera-madera en dos lados (CLT-madera-CLT o madera-CLT-madera, nunca tres CLT) y no se puede seleccionar en cortadura doble.",
            note: "Para la demostración el tipo de conexión se ajusta un momento y luego vuelve al que elegiste.",
          },
          panelTimber: {
            title: "6 · Conexión tablero-madera",
            text: "Con «tablero-madera / cortadura simple» el elemento 1 es un tablero derivado de la madera (contrachapado, OSB/3-OSB/4, tableros de partículas) en el lado de la cabeza del tornillo, cargado y girado θ; el elemento 2 es la madera, fija. Solo se usan tornillos HBS y TBS con cabeza ≥ 2·d. El aplastamiento del tablero sigue EC5 §8.3.1.3 (la ETA no lo proporciona), las distancias mínimas la Tabla 8.2 con a1 y a2 ×0,85 en ambos elementos, y el kmod es la raíz del producto del kmod del tablero y el de la madera. Con «tablero-madera / cortadura simple en dos lados» los tableros (elementos 1 y 3, idénticos) están a los lados de la madera central (elemento 2): tornillos por los dos lados, resistencia doble, hendimiento en la madera, como en acero-madera en dos lados. Con «tablero-madera / cortadura doble» el tablero es el elemento 1 central y la madera está a los lados (elementos 2 y 3, idénticos): un solo tornillo atraviesa los tres elementos, como en madera-madera a cortadura doble (ec. 8.7), sin comprobación a hendimiento. El cálculo se detiene, indicando el motivo, si el espesor del tablero es inferior al mínimo (6 mm contrachapado, 8 mm partículas y OSB, en todo caso ≥ 1,2·d), si la cabeza es menor que 2·d o si la clase de servicio no se admite para ese tablero. En cortadura doble el CLT no se puede seleccionar para las maderas laterales; en la vista 3D los tableros se dibujan en una madera más oscura que la maciza o laminada.",
            note: "Para la demostración el tipo de conexión se ajusta un momento y luego vuelve al que elegiste.",
          },
          loads: {
            title: "7 · Cargas de cálculo",
            text: "Introduce Fn (fuerza a lo largo del eje del elemento 1), Fv (cortante, perpendicular) y Fa (extracción, positiva si arranca el tornillo) en kN. Elige la clase de servicio y la duración de la carga: γM se rellena automáticamente según la normativa.",
          },
          connection: {
            title: "8 · Elección de la conexión",
            text: "Elige familia, tipo de agujero (con o sin pretaladro), diámetro y longitud del tornillo: los menús solo proponen tornillos compatibles con espesores y materiales. Si la ETA exige pretaladro (ρk superior a 550 kg/m³, 500 para el inoxidable, salvo las familias HARDWOOD, LBS y KKT) el campo se bloquea en «Con pretaladro» y una nota explica el motivo. Con «Usar arandela» puedes alargar el tornillo. Si aparece «ningún tornillo compatible», modifica los espesores o busca una solución.",
          },
          grid: {
            title: "9 · Retícula de tornillos",
            text: "Define filas y columnas de tornillos: están limitadas al máximo permitido por las distancias mínimas. Con «Distribuir los tornillos en la zona admisible» (activa por defecto, con las distancias mínimas automáticas) las filas y columnas elegidas se llevan a los límites de la zona admisible en lugar de quedar juntas: las separaciones efectivas aparecen bajo los campos s1 y s2. También puedes escalonar los tornillos, desplazar el centro de la retícula o calcular el número máximo de tornillos.",
          },
          view: {
            title: "10 · Vista 3D y comprobaciones",
            text: "Controla la vista 3D y las comprobaciones (cortante y extracción): el resultado es «Admisible» o «No admisible». Los valores de cálculo y las fórmulas están en las fichas de resultados.",
          },
          solutions: {
            title: "11 · Soluciones compatibles",
            text: "¿No sabes qué tornillo usar? Con «Buscar soluciones compatibles» el programa propone los tornillos y el número mínimo que cumplen todas las comprobaciones. Las opciones «Pretaladro» y «Arandela» restringen la búsqueda; «Usar esta» aplica la solución.",
          },
          positioning: {
            title: "12 · Posicionamiento de los tornillos",
            text: "Aquí ves dónde quedan los tornillos respecto a los bordes de cada elemento: los dibujos indican las cotas desde el borde hasta el eje del tornillo y la tabla enumera las distancias de cada tornillo. Ahora la ficha se abre y se desplaza hacia abajo.",
            note: "La ficha vuelve a como estaba (abierta o cerrada) al pasar al paso siguiente.",
          },
          reportPreview: {
            title: "13 · Vista previa del informe",
            text: "Con «Ver informe» ves el informe PDF que descargas, en tu idioma: ahora lo ves abrirse y desplazarse hacia abajo, página tras página. Con la barra superior puedes cambiar el zoom, pasar a página simple o a dos páginas y ver la vista previa en blanco y negro.",
            note: "Si faltan datos, para la demostración se introducen los valores de ejemplo (se eliminan al cerrar).",
          },
          reportPdf: {
            title: "14 · Informe PDF",
            text: "Desde Herramientas › Generar informe PDF elige la versión compacta o completa; el mismo comando está en la barra del título y en la ficha Informe de cálculo. Los datos del cálculo se guardan automáticamente: no hace falta guardarlos a mano.",
          },
          dxf: {
            title: "15 · Exportar DXF",
            text: "Desde Herramientas › Exportar DXF (detalle), o con el botón «Exportar DXF» de la ficha Informe de cálculo, descargas el detalle constructivo para CAD: vista frontal de los elementos, vistas con las cotas desde los bordes, tornillos con su diámetro real, cajetín y tabla de posiciones (también para la conexión de 3 elementos: los tornillos escalonados del lado 3 están en una capa aparte; el cajetín indica también las separaciones efectivas cuando los tornillos están distribuidos). El dibujo está a escala real 1:1 y el comando solo está activo con la conexión verificada.",
            note: "En el tutorial el archivo no se descarga: el cursor solo muestra dónde está el comando.",
          },
        },
      },
      status: {
        pending: "A la espera de datos",
        verified: "Verificado",
        notVerified: "No verificado",
      },
      sections: {
        tipologia: "Tipo de conexión y ángulo",
        element1: "Datos elemento 1",
        element1Panel: "Datos elemento 1 (tablero)",
        element1Central: "Datos elemento 1 (central)",
        element1PanelCentral: "Datos elemento 1 (tablero central)",
        element13Steel: "Datos elementos 1 y 3 (chapas)",
        element13Panel: "Datos elementos 1 y 3 (tableros)",
        element2: "Datos elemento 2",
        element2Central: "Datos elemento 2 (madera central)",
        element23: "Datos elementos 2 y 3 (laterales)",
        loads: "Cargas de cálculo",
        connection: "Elección de la conexión",
        grid: "Retícula de tornillos",
      },
      engine: {
        a4Reduce: {
          notThin: (t: string, fiveD: string) => `— no aplicado: espesor t=${t}mm no inferior a 5d=${fiveD}mm.`,
          spacing: (twentyFiveD: string, values: string) =>
            `— no aplicado: requiere a1, a3,t y a3,c ≥ 25d=${twentyFiveD}mm (actuales: ${values}).`,
        },
        block: {
          noScrew:
            "ningún tornillo es compatible con los espesores y materiales introducidos: modifica los espesores t1/t2 o los materiales, o elige otra familia/diámetro.",
          length: (L: number, rule: string, values: string, washerH: number) =>
            `la longitud del tornillo (L=${L} mm) no es compatible con los espesores: se requiere ${rule} (${values}${washerH > 0 ? `, arandela h=${washerH} mm` : ""}).`,
          partialThread: (rule: string, aRule: { rule: string; values: string } | null, values: string) =>
            `tornillo de rosca parcial no compatible con los espesores (regla de compatibilidad): se requiere ${rule}${aRule ? ` y ${aRule.rule} (aquí ${aRule.values})` : ""} (aquí ${values}).`,
          penetrationRule: (screw: string) =>
            `tornillo no compatible con los espesores (regla de compatibilidad sobre longitud/rosca/penetración para ${screw}).`,
          minSize: (screw: string) =>
            `${screw} no compatible con el espesor/ancho mínimos de los elementos (regla de compatibilidad): aumenta los espesores o elige un diámetro menor.`,
          emptyFields: (tutorial: string) => `rellena los campos que aún están vacíos. Consulta ${tutorial} para la introducción guiada.`,
          cltNarrowPenetration: (lt1: string, pen: string, min: string, neededL: string) =>
            `penetración en el panel CLT (lado estrecho) L−${lt1} = ${pen} mm inferior al mínimo 10·d = ${min} mm requerido por la ETA-11/0030: el método de cálculo no es aplicable. Aumenta la longitud del tornillo (L ≥ ${lt1}+10d = ${neededL} mm) o elige un tornillo de diámetro menor.`,
          penetration: (ptN: number, lt1: string, pen: string, min: string, neededL: string) =>
            `penetración en el elemento ${ptN} L−${lt1} = ${pen} mm no superior al mínimo 4·d = ${min} mm: el método de cálculo no es aplicable. Aumenta la longitud del tornillo (L > ${lt1}+4d = ${neededL} mm) o elige un tornillo de diámetro menor.`,
        },
        cltWideOnly: {
          panelSides:
            "Conexión tablero-madera en dos lados: los tornillos entran por las dos caras anchas de la madera central: solo se admite el lado ancho.",
          steelSides:
            "Conexión acero-madera en dos lados: los tornillos entran por las dos caras anchas de la madera central: solo se admite el lado ancho.",
          three:
            "Conexión de 3 elementos: los tornillos atraviesan los elementos 2 y 3 desde la cara ancha: solo se admite el lado ancho.",
        },
        epsNote: {
          element1:
            '— ángulo entre el eje del tornillo y la fibra del elemento 1, usado SOLO para f<sub>h,1,k</sub> (aplastamiento): distinto del ángulo fuerza/fibra usado para las distancias mínimas a1/a2, que permanece invariable. Desmarca "automático" para fijarlo manualmente.',
          element2: (elementRef: string) =>
            `— ángulo entre el eje del tornillo y la fibra de <span id="lbEps2n">${elementRef}</span>, usado SOLO para f<sub>h,k</sub> (aplastamiento): distinto del ángulo fuerza/fibra usado para las distancias mínimas a1/a2 (φ<sub>2</sub> arriba, ligado a θ/Fn/Fv), que permanece invariable. Desmarca "automático" para fijarlo manualmente en ambos casos.`,
          cltWide:
            'Panel CLT, lado ancho (ETA-11/0030): ε = ángulo entre la fuerza y la fibra de la capa exterior, usado en f<sub>h,k</sub> (aplastamiento como para madera maciza con el 110 % de la densidad mínima de las capas). Automático = calculado a partir de θ, F<sub>n</sub>/F<sub>v</sub> y la orientación de la capa exterior; desmarca "automático" para fijarlo manualmente.',
        },
        layout: {
          spacingBelowMin: (spacing: string, min: string) =>
            `⚠ ${spacing} mm inferior al mínimo requerido (${min} mm).`,
          lengthBelowEndDistance: (name: string, current: number, required: string) =>
            `⚠ aumenta ${name} (actual ${current} mm): debe ser ≥ ${required} mm — la longitud del elemento no puede ser inferior a la distancia de extremo requerida.`,
          cltHalfThickness: (d2: string, required: string, minThickness: string) =>
            `⚠ d2 = b2/2 = ${d2} mm es inferior a ${required} mm: aumenta el espesor del tablero (se requiere un espesor total ≥ ${minThickness} mm) o reduce el diámetro del tornillo.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— filas y/o columnas reducidas a ${rows}×${cols} para mantener dentro de 2·b1 = ${max} mm la distancia entre los tornillos situados en los extremos de la retícula`,
        },
        maxScrews: {
          none: "<strong>Ninguna disposición admisible</strong> — ni siquiera un tornillo respeta simultáneamente las distancias mínimas de ambos elementos.",
          max: (rows: number, cols: number, total: number) =>
            `<strong>Máximo teórico: ${rows} filas × ${cols} columnas = ${total} tornillos</strong>`,
          spacing: (s1: string, s2: string) =>
            ` (paso s<sub>1</sub>=${s1} mm a lo largo del eje del elemento 1, s<sub>2</sub>=${s2} mm a lo largo del eje del elemento 2 — retícula oblicua, mínimo normativo para ambos ejes)`,
          offset: (x: string, y: string) =>
            `— retícula desplazada del centro (desplazamiento del centro: horiz.=${x} mm, vert.=${y} mm) para que quepa entera: una retícula REGULAR (mismo paso uniforme, sin desfase) centrada en el cruce de los ejes elemento1/elemento2 contendría menos, porque la envolvente admisible no es simétrica respecto a ese cruce.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— ángulo rasante (θ≤20° o θ≥160°): filas y/o columnas reducidas a ${rows}×${cols} para mantener dentro de 2·b<sub>1</sub> = ${max} mm la distancia entre los tornillos situados en los extremos de la retícula. Desmarca la casilla correspondiente para considerar todas las filas/columnas geométricamente admisibles.`,
          capped:
            "— búsqueda limitada a un tope práctico de 500×500: con el paso mínimo, en esta geometría (elemento 2 con lado superior infinito y/o elemento 1 con lado F infinito), el número de tornillos admisibles podría ser incluso mayor.",
        },
        panel: {
          notAdmitted: (classe: string) =>
            `el tablero «${classe}» no está admitido por la ETA-11/0030 (admitidos contrachapado EN 636, OSB/3-OSB/4, tableros de partículas EN 312): elige otro material para el elemento 1.`,
          noEmbedment: (d: string) =>
            `f_h,k del tablero no determinable para d = ${d} mm (EC5 §8.5.1.2: para d > 8 mm solo contrachapado, OSB y tableros de partículas).`,
          noKmod: (classe: string, serviceClass: string) =>
            `k_mod del tablero «${classe}» no definido en EC5 Tabla 3.1 para la clase de servicio ${serviceClass}: el tablero no es apto para esta clase de servicio.`,
          tooThin: (t1: string, minT: number, tMin: string) =>
            `espesor del tablero t1 = ${t1} mm inferior al mínimo max(${minT} mm; 1,2·d) = ${tMin} mm (ETA-11/0030): el método de cálculo no es aplicable.`,
          smallHead: (dk: string, twoD: string) =>
            `diámetro de la cabeza dk = ${dk} mm inferior a 2·d = ${twoD} mm: las fórmulas EC5 §8.3.1.3 para f_h,k de los tableros requieren cabeza ≥ 2·d.`,
          onlyHbsTbs: "en el módulo tablero-madera solo se admiten tornillos HBS y TBS.",
        },
        predrill: {
          reason: (rho: string, limit: number, element: number, hard: boolean, stainless: boolean) =>
            `ρk = ${rho} kg/m³ > ${limit} kg/m³ (elemento ${element}${hard ? ", frondosa" : ""}, tornillos de ${stainless ? "acero inoxidable" : "acero al carbono"})`,
          forced: (reasons: string) => `— Pretaladro impuesto (ETA-11/0030 pág. 8 y 14): ${reasons}.`,
        },
        resistance: {
          ropeCap: (rp: string) =>
            ` — F<sub>ax</sub>/4 limitado al 100 % de la parte de Johansen (${rp} N, EC5 §8.2.2(2))`,
          mode: (mode: string) => `modo (${mode})`,
          steelPlate: {
            thin: "chapa delgada",
            thick: "chapa gruesa",
            interp: "chapa de espesor intermedio",
          },
        },
        results: {
          spreadNote: (s1: string, s2: string, s1Min: string, s2Min: string) =>
            `Tornillos distribuidos en la zona admisible: s<sub>1</sub> = ${s1} mm, s<sub>2</sub> = ${s2} mm (mínimos requeridos ${s1Min} / ${s2Min} mm); los tornillos exteriores están a las distancias mínimas de borde.`,
          staggered: (lEff: string, limit: string, d: string, dir: string, element: number) =>
            `ℹ tornillos de los dos lados desfasados: L−h = ${lEff} mm supera ${limit} mm, por lo que los tornillos del elemento 3 se desplazan 1·d = ${d} mm (dirección ${dir}) respecto a los del elemento ${element}, para evitar que las puntas se solapen en el central.`,
          tipsOverlap: (steel3: boolean, lEff: string, limit: string, tMinus: string, fourD: string, gridTitle: string) =>
            `ℹ puntas de los dos lados solapadas en el ${steel3 ? "madera" : "elemento"} central: L−h = ${lEff} mm supera ${limit} mm. Se admite (EC5 §8.3.1.1(7)) porque (t − t2) = ${tMinus} mm > 4d = ${fourD} mm; para desfasar los tornillos de los dos lados 1·d activa la casilla en el panel ${gridTitle}.`,
          washerNet: (h: string, lEff: string) => ` — una vez descontada la arandela (h=${h} mm): L−h=${lEff} mm`,
          screwTooLong: (l: string, rule: string, margin: string, twoSides: boolean) =>
            `⚠ tornillo demasiado largo (L=${l}): debe ser L < ${rule} (al menos ${margin} mm desde la punta hasta la cara opuesta${twoSides ? " del elemento central, EC5: (t − t2) > 4d" : ""}).`,
          screwTooShortClt: (l: string, penetration: string, tenD: string, need: string) =>
            `⚠ tornillo demasiado corto (L=${l}): penetración en el panel CLT (lado estrecho) ${penetration}, mínimo 10·d = ${tenD} mm (ETA-11/0030) — se requiere ${need}.`,
          screwTooShort: (l: string, rule: string, element: number) =>
            `⚠ tornillo demasiado corto (L=${l}): debe ser ${rule} (penetración mínima de 4d en el elemento ${element}).`,
          cltInfo: (thickness: string, rhok: string, classe: string) =>
            `Espesor total del tablero: ${thickness} mm · ρk = ${rhok} kg/m³ (clase ${classe})`,
          cltNarrowThin: (threshold: string) =>
            ` — ⚠ inferior a 10·d = ${threshold} mm requeridos por la ETA-11/0030 Tab. B1 para la inserción en el lado estrecho.`,
        },
        screwLists: {
          noFamily: (el1: string, el2: string) =>
            `⚠ ninguna familia compatible (tipo de conexión/clase de servicio) tiene un tornillo compatible con los espesores actuales (${el1}mm, ${el2}mm): reduce el espesor.`,
          noDiameter: (el1: string, el2: string) =>
            `⚠ ningún diámetro de esta familia tiene una longitud compatible con los espesores actuales (${el1}mm, ${el2}mm): reduce el espesor o elige otra familia.`,
          noLength: (lower: string, upper: string, washerH: number, el1: string, el2: string) =>
            `⚠ ninguna longitud de esta familia/diámetro queda entre ${lower} y ${upper} mm${washerH > 0 ? ` (con arandela h=${washerH} mm)` : ""} (espesores actuales ${el1}mm, ${el2}mm): reduce el espesor o elige un diámetro distinto.`,
        },
        threeNotes: {
          twoSides:
            "Elemento 1 central (cargado, girado θ). Elementos 2 y 3 laterales, fijos e idénticos: los datos del elemento 2 valen también para el elemento 3. Tornillos insertados desde los dos lados exteriores (cabeza sobre el elemento 2 y el elemento 3, punta en el elemento 1): mismo tornillo y retícula especular; la carga se reparte por igual entre las dos conexiones (una por lado, cada una a cortante simple con un solo plano de cortante).",
          double:
            "Elemento 1 central (cargado, girado θ). Elementos 2 y 3 laterales, fijos e idénticos: los datos del elemento 2 valen también para el elemento 3. Un único tornillo atraviesa los tres elementos: cabeza sobre el elemento 2, paso por el elemento 1, punta en el elemento 3 (cortante doble, EC5 ec. 8.7): una sola retícula de tornillos, sin desfase.",
          steelSides:
            "Elemento 2 de madera central y fijo. Chapas de acero en los lados: elemento 1 y su copia idéntica (elemento 3), cargados y girados θ; los datos del elemento 1 valen también para el elemento 3. Tornillos insertados desde los dos lados exteriores (cabeza sobre las chapas, punta en la madera): mismo tornillo y retícula especular; la carga se reparte por igual entre las dos conexiones (una por lado, cada una a cortante simple con un solo plano de cortante).",
          panelDouble:
            "Elemento 1 = tablero a base de madera, central (cargado, girado θ). Elementos 2 y 3 de madera, laterales, fijos e idénticos: los datos del elemento 2 valen también para el elemento 3. Un único tornillo (solo HBS y TBS, cabeza ≥ 2·d) atraviesa los tres elementos: cabeza sobre el elemento 2, paso por el tablero, punta en el elemento 3 (cortante doble, EC5 ec. 8.7): una sola retícula de tornillos, sin desfase. Aplastamiento del tablero según EC5 §8.3.1.3 / §8.5.1.2; distancias con a1, a2 ×0,85; kmod = √(kmod,tablero·kmod,madera).",
          panelSides:
            "Elemento 2 de madera central y fijo. Tableros a base de madera en los lados: elemento 1 y su copia idéntica (elemento 3), cargados y girados θ; los datos del elemento 1 valen también para el elemento 3. Solo tornillos HBS y TBS con cabeza ≥ 2·d. Tornillos insertados desde los dos lados exteriores (cabeza sobre los tableros, punta en la madera): mismo tornillo y retícula especular; la carga se reparte por igual entre las dos conexiones (una por lado, cada una a cortante simple con un solo plano de cortante). Aplastamiento del tablero según EC5 §8.3.1.3 / §8.5.1.2; distancias con a1, a2 ×0,85; kmod = √(kmod,tablero·kmod,madera).",
        },
        violations: {
          edge2: (code: string) => `elemento 2: borde (${code})`,
          end2: (code: string) => `elemento 2: extremo inferior (${code})`,
          line1: (n: string, code: string) => `elemento 1: recta ${n} (${code})`,
          side3: (text: string) => `lado elemento 3 (desfasados 1d): ${text}`,
        },
      },
      elementRefs: {
        element1: "elemento 1",
        element1Central: "elemento 1, central",
        elements13Steel: "elementos 1 y 3, chapas",
        elements13Panel: "elementos 1 y 3, tableros",
        element2: "elemento 2",
        element2Central: "elemento 2, madera central",
        elements23: "elementos 2 y 3",
      },
      typeSection: {
        theta: "θ — ángulo del elemento 1",
        thetaUnit: "0°→F hacia abajo · 180°→F hacia arriba · antihorario",
        connectionType: "Tipo de conexión",
        types: {
          legno: "madera-madera / cortadura simple",
          legno3: "madera-madera / cortadura simple en dos lados",
          legno3d: "madera-madera / cortadura doble",
          acciaio: "acero-madera / cortadura simple",
          acciaio3: "acero-madera / cortadura simple en dos lados",
          pannello: "tablero-madera / cortadura simple",
          pannello3: "tablero-madera / cortadura simple en dos lados",
          pannello3d: "tablero-madera / cortadura doble",
        },
      },
      // Names of the engine's material type keys (and of the species fallbacks of panels/steel)
      materialTypes: {
        solidwoodSoftwood: "Madera maciza de coníferas",
        glulam: "Madera laminada encolada",
        clt: "Paneles CLT",
        lvl: "LVL (madera microlaminada)",
        solidwoodHardwood: "Madera maciza de frondosas",
        panels: "Tableros derivados de la madera",
        steel: "Acero",
        panelsSpecies: "Tableros OSB / de cemento-madera",
        steelSpecies: "Acero estructural",
      },
      element: {
        materialType: "Tipo de material",
        materialTypeUnit: "BD madera",
        species: "Especie de madera",
        class: "Clase",
        customName: "Nombre del material",
        customNameUnit: "personalizado",
        customNamePlaceholder: "p. ej. Madera especial XYZ",
        rhoHint:
          "ρ<sub>k</sub> y ρ<sub>mean</sub> se rellenan desde la base de datos al elegir el material y siguen siendo editables.",
        rhoMissing: "ρ<sub>k</sub> no disponible en la base de datos, introducir manualmente",
        fiber: (n: number) => `φ${n === 1 ? "₁" : "₂"} — orientación de la fibra`,
        fiberUnit1: "respecto al eje del elemento 1",
        fiberUnit2: "respecto al eje del elemento 2",
        fiberUnit23: "respecto al eje de los elementos 2 y 3",
        eps: (n: number) => `ε${n === 1 ? "₁" : "₂"} — ángulo eje tornillo/fibra`,
        epsUnit1: "solo para fₕ,ₖ del elemento 1",
        epsUnit2: "solo para fₕ,ₖ del elemento 2",
        epsUnit23: "solo para fₕ,ₖ de los elementos 2 y 3",
        epsAuto: (n: number) => `ε<sub>${n}</sub> automático`,
        epsAutoUnit1: "siempre 90° (tornillo siempre ⟂ a la fibra, independiente de θ)",
        epsAutoUnit2: "90° para acero-madera (tornillo siempre ⟂ a la fibra, independiente de θ); derivado de θ para madera-madera",
        continuous: "Elemento continuo",
        continuousUnit: "sin extremo real: continúa más allá de la unión por ambos lados",
        thickness: (n: number) => `espesor t${n}`,
        rhomeanUnit: "kg/m³ — para Kₛₑᵣ",
        rhomeanMissing: "No disponible en la base de datos para esta clase: introducir manualmente",
        douglas: "Especie abeto Douglas",
        douglasUnit: "sin pretaladro: ETA-11/0030 exige a1 y a3 ×1,5",
        a4reduce: (n: number) => `Reducir a4,c,${n} (borde no cargado) a 3d`,
        a4reduceUnit1: "madera delgada, requiere a1 y a3,t,1 ≥ 25d",
        a4reduceUnit2: "madera delgada, requiere a1 y a3 ≥ 25d",
        a1e: (n: number) => `a1,elemento${n}`,
        a2e: (n: number) => `a2,elemento${n}`,
        a1Unit: "mm · separación mín. a lo largo del eje",
        a2Unit: "mm · separación mín. perpendicular",
        a3t1: "a3,t,1 (extremo cargado)",
        a3t2Unit: "mm · extremo, 0°≤θ≤90°",
        a3c2Unit: "mm · extremo, 90°<θ≤180°",
      },
      clt: {
        lato: "Lado de inserción",
        latoOptions: {
          narrow: "Narrow (canto estrecho, tornillo paralelo al plano)",
          wide: "Wide (cara ancha, tornillo perpendicular al plano)",
        },
        latoHint1: "Elemento 1 en conexión madera-madera: solo se admite el lado wide.",
        latoHint2:
          "Lado narrow: b<sub>2</sub> = espesor total del panel y d<sub>2</sub> = b<sub>2</sub>/2 (automáticos); t<sub>2</sub> (profundidad de inserción) es editable.",
        numStrati: "Número de capas",
        disposizione: "Disposición de las capas",
        disposizioni: {
          fisso: "Fija (alternada 0°/90°)",
          alternati: "Alternadas",
          accoppiati: "Emparejadas (0° emparejadas en las capas exteriores)",
          zeroDoppio: "Todas las capas a 0° dobles (solo 11 capas)",
          userDefined: "Definición del usuario (por capa)",
        },
        spessoriMode: "Espesor de las capas",
        spessoriModes: {
          uniforme: "Uniforme (mismo espesor para todas las capas)",
          differenziato: "Diferenciado (0° / 90°)",
        },
        spessoreSingle: "Espesor de cada capa",
        spessore0: "Espesor de las capas a 0°",
        spessore90: "Espesor de las capas a 90°",
        classe: "Clase de madera maciza de conífera",
        userLayers: "Capas — espesor, dirección, clase",
        userLayersUnit: "del exterior al interior",
        layer: (i: number) => `Capa ${i}`,
        layerThickness: "Espesor (mm)",
        layerDirection: "Dirección de la fibra",
        layerClass: "Clase de madera maciza",
      },
      loadsSection: {
        fn: "Fn, fuerza axial",
        fnUnit: "kN · a lo largo del eje del elemento 1",
        fv: "Fv, fuerza cortante",
        fvUnit: "kN · perpendicular al eje del elemento 1",
        fa: "Fa, fuerza de extracción",
        faUnit: "kN · a lo largo del eje del tornillo, positiva = extracción",
        resultantAngle: "ángulo resultante/fibra",
        withdrawal: "extracción",
        notYetVerified: "todavía no verificada a extracción",
        serviceClass: "Clase de servicio",
        loadDuration: "Duración de la carga",
        gammaMRef: "Ref. γM",
        gammaMRefUnit: "conexiones/uniones",
        standards: {
          EC5: "EC5 (Tabla 2.3 — conexiones)",
          NTC2018: "NTC2018 (Tab. 4.4.III — uniones)",
        },
        column: "Columna",
        columns: {
          A: "A — producción corriente",
          B: "B — con control FPC certificado",
        },
        gammaMAuto: "γ<sub>M</sub> automático (según la normativa seleccionada)",
      },
      connectionSection: {
        family: "Familia de tornillo",
        foro: "Tipo de agujero",
        predrilled: "Con pretaladro",
        nonPredrilled: "Sin pretaladro",
        diameter: "Diámetro exterior de la rosca d",
        length: "Longitud del tornillo L",
        lengthUnit: "mm · de la base de datos de tornillos",
        washer: "Usar arandela",
        washerUnit:
          "aumenta el diámetro eficaz de la cabeza (d<sub>h</sub>) en la verificación de penetración de la cabeza (F<sub>ax,ε,Rk</sub>)",
        autoSpacing: "Distancias mínimas automáticas (a1, a2, a3, a4 según normativa)",
      },
      notes: {
        panel:
          "Elemento 1 = panel a base de madera (contrachapado, OSB/3-OSB/4, aglomerado), en el lado de la cabeza del tornillo, cargado y girado θ. Elemento 2 = madera, fijo. Solo tornillos HBS y TBS con cabeza ≥ 2·d. Penetración en el panel según EC5 §8.3.1.3 (ec. 8.20–8.22) o §8.5.1.2 (ec. 8.36–8.37) para d &gt; 8 mm; distancias mínimas en el panel según EC5 Tabla 8.2 con α = 0 y sin dependencia de ρ<sub>k</sub>, y a<sub>1</sub>, a<sub>2</sub> ×0,85 en panel y madera (§8.3.1.3); k<sub>mod</sub> = √(k<sub>mod,panel</sub>·k<sub>mod,madera</sub>) (§2.3.2.2).",
        continuous: (n: 1 | 2): string =>
          n === 1
            ? "— d1 y a3,t,1 ya no son necesarios (pierden sentido: no existe un extremo real). El límite de posicionamiento de los tornillos pasa a ser directamente un extremo virtual, interno y oculto, igual a b2/(2·|cos(θ)|), medido desde la intersección de los ejes de los dos elementos."
            : "— d2 y a3,t,2/a3,c,2 ya no son necesarios (pierden sentido: no existe un extremo real). El límite de posicionamiento de los tornillos pasa a ser directamente un extremo virtual, interno y oculto, igual a b1/(2·|cos(θ)|), medido desde la intersección de los ejes de los dos elementos.",
        continuousDisabled:
          "— no disponible con θ=0°/180°: los ejes de los dos elementos coinciden (el elemento 1 y el elemento 2 quedarían alineados sobre la misma recta).",
        steel1Spacing:
          "— elemento 1 en placa de acero: una placa no tiene fibra propia, por lo que a1/a2 del elemento 1 no son un requisito físico y no influyen en el paso de la rejilla (EC5 §8.3.1.4): s1/s2 dependen solo de a1,elemento2/a2,elemento2 (ya ×0,7).",
        steel1Dist:
          "— elemento 1 en placa de acero: a4,t,1 = a4,c,1 = 1,5·1,1·d, a3,t,1 = 1,2·1,1·d (distancia mínima de taladro en la placa, no Tabla 8.2 de madera)",
        a3t1Rule:
          "— regla: ningún tornillo puede estar más cerca de a3,t,1 del extremo real del elemento 1 (línea 3 del dibujo), tanto en madera como en placa de acero — los tornillos de esa zona se marcan en rojo y no cuentan para la resistencia.",
        foro:
          '— "Sin pretaladro": la fórmula de las distancias mínimas se deduce automáticamente de la familia de tornillos seleccionada arriba (KKT para las familias KKT, fórmula HBS para las familias HBS con d = 6…12 mm, EC5 Tabla 8.2 para todos los demás tornillos y diámetros, según ETA-11/0030).',
        plateHw:
          "— familia HBS PLATE / LBS HARDWOOD EVO para unión madera-madera: ETA-11/0030 exige a1 y a2 ×1,5 en ambos elementos.",
        myk:
          "— My,k no disponible en la base de datos de tornillos para esta familia/diámetro: algunos modos de rotura de Johansen no se consideran, el resultado puede no ser conservador. Verifique o complete los datos en Screw.xlsx.",
        limitGrid:
          "— límite desactivado: puede introducir más tornillos de los geométricamente admisibles; los que quedan fuera de la envolvente siguen marcados en rojo y en el panel inferior, bajo su responsabilidad",
      },
      gridSection: {
        rows: "filas (a lo largo del eje del elemento 1)",
        rowsUnit: "máx. según θ/tornillo",
        cols2: "columnas (a lo largo del eje del elemento 2)",
        cols23: "columnas (a lo largo del eje de los elementos 2 y 3)",
        cltRowNote: "Elemento 2 de CLT, lado narrow: una sola fila de tornillos, en el eje del espesor del elemento 2.",
        s1Unit: "mm · a lo largo del eje del elemento 1",
        s2Unit2: "mm · a lo largo del eje del elemento 2",
        s2Unit23: "mm · a lo largo del eje de los elementos 2 y 3",
        offsetX: "Desplazamiento del centro, horizontal",
        offsetY: "Desplazamiento del centro, vertical",
        spread: "Distribuir los tornillos en la zona admisible (separación máxima, solo con distancias mínimas automáticas)",
        stagger: "Desplazar 1d los tornillos de la fila paralela a la fibra (evita la reducción de n<sub>ef</sub>)",
        staggerTwoSides:
          "Desplazar 1d los tornillos de los dos lados si las puntas se superponen (de lo contrario quedan superpuestas, siempre que (t − t<sub>2</sub>) &gt; 4d)",
        maxScrews: "Calcular el número máximo de tornillos",
        applyMax: "Aplicar filas/columnas",
        limitGrid: "Limitar filas/columnas al número máximo admisible (según θ y diámetro del tornillo)",
        limitGrazing:
          "Con ángulo rasante (θ≤20° o θ≥160°) limitar a 2×b<sub>1</sub> la distancia entre los tornillos situados en los extremos de la retícula",
      },
      view: {
        title: "Vista 3D",
        showDims: "Mostrar cotas",
        branchMeta: (theta: string, branch: string) => `θ = ${theta}°  ·  rama ${branch}`,
        branchPending: "rama —",
        hintControls:
          "Arrastrar = girar · rueda = zoom · botón derecho = desplazar · menú Vista = vistas predefinidas · menú Elementos = mostrar u ocultar componentes.",
        hintTail: "el contexto geométrico; los tornillos son el producto verificado por el cálculo. Los tornillos fuera de la envolvente aparecen en rojo.",
        hintSubjects: {
          two: "Los dos elementos de madera son",
          three: "Los tres elementos de madera son",
          panel: "El tablero y la madera son",
          steel3: "Los tres elementos (dos chapas y la madera) son",
          panel3: "Los tres elementos (dos tableros y la madera) son",
        },
        legend: {
          el2: "elemento 2 (madera, fijo)",
          el2Central: "elemento 2 (madera central, fijo)",
          el23: "elementos 2 y 3 (madera, fijos, idénticos)",
          el1: "elemento 1 (girado θ)",
          el1Panel: "elemento 1 (tablero, girado θ)",
          el1Central: "elemento 1 (central, girado θ)",
          el1PanelCentral: "elemento 1 (tablero central, girado θ)",
          el13Steel: "elementos 1 y 3 (chapas, girados θ)",
          el13Panel: "elementos 1 y 3 (tableros, girados θ)",
          screwOk: "tornillo admisible",
          screwBad: "tornillo no admisible",
          dims: "cotas (valores de entrada)",
          fn: "Fn (axial)",
          fv: "Fv (cortante)",
          fa: "Fa (extracción, a lo largo del eje del tornillo)",
        },
        toolbar: {
          views: {
            iso: "Vista 3D",
            front: "Alzado",
            top: "Planta",
            side: "Vista lateral",
          },
          menuView: "Vista",
          menuElements: "Elementos",
          resetView: "Restablecer vista",
          elements: {
            element1: "Elemento 1",
            elements13: "Elementos 1 y 3",
            element2: "Elemento 2",
            elements23: "Elementos 2 y 3",
            screws: "Tornillos",
            forces: "Fuerzas",
            grid: "Cuadrícula",
            solid: "Sólido",
            ghost: "Semitransparente",
            hidden: "Oculto",
          },
        },
        capture: {
          legibilityWarning: (lines: string) =>
            `Atención: algunas cotas de las vistas del informe podrían no ser legibles (${lines}).`,
          legibilityLine: (view: string, clipped: number, overlapping: number) =>
            `${view}: ${clipped} cotas cortadas, ${overlapping} superpuestas`,
        },
      },
      stats: {
        layout: "Disposición de los tornillos",
        layoutOk: "Admisible",
        layoutBad: "No admisible",
        combined: "Carga combinada (EC5 8.28)",
        fvRkScrew: "F<sub>v,Rk</sub> por tornillo",
        nefAxial: "n eficaz (axial)",
        nefShear: "n eficaz (cortante)",
        admissibleScrews: (ok: number, total: number, twoSides: boolean) =>
          `tornillos adm. ${ok}/${total}${twoSides ? " por lado (×2 lados)" : ""}`,
        fvRkUnion: "F<sub>v,Rk</sub> unión",
        totalKn: "kN totales",
        faxRd: "F<sub>ax,Rd</sub>",
        kmodGammaM: "k<sub>mod</sub> · γ<sub>M</sub>",
        fvEd: "F<sub>v,Ed</sub>",
        fvRdUnion: "F<sub>v,Rd</sub> unión",
        kser: "K<sub>ser</sub> por plano de cortadura",
        kserUnit: "(ELS)",
        notAvailable: "N.D.",
        shearCheck: "Verificación a cortante F<sub>v,Ed</sub>/F<sub>v,Rd</sub>",
        axialCheck: "Verificación a extracción F<sub>ax,Ed</sub>/F<sub>ax,Rd</sub>",
        splitCheck: "Verificación a hendimiento del elemento central",
        verified: "verificado",
        notVerified: "NO verificado",
      },
      violations: {
        title: "Tornillos fuera de la envolvente",
        allOk: "Todos los tornillos respetan las distancias mínimas requeridas.",
        blockedPrefix: "Cálculo no realizado: ",
      },
      solutions: {
        title: "Soluciones compatibles",
        intro:
          "Para cada tornillo actualmente compatible (familia/diámetro/longitud, tras los filtros de espesores/clase de servicio/tipo de conexión) busca el número mínimo de tornillos (filas×columnas, retícula centrada) que cumple todas las verificaciones activas (cortante; carga combinada y extracción solo si F<sub>a</sub>&gt;0). Cálculo pesado: se inicia bajo petición; tras la primera búsqueda se actualiza sola con cada cambio de los datos de entrada (la tabla anterior se borra). Durante la búsqueda se fuerzan temporalmente \"Distancias mínimas automáticas\" y \"Desplazamiento del centro\"=0 para todos los tornillos candidatos y después se restablece el estado actual.",
        predrilled: "Pretaladro",
        washer: "Arandela",
        optionsHint:
          "Sin marcar: solo soluciones sin pretaladro y sin arandela (la configuración del formulario no cuenta; los tornillos para los que el ETA impone el pretaladro se verifican igualmente con pretaladro). Con una marca: solo soluciones que usan esa opción (con \"Arandela\" se prueban todas las arandelas compatibles).",
        search: "Buscar soluciones compatibles",
        preparing: "Preparación...",
        progress: (processed: number, total: number, found: number) =>
          `Evaluación en curso: ${processed}/${total} tornillos candidatos (${found} soluciones encontradas hasta ahora)...`,
        missingTitle: (count: number) =>
          count === 1
            ? "No es posible generar las soluciones: rellenar el campo indicado."
            : "No es posible generar las soluciones: rellenar los campos indicados.",
        missingPanel: (panel: string) => `— panel «${panel}»`,
        none: (candidates: number) =>
          `No se ha encontrado ninguna solución entre ${candidates} tornillos candidatos: ninguna combinación familia/diámetro/longitud/retícula cumple todas las verificaciones dentro del número máximo de tornillos geométricamente admisible.`,
        summary: (found: number, candidates: number, shown: number) =>
          `${found} soluciones encontradas entre ${candidates} tornillos candidatos evaluados — se muestran las primeras ${shown}, ordenadas por % cortante (luego % combinada, luego % extracción).`,
        selectNew:
          "La configuración actual ya no es válida con los datos introducidos: <b>seleccione la nueva solución compatible</b> en la tabla (botón «Usar esta»).",
        columns: {
          family: "Familia",
          grid: "Filas×columnas (tot.)",
          options: "Opciones",
          shear: "η cortante",
          combined: "η combinada",
          withdrawal: "η extracción",
        },
        predrilledOption: "pretaladro",
        apply: "Usar esta",
      },
      formulas: {
        noDiameter:
          'dato no disponible (ningún diámetro de tornillo válido seleccionado — véase el aviso superior "Diámetro exterior de la rosca").',
        staggerNef: (n: number) =>
          `Desfase ≥1d activo (EC5 Tabla 8.1, nota 8): sin reducción, n<sub>ef</sub> = n = ${n} (tornillos admisibles).`,
        nefAxial: (n: number, pow: string, ninety: string, result: string) =>
          `n<sub>ef,axial</sub> = max(n<sup>0,9</sup>; 0,9·n) — ETA-11/0030 (tornillos autoperforantes, fila de n tornillos paralela a la carga) — n=${n} → max(${pow}; ${ninety}) = ${result}`,
        staggerNefShear: (n: number) =>
          `Desfase ≥1d activo (EC5 Tabla 8.1, nota 8): sin reducción, n<sub>ef,cortante</sub> = n = ${n}.`,
        rowParallel: (
          nRow: number,
          a1: string,
          a1d: string,
          element: number,
          reps: number,
          extra: string,
        ) =>
          `Fila de ${nRow} tornillos paralela a la carga (paso efectivo a1=${a1}mm=${a1d}d, elemento ${element}), repetida ${reps} veces (filas independientes)${extra}`,
        table81Steel: (a1Eff: string, ratio: string) =>
          ` (para la Tabla 8.1, acero-madera: a<sub>1</sub>/0,7 = ${a1Eff}mm=${ratio}d)`,
        kefLine: (kef: string, n: number, nef0: string) =>
          `k<sub>ef</sub> = ${kef} (EC5 Tabla 8.1) → n<sub>ef,0</sub> = n<sup>k<sub>ef</sub></sup> = ${n}<sup>${kef}</sup> = ${nef0}`,
        angleLine: (angle: string, panel: string, nef0: string, nRow: number, result: string) =>
          `ángulo efectivo fuerza/fibra = ${angle}°${panel} → n<sub>ef,cortante</sub> = n<sub>ef,0</sub> + (n − n<sub>ef,0</sub>)·(ángulo/90°) = ${nef0} + (${nRow} − ${nef0})×(${angle}/90) = ${result}`,
        panelNoInterp: " (panel-madera: sin interpolación con el ángulo, como en el informe de referencia)",
        totalShear: (reps: number, result: string, crit: string) =>
          `n<sub>ef,cortante</sub> (total) = ${reps} × ${result}${crit}`,
        critElement: (element: number) => ` (elemento ${element}, mayor número de tornillos en fila)`,
        doubleNote: "cortante doble: el mismo tornillo atraviesa los tres elementos, 2 planos de cortante",
        steelNote: (sides: string) => `dos uniones a cortante simple, una por lado: ${sides} con madera central 2`,
        threeNote: "dos uniones a cortante simple, una por lado: elementos 2 y 3 con elemento 1",
        sidePanels: "panel 1 y panel 3",
        sidePlates: "placa 1 y placa 3",
        govPlane: "determinante",
        kserNoRho:
          "ρ<sub>mean</sub> no disponible (N.D. en la base de datos para esta clase): introduzca manualmente ρ<sub>mean</sub> para calcular K<sub>ser</sub>.",
        kserLvlNote: (family: string) =>
          `<span class="report-formula">ETA-11/0030 — tornillo ${family} en madera LVL de conífera, 5≤d≤10mm, independiente del ángulo ε</span>`,
        kserPredrilled: "pretaladrado",
        kserNotPredrilled: "sin pretaladro",
        kserPanelRho: (rhok: string, rhoMean: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>panel</sub>·ρ<sub>mean,2</sub>) = √(${rhok}×${rhoMean}) = ${rhoM} kg/m³ (ρ<sub>mean</sub> del panel asumida igual a ρ<sub>k</sub>)`,
        kserMean2: (rhoM: string) => `ρ<sub>mean,2</sub> = ${rhoM} kg/m³`,
        kserTimberRho: (rho1: string, rho2: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>mean,1</sub>·ρ<sub>mean,2</sub>) = √(${rho1}×${rho2}) = ${rhoM} kg/m³`,
        kserSteelNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Tabla 7.1, como para clavos ${predrilled} — ${rho}; ×2,0 para uniones acero-madera (EC5 §7.1(3))</span>`,
        kserTimberNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Tabla 7.1, como para clavos ${predrilled} — ${rho}</span>`,
      },
      positioning: {
        title: "Posicionamiento de los tornillos respecto a los bordes",
        meta: "elemento 1 y elemento 2, cotas en mm",
        metaThree: "elemento 1 (central) y elementos 2 y 3, cotas en mm",
        metaSteel3: "elementos 1 y 3 (placas) y elemento 2 (madera central), cotas en mm",
        metaPanel3: "elementos 1 y 3 (paneles) y elemento 2 (madera central), cotas en mm",
        notAvailable: "Posicionamiento de tornillos no disponible: cálculo no realizado.",
        notAvailableSee: "Posicionamiento de tornillos no disponible: cálculo no realizado (véase el aviso a la derecha).",
        none: "Ningún tornillo admisible que posicionar: cambie filas/columnas o los datos de entrada.",
        missingDims: "Complete los datos geométricos para ver el posicionamiento de los tornillos.",
        captionElements13Plates: "Elementos 1 y 3 (placas idénticas)",
        captionElements13Panels: "Elementos 1 y 3 (paneles idénticos)",
        captionElement1: "Elemento 1",
        suffixPlate: " (placa)",
        suffixPanel: " (panel)",
        suffixCentral: " (central)",
        captionView1: " — vista en su propio plano: extremo abajo (opuesto a F), F hacia arriba",
        captionView2: " — vista en su propio plano: extremo inferior abajo",
        captionElements23: "Elementos 2 y 3 (idénticos)",
        captionElement2Timber: "Elemento 2 (madera central)",
        captionElement2: "Elemento 2",
        continuousNoEnd: " · elemento continuo: sin cota de extremo",
        axisElement1: "elemento 1",
        axisElements23: "elementos 2 y 3",
        axisElement2: "elemento 2",
        note: (p: {
          s1: string;
          s2: string;
          axis2: string;
          spread: boolean;
          s1Min: string;
          s2Min: string;
          rows: number;
          cols: number;
          staggered: boolean;
          nShow: number;
          nTotal: number;
          threeD: boolean;
          twoSides: boolean;
          steel3: boolean;
          panel1: boolean;
          stagger: boolean;
          d: string;
          dir: string;
        }) =>
          `Cotas en mm, desde el borde del elemento al eje del tornillo. Pasos de la rejilla: s<sub>1</sub> = ${p.s1} mm (a lo largo del eje del elemento 1), s<sub>2</sub> = ${p.s2} mm (a lo largo del eje de ${p.axis2})${p.spread ? ` — tornillos distribuidos hasta los límites del área admisible (pasos mínimos requeridos ${p.s1Min} / ${p.s2Min} mm)` : ""} · ${p.rows} × ${p.cols} tornillos${p.staggered ? ", desfasados" : ", alineados (no desfasados)"} · ${p.nShow} admisibles${p.nShow < p.nTotal ? ` de ${p.nTotal}; los tornillos fuera de la envolvente están en rojo y no deben instalarse` : ""}${p.threeD ? " · elementos 2 y 3 idénticos: el mismo tornillo atraviesa los tres elementos (una única rejilla)" : ""}${p.twoSides ? ` · elementos 2 y 3 idénticos: los tornillos del elemento 3 son la copia especular de los del elemento 2 (${p.nShow} por lado, ${2 * p.nShow} en total)${p.stagger ? `, desfasados 1·d = ${p.d} mm (dirección ${p.dir}) porque L−h supera t2 + t1/2: círculos vacíos = tornillos del elemento 3 (no acotados)` : ""}` : ""}${p.steel3 ? ` · ${p.panel1 ? "paneles 1 y 3 idénticos" : "placas 1 y 3 idénticas"}: los tornillos del lado 3 son la copia especular de los del lado 1 (${p.nShow} por lado, ${2 * p.nShow} en total)${p.stagger ? `, desfasados 1·d = ${p.d} mm (dirección ${p.dir}) porque L−h supera t1 + t2/2: círculos vacíos = tornillos de la placa 3 (no acotados)` : ""}` : ""}.`,
        tableNo: "N.º",
        tableElements13: (plates: string) => `Elementos 1 y 3 (${plates})`,
        tableElement1: "Elemento 1",
        tableElements23: "Elementos 2 y 3",
        tableElement2: "Elemento 2",
        tableDistance: "distancia al borde [mm]",
        tableEnd: "extremo",
        tableLeft: "izquierdo",
        tableRight: "derecho",
        tableMinMaxTitle: "Distancia mínima/máxima al borde [mm]",
        tableMin: "mínima",
        tableMax: "máxima",
        tableFromEnd1: "Elemento 1 — desde el extremo",
        tableFromLeft1: "Elemento 1 — desde el borde izquierdo",
        tableFromLeft2: (el2: string) => `${el2} — desde el borde izquierdo`,
        tableFromBottom2: (el2: string) => `${el2} — desde el extremo inferior`,
        tableMoreThan40:
          "Con más de 40 tornillos no se enumeran las coordenadas de cada tornillo: valen las cotas de conjunto de la figura y los pasos de la rejilla s<sub>1</sub>/s<sub>2</sub>.",
        platesWord: "placas",
        panelsWord: "paneles",
      },
      report: {
        title: "Informe de cálculo",
        note: "Vista previa del informe PDF que se imprime (datos, vistas, resultados, notas metodológicas).",
        versionLabel: "Versión del informe",
        full: "Completa",
        fullHint: "(con notas metodológicas)",
        compact: "Compacta",
        compactHint: "(sin notas metodológicas)",
        show: "Ver informe",
        hide: "Ocultar informe",
        generating: "Generando el informe…",
        generatePdf: "Generar informe PDF",
        exportDxf: "Exportar DXF",
        viewer: {
          toolbar: "Controles del visor del informe",
          previousPage: "Página anterior",
          pageNumber: "Número de página",
          nextPage: "Página siguiente",
          zoomOut: "Reducir zoom",
          zoom: "Zoom",
          zoomIn: "Aumentar zoom",
          fitWidthOption: (pct: number) => `Ajustar ancho (${pct} %)`,
          fitPageOption: (pct: number) => `Ajustar página (${pct} %)`,
          fitWidth: "Ajustar al ancho",
          fitPage: "Ajustar a la página",
          continuous: "Desplazamiento continuo",
          singlePage: "Página única",
          twoPages: "Dos páginas en paralelo",
          blackWhite: "Vista previa en blanco y negro",
          blackWhiteShort: "B/N",
          paginating: "Maquetando el informe…",
        },
        previewError: "No se ha podido generar la vista previa del informe: inténtalo de nuevo.",
      },
    },
  },

  envelopeDesignerModule: {
    resultCodes: {
      errors: {
        INVALID_REQUEST_BODY: "Cuerpo de la solicitud no válido.",
        INVALID_REQUEST_UNKNOWN_ACTION: "Solicitud no válida: acción desconocida.",
        INVALID_REQUEST_NO_LAYERS: "Solicitud no válida: no hay capas que calcular.",
        INVALID_REQUEST_NO_MATERIALS: "Solicitud no válida: faltan los datos de los materiales.",
        INVALID_REQUEST_NO_MONTHLY_CLIMATE: "Solicitud no válida: falta el clima mensual (12 temperaturas y presiones de vapor) - cargue un archivo EPW.",
        INVALID_REQUEST_OUTSIDE_SIDE: "Glaser mensual: la solicitud no indica cuál es el lado exterior.",
        LAYERS_TOO_MANY: (p: { max: number }) => `Solicitud no válida: como máximo ${p.max} capas.`,
        MONTHLY_OUTSIDE_TEMPERATURE_INVALID: "Dato no válido: una temperatura exterior media mensual está fuera del intervalo de -60 a 60 °C.",
        MONTHLY_OUTSIDE_VAPOUR_PRESSURE_INVALID: "Dato no válido: una presión de vapor exterior media mensual no está entre 0 y 10000 Pa.",
        MONTHLY_METHOD_UNKNOWN: "Dato no válido: método de cálculo desconocido.",
        MONTHLY_CONDENSATION_TOLERANCE_INVALID: "Dato no válido: la tolerancia de condensación debe ser 0 (exacta) o 1e-9.",
        MONTHLY_SERVER_ERROR: "Glaser mensual: el análisis ha fallado en el servidor.",
        CALCULATION_SERVER_ERROR: "El cálculo ha fallado en el servidor.",
        HUMIDITY_CLASS_INVALID: "Dato no válido: la clase de humedad UNI EN ISO 13788 debe ser 1, 2, 3, 4 o 5.",
        INSIDE_TEMPERATURE_INVALID: "Dato no válido: la temperatura interior debe ser un número entre -10 y 50 °C.",
        INSIDE_RELATIVE_HUMIDITY_INVALID: "Dato no válido: la humedad relativa interior debe ser un número mayor que 0 y como máximo 100 %.",
        EN15026_MONTHLY_VALUES_MISSING: "Dato no válido: el clima interior del Anexo D de la EN 15026 necesita los valores mensuales calculados a partir del archivo climático (vuelva a cargar el archivo EPW).",
        ASHRAE160_UNAVAILABLE: "Clima interior ASHRAE 160: aún no disponible - los valores de su método simplificado no están verificados.",
        INSIDE_MODEL_UNKNOWN: "Dato no válido: modelo de clima interior desconocido.",
      },
      warnings: {} as Record<string, { title: string; detail: string }>,
    },
    inputErrors: {
      layer: (position: number, material: string) => `Capa ${position} (${material})`,
      stud: (layer: string, material: string) => `${layer}, montante ${material}`,
      bad: { nan: "no es un número", infinite: "infinito", negative: (value: number) => `negativo (${value})`, zero: "0" },
      messages: {
        layerThickness: (subject: string, got: string) => `${subject}: el espesor debe ser mayor que 0 (valor: ${got}).`,
        layerLambda: (subject: string, got: string) => `${subject}: la conductividad térmica λ debe ser mayor que 0 (valor: ${got}).`,
        layerMu: (subject: string, got: string) => `${subject}: el factor de resistencia al vapor μ debe ser mayor que 0 (valor: ${got}).`,
        studWidth: (subject: string, got: string) => `${subject}: el ancho debe ser mayor que 0 (valor: ${got}).`,
        studSpacing: (subject: string, got: string) => `${subject}: la separación debe ser mayor que 0 (valor: ${got}).`,
        studDepth: (subject: string, got: string) => `${subject}: la profundidad debe ser mayor que 0 (valor: ${got}).`,
        studDepthExceeds: (subject: string, _got: string, depthMM: number, thicknessMM: number) =>
          `${subject}: la profundidad (${depthMM} mm) supera el espesor de su capa (${thicknessMM} mm).`,
        studLambda: (subject: string) => `${subject}: el material no tiene una conductividad térmica λ válida.`,
        studMu: (subject: string) => `${subject}: el material no tiene un factor de resistencia al vapor μ válido.`,
        rsi: (_subject: string, got: string) => `La resistencia superficial interior Rsi debe ser mayor que 0 (valor: ${got}).`,
        rse: (_subject: string, got: string) => `La resistencia superficial exterior Rse debe ser mayor que 0 (valor: ${got}).`,
        tInt: (_subject: string, got: string) => `La temperatura interior debe ser un número (valor: ${got}).`,
        tExt: (_subject: string, got: string) => `La temperatura exterior debe ser un número (valor: ${got}).`,
        rhInt: (_subject: string, got: string) => `La humedad relativa interior debe estar entre 0 y 100 % (valor: ${got}).`,
        rhExt: (_subject: string, got: string) => `La humedad relativa exterior debe estar entre 0 y 100 % (valor: ${got}).`,
      },
    },
    admin: {
      materials: "Materiales",
      examples: "Estratigrafías de ejemplo",
      manufacturer: "Fabricante",
      variable: "variable",
      deleteMaterialQuestion: "¿Seguro que quieres eliminar este material? Un material usado por un cálculo o un ejemplo no se puede eliminar: ocúltalo.",
      material: "material",
      subcategory: "Subcategoría",
      category: "Categoría",
      categories: "Categorías",
      subcategories: "Subcategorías",
      condCategories: "Categorías de condensación",
      rule: "Regla",
      condCategory: "Categoría de condensación (EN ISO 13788)",
      hatch: "Trama",
      thicknessMode: "Modo de espesor",
      defaultThickness: "Espesor de referencia",
      knownThicknesses: "Espesores disponibles (separados por comas)",
      minThickness: "Espesor mínimo",
      maxThickness: "Espesor máximo",
      sdCurveHint: "Solo membranas de Sd variable: puntos con HR como fracción 0-1 y Sd en metros.",
      sectionMembrane: "Membrana",
      sectionTestDatabase: "Base de datos de prueba (valores higroscópicos del archivo)",
      sectionCurves: "Curvas medidas",
      sectionProvenance: "Fuentes de los datos",
      sdVariableMin: "Sd variable, mín. [m]",
      sdVariableMax: "Sd variable, máx. [m]",
      dryCupPerm: "Permeancia dry-cup [US perm]",
      curvesHint: "Cada curva es una lista JSON de puntos, con las claves indicadas en su campo (vacía: sin curva).",
      moistureCurve: "Curva de sorción w(HR) [HR %, w kg/m³]",
      lambdaMoistureCurve: "λ(w) [w m³/m³, λ W/(m·K)]",
      muMoistureCurve: "μ(w) [w m³/m³, μ -]",
      sdHumidityCurve: "Curva Sd variable con la humedad Sd(HR) [HR 0-1, Sd m]",
      suctionCurve: "Curva de succión [log10 p_C Pa, w kg/m³]",
      liquidConductivityCurve: "Conductividad líquida [w kg/m³, log10 K_l s]",
      datasheetUrl: "Ficha técnica (URL)",
      productPageUrl: "Página del producto (URL)",
      datasheetChecked: "Ficha verificada el",
      formerlyNamed: "Nombre anterior",
      sourceDatabase: "Base de datos de origen",
      sourceLaboratory: "Laboratorio",
      sourceDate: "Fecha de la medición",
      sourceFile: "Archivo de origen",
      assumedFields: "Valores supuestos (no incluidos en la ficha)",
      derivedFields: "Valores derivados (calculados a partir de la ficha)",
      dataNote: "Nota sobre la ficha",
      dataNoteLambda: "λ de la ficha [W/(m·K)]",
      invalidCurve: (curve: string) => `${curve}: no es una lista de puntos válida.`,
      label: "Nombre",
      element: "Elemento",
      structure: "Estructura",
      regions: "Regiones",
      layerCount: "Capas",
      default: "predeterminado",
      deleteExampleQuestion: "¿Seguro que quieres eliminar este ejemplo?",
      example: "ejemplo",
      description: "Descripción",
      layersInsideOut: "Capas (interior → exterior)",
      addBridge: "Añadir montante/cabio",
      width: "ancho",
      spacing: "separación libre",
      fullDepth: "todo el espesor",
      addLayer: "Añadir capa",
    },
    errors: {
      materialAlreadyExists: "Ya existe un material con el mismo nombre y fabricante.",
      materialInUse: "El material lo usa un cálculo o un ejemplo: ocúltalo en lugar de eliminarlo.",
      exampleNotFound: "Ejemplo no encontrado.",
      invalidExample: "Estratigrafía de ejemplo no válida.",
      exampleAlreadyExists: "Ya existe un ejemplo con el mismo código.",
      invalidMaterial: "Material personalizado no válido.",
      materialNotFound: "Una capa usa un material que no está en el catálogo.",
      customMaterialNotFound: "Una capa usa un material personalizado inexistente.",
      invalidLayer: "Capa no válida.",
      invalidBridge: "Puente térmico no válido.",
      condCategoryNotFound: "Categoría de condensación desconocida.",
      categoryAlreadyExists: "Ya existe una categoría con el mismo código.",
      categoryInUseByMaterial: "Hay materiales de esta categoría: ocúltala en lugar de eliminarla.",
      categoryInUseBySubcategory: "Hay subcategorías de esta categoría: elimínalas o muévelas primero.",
      subcategoryAlreadyExists: "Ya existe una subcategoría con el mismo código.",
      subcategoryInUseByMaterial: "Hay materiales de esta subcategoría: ocúltala en lugar de eliminarla, y muévela solo a la categoría de sus materiales.",
    },
    nav: {
      project: "Proyecto",
      data: "Datos",
      dataTitle: "Datos: proyecto, emplazamiento, elemento, análisis y modo de cálculo",
      stratigraphy: "Estratigrafía",
      stratigraphyTitle: "Estratigrafía: capas y materiales del elemento",
      report: "Informe",
      reportTitle: "Informe: vista previa e impresión",
      general: "General",
      materialsDatabase: "Base de datos de materiales",
      materialsDatabaseTitle: "Base de datos de materiales: consultar, filtrar y añadir materiales",
      settings: "Configuración",
      settingsTitle: "Configuración: unidades, normas, cálculo y atajos de teclado",
      tutorial: "Tutorial",
      tutorialTitle: "Tutorial - una demostración de las funciones principales",
      toggleSidebar: "Contraer/expandir la barra lateral",
      analysisResults: "Análisis y resultados",
      analysisResultsTitle: "Análisis y resultados: el análisis estático y el cuasidinámico",
      quasiDynamic: "Cuasidinámico",
      quasiDynamicLockedTitle: "Análisis cuasidinámico: no disponible en esta beta",
      openQuasiDynamicTitle: "¿Abrir también el análisis cuasidinámico?",
      openQuasiDynamicMessage: "En la pestaña Datos se seleccionó el análisis estático. ¿Desea continuar también con el análisis cuasidinámico?",
      openQuasiDynamicConfirm: "Sí, abrirlo",
      reportLockedNote: "Disponible una vez calculado el análisis estático para esta estratigrafía",
      reportLockedToast: "El informe: disponible una vez calculado el análisis estático para esta estratigrafía.",
      completeDataFirst: "Complete primero la pestaña Datos (proyecto, elemento, análisis)",
      addLayerFirst: "Añada primero al menos una capa a la estratigrafía",
      openStratigraphyFirst: "Abra primero la Estratigrafía",
      statusTodo: "Por completar",
      statusOk: "Completo",
      statusWarn: "Cumplido, con una advertencia",
      statusBad: "Al menos una verificación no cumplida",
      statusBusy: "Calculando…",
      undoPrevious: "Deshacer la acción anterior",
      showRecentActions: "Mostrar las acciones recientes",
      redoNext: "Rehacer la acción siguiente",
      recentUndoHistory: "Historial reciente de cambios",
      undoMultiple: "Deshacer varias acciones",
      noActions: "Ninguna acción",
      historyEmpty: "Sus cambios recientes aparecerán aquí.",
      historyFoot: "Seleccione una acción para volver al estado inmediatamente anterior. Pase el cursor por encima para ver todos los detalles del cambio.",
    staticAnalysis: "Estático",
    staticAnalysisTitle: "Análisis estático: resultados (1D o 2D), Glaser mensual, configuración",
    quasiDynamicTitle: "Análisis cuasi-dinámico: datos de entrada y resultados",
    },
    preferences: {
      preferences: "Preferencias",
      preferencesTitle: "Preferencias: unidades y normas",
      units: "Unidades",
      unitsTitle: "SI: sistema métrico. Imperial: todos los valores convertidos (difusión de vapor como permeancia en US perm). UK: SI, salvo la difusión de vapor, expresada como resistencia al vapor en MN·s/g. Los cálculos se realizan siempre en SI: solo cambia lo que se muestra e imprime.",
      ukTitle: "Las mismas unidades SI que en el resto, salvo la difusión de vapor, expresada como resistencia al vapor en MN·s/g, la unidad habitual en la física de la construcción del Reino Unido",
      unitsHint: "Se guardan con el cálculo; un cálculo nuevo empieza con las últimas unidades elegidas (si no, SI).",
      standards: "Normas",
      standardEuTitle: "Los menús muestran las opciones UNI / EN (EN 15026, UNI EN ISO 13788)",
      standardNaTitle: "Los menús muestran las opciones ASHRAE (ASHRAE 160)",
      standardsHint: "Los menús con alternativas según la norma (lluvia en el análisis cuasidinámico, clima interior del análisis Glaser mensual) muestran solo las opciones de la norma elegida.",
    },
    settings: {
      changesPending: "Cambios aún no aplicados - Listo los aplica",
      changesApplyOnDone: "Los cambios se aplican al pulsar Listo",
      done: "Listo",
      title: "Configuración",
      keyboardShortcuts: "Atajos de teclado",
      solver2d: "Solucionador 2D",
      climate: "Clima",
      quasiDynamic: "Cuasidinámico",
      quasiDynamicLockedTitle: "Análisis cuasidinámico: no disponible en esta beta",
      quasiDynamicLockedLabel: "Cuasidinámico: no disponible en esta beta",
      about: "Acerca de",
      shortcutsHint: "Haga clic en un campo y pulse la combinación de teclas que desee.",
      resetToDefaults: "Restablecer valores predeterminados",
      solver2dHeading: "Método higrotérmico estacionario 2D — malla, iteraciones y tolerancias",
      solver2dIntro: "Controla la resolución acoplada por volúmenes finitos de temperatura y presión de vapor del modo de malla 2D. No afecta al método de Glaser 1D.",
      solvingMethod: "Método de resolución",
      solverCg: "Gradiente conjugado (precondicionado Jacobi)",
      solverCgHint: "El análisis estático 2D usa siempre el gradiente conjugado precondicionado Jacobi: converge también con barreras de vapor, donde Gauss-Seidel y el ciclo V se detienen antes de cumplir el balance energético.",
      meshRows: "Malla (filas en el espesor)",
      meshHint: "Auto (primera posición) recalcula con 20→40→70→100→160→250→300 filas y se detiene cuando el flujo de calor varía menos del 0,1 % entre dos mallas y el propio solucionador ha convergido (vea la tarjeta Diagnóstico). Deslice a la derecha para fijar la malla: 20, 40, 70, 100, 160, 250 o 300 filas; cuanto más fina, más precisa y más lenta.",
      maxIterationsBefore: "Iteraciones máx. (T y p",
      maxIterationsAfter: ", bucle acoplado)",
      epsU: "εU — tolerancia del valor U / transmitancia [-]",
      epsRH: "εRH — tolerancia de presión de vapor / RH [-]",
      toleranceHintBefore: "La tolerancia se aplica solo a las dos magnitudes que realmente importan para el resultado técnico: εU es la variación relativa del valor U/flujo de calor entre ciclos acoplados (como el campo de temperaturas procede de la misma resolución térmica, también determina con qué precisión se estabilizan las temperaturas); εRH es la variación de la humedad relativa entre ciclos acoplados (RH=p",
      toleranceHintAfter: "(T), por lo que solo se mantiene estable cuando se han asentado tanto el campo de presión de vapor como la temperatura de la que depende). Una malla más gruesa, tolerancias más amplias o menos iteraciones convergen más rápido, pero resuelven con menos precisión el campo alrededor de un elemento estructural: las tarjetas de estado de la malla sobre el botón Ejemplos indican si la configuración actual alcanza realmente la convergencia. Gauss-Seidel y el gradiente conjugado resuelven directamente la malla fina; el multigrid de ciclo V utiliza además una jerarquía de mallas más gruesas, agregadas algebraicamente, para acelerar esa misma resolución: los tres convergen al mismo campo.",
      aboutNotice: "© 2026 Rotho Blaas Srl · Envelope Designer. Beta - versión no oficial, todavía en desarrollo.",
      methodology: "Metodología y limitaciones",
      aboutBuild: "Acerca de esta versión",
      climateSources: "Fuentes climáticas",
      climateSourcesHint: "Elija, magnitud por magnitud, de dónde procede el clima (PVGIS, su archivo EPW, estimaciones), vea qué ofrece cada proveedor y descargue el año combinado como .epw.",
      epwAdvanced: ".epw avanzado…",
      epwWhereToGet: "Dónde obtener archivos climáticos EPW",
      epwHint: "Elija la estación más cercana al emplazamiento, con altitud y exposición similares (EN 15026 §5.2.1); un aeropuerto costero puede diferir mucho de un emplazamiento de interior o de montaña. Use un año típico para el caso de diseño y la opción −2 K del panel para los problemas invernales. El archivo debe contener un año completo de valores horarios (8760 filas); el panel lo comprueba e indica las lagunas. Un archivo cargado se envía con cada cálculo y no se almacena (el registro de solicitudes solo conserva una huella).",
      epwOneBuilding: "años típicos gratuitos (TMYx) para miles de estaciones meteorológicas de todo el mundo, un ZIP por estación con el EPW dentro. Respete las condiciones de uso del repositorio; no redistribuya los archivos.",
      epwMap: "un mapa de las mismas estaciones: haga clic en la más cercana al emplazamiento para descargarla.",
      epwPvgis: "la herramienta \"Typical meteorological year\" exporta como EPW el mismo año típico que la fuente PVGIS descarga aquí para el punto del mapa.",
      epwEnergyPlus: "la página de EnergyPlus para archivos climáticos, que remite al repositorio anterior.",
      epwNsrdb: "años típicos basados en datos satelitales para Norteamérica (registro gratuito); se descargan en CSV y deben convertirse a EPW.",
      epwLicensed: "conjuntos de datos con licencia y control de calidad (Meteonorm: meteonorm.com), cuando un proyecto los requiera.",
      qdHeading: "Análisis cuasidinámico — cálculo",
      qdIntro: "Se aplica a la próxima ejecución del panel Cuasidinámico.",
      timeStep: "Paso de tiempo",
      timeStepAuto: "Automático (1D: 5 min, 2D: 1 h)",
      timeStepHint: "Integración temporal implícita: cualquier paso es estable, pero los pasos de más de 1 h usan el clima promediado sobre el paso, por lo que el ciclo diario (enfriamiento nocturno, sol del mediodía) se suaviza. La EN 15026 trabaja con datos climáticos horarios: mantenga 1 h o menos para los resultados de diseño. Medido en un muro de montantes 2x6 (2D, 20 filas, un año): el pico de condensación fue de 94 g/m² con 1 h, 100 con 6 h y 112 con 24 h.",
      solutionMethod: "Método de resolución (malla 2D)",
      qdMethods: {
        pcg: "Gradiente conjugado precondicionado por líneas (PCG)",
        "pcg-jacobi": "Gradiente conjugado precondicionado Jacobi",
        gs: "Gauss-Seidel",
        jacobi: "Jacobi",
        thomas: "Directo tridiagonal (Thomas)",
      },
      defaultDash: " - predeterminado",
      solutionMethodHint: "Solucionador de los sistemas de calor y vapor de cada paso de tiempo, todos detenidos con el mismo residuo relativo. El PCG precondicionado por líneas resuelve de forma exacta cada columna de la malla en cada iteración (las membranas delgadas y las resistencias superficiales acoplan las celdas a través del espesor mucho más que lateralmente) y es el más rápido; el PCG precondicionado Jacobi es el algoritmo de referencia del informe de validación 05. Gauss-Seidel y Jacobi necesitan muchas más iteraciones (en un muro 2x6 un año tardó más de 12 minutos, más que los 300 s del servidor): úselos para contrastar simulaciones cortas. La sección Convergencia muestra cada hora. El análisis 1D usa siempre el solucionador directo tridiagonal (Thomas).",
      tolerance: "Tolerancia",
      defaultParen: " (predeterminado)",
      maxIterations: "Iteraciones máx.",
      toleranceQdHint: "Una resolución ha convergido cuando ‖b − Ax‖ / ‖b‖ es inferior a la tolerancia. Con 1e−10, las mallas 2D con filas delgadas de membrana dejaban residuos del balance de masa de aproximadamente 1e−5; 1e−12 los mantiene por debajo de 1e−6. Iteraciones máx.: por resolución.",
      gridRows: "Filas de la malla 2D",
      gridRowsOption: (rows: number) => `${rows} filas`,
      gridRowsHint: "Solo en modo malla 2D. Las mallas más finas resuelven mejor las interfaces pero tardan más; el servidor detiene un cálculo a los 300 s. Medido en un muro de montantes 2x6, un año simulado con el PCG precondicionado por líneas: 20 filas 12 s, 40 filas 18 s, 70 filas unos 4 min (un solo año). Las mallas más finas no terminarían a tiempo.",
      restoreDefaults: "Restablecer valores predeterminados",
      lockedNote: "No disponible en esta beta",
    notAppliedTitle: "Configuración no aplicada",
    notAppliedMessage: "Ha modificado ajustes que aún no se han aplicado: ninguna pestaña se actualiza hasta que pulse Listo. ¿Cómo desea continuar?",
    keepEditing: "Seguir editando",
    discardChanges: "Descartar los cambios",
    applyDone: "Aplicar (Listo)",
    meshAuto: "Auto",
    meshRowsReadout: (rows: number) => `${rows} filas`,
    },
    confirm: {
      cancel: "Cancelar",
    },
    data: {
      stepLabel: (n: number, title: string) => `Paso ${n}: ${title}`,
      stepSite: "Emplazamiento",
      stepElement: "Elemento",
      stepAnalysis: "Análisis",
      stepCalculation: "Cálculo",
      useThisSite: "Usar este emplazamiento",
      elements: {
        wall: "Muro",
        roof: "Cubierta",
        ground: "Contacto con el terreno",
      },
      analyses: {
        static: "Análisis estático",
        dynamic: "Análisis cuasi-dinámico",
      },
      wallDesc: "Muro exterior",
      roofDesc: "Cubierta o último forjado",
      groundDesc: "Solera sobre el terreno",
      groundDynamicNote: "El análisis cuasi-dinámico no modela el contacto con el terreno: la superficie exterior está expuesta al aire exterior.",
      staticOption: "Estático",
      staticDesc: "Condición de diseño y Glaser mensual",
      quasiDynamic: "Cuasi-dinámico",
      quasiDynamicDesc: "Hora a hora durante varios años",
      simplified: "Simplificado",
      simplifiedDesc: "1D a través de las capas",
      meshMode: "Modo malla",
      meshModeDesc: "Malla 2D, montantes resueltos",
      meshNoteLabel: "Modo malla 2D: métodos numéricos (nota)",
      numericalMethods: "Métodos numéricos.",
      meshNote: "El modo malla 2D utiliza métodos numéricos: la verificación de su validez y de la idoneidad de cada material y producto seleccionado en la estratigrafía, para el caso concreto, es responsabilidad exclusiva del proyectista responsable.",
      missing: {
        site: "emplazamiento",
        element: "elemento",
        analysis: "análisis",
      },
      stillToChoose: (list: string) => `Falta elegir: ${list}.`,
      continueToStratigraphy: "Continuar a la estratigrafía →",
      continueToAnalysis: {
        static: "Continuar al análisis estático →",
        dynamic: "Continuar al análisis cuasi-dinámico →",
      },
      backToData: "← Datos",
      howToStart: "¿Cómo desea empezar?",
      fromExample: "A partir de un ejemplo",
      fromExampleDesc: (n: number, element: string) => `Elija una de las ${n} soluciones constructivas (${element}) y adáptela`,
      fromScratch: "Desde cero",
      fromScratchDesc: "Una estratigrafía vacía: añada las capas desde la biblioteca de materiales",
      back: "← Atrás",
    },
    examples: {
      close: "Cerrar",
      examplesTitle: (element: string) => `Ejemplos: ${element}`,
      elementName: {
        wall: "muro",
        roof: "cubierta",
        ground: "contacto con el terreno",
      },
      exampleLoaded: (name: string) => `Ejemplo cargado: ${name}`,
      changeExample: "Cambiar ejemplo",
      changeExampleTitle: (element: string) => `Sustituye las capas por otro ejemplo (${element}); Deshacer las recupera`,
      structures: {
        timber: "Entramado de madera",
        mass_timber: "Madera maciza",
        masonry: "Fábrica",
        concrete: "Hormigón",
        steel: "Acero",
        other: "Otro",
      },
      types: {
        wall: "Muro",
        roof: "Cubierta / forjado",
        ground: "Solera",
      },
      searchPlaceholder: "Buscar ejemplos...",
      exampleSingular: "ejemplo",
      examplePlural: "ejemplos",
      filtersLabel: "Filtros de ejemplos",
      region: "Región",
      yourLocation: (country: string) => `su ubicación: ${country}`,
      type: "Tipo",
      structure: "Estructura",
      resetFilters: "Restablecer filtros",
      noMatch: "Ninguna solución constructiva coincide con la búsqueda o los filtros seleccionados.",
      noneInRegion: "Ninguna solución constructiva de este tipo en la región seleccionada.",
      backToTypes: "Volver a los tipos",
    },
    climate: {
      loadError: "No se han podido cargar los datos climáticos.",
      suggestedCorrections: "Correcciones sugeridas",
      applyAndReload: "Aplicar y recargar",
      correctionsHint: "No se corrige nada a menos que usted lo elija; las correcciones aplicadas se indican junto con los resultados.",
      correctionsChosen: (list: string) => `Correcciones elegidas: ${list} (nada que corregir para esta fuente).`,
      undo: "Deshacer",
      cardTitle: "Clima del emplazamiento",
      autoEpwTitle: "El clima del punto del mapa, generado automáticamente a partir de un proveedor (PVGIS, ERA5 vía Open-Meteo, estación TMYx).",
      autoEpw: "EPW automático",
      importEpwTitle: "Cargue un archivo EPW (TMYx, NSRDB, Meteonorm o cualquier otra fuente).",
      importEpw: "Importar EPW",
      yearType: "Tipo de año",
      yearTypical: "Año tipo, repetido",
      yearMinus2: "Año tipo −2 K (problemas de invierno, EN 15026 §5.2.1 c)",
      yearPlus2: "Año tipo +2 K (problemas de verano)",
      provider: "Proveedor de datos climáticos",
      providerPvgis: "Año tipo PVGIS (JRC UE) - sin lluvia",
      providerOpenMeteo: "ERA5 vía Open-Meteo - lluvia, temperaturas del suelo",
      providerTmyx: "Estación meteorológica TMYx (climate.onebuilding.org)",
      era5Year: "Año ERA5",
      era5YearTitle: "El primer año tipo de un emplazamiento descarga 10 años de datos horarios (unos 10-20 s); después se conserva para esa celda de la malla.",
      era5Typical: "Año tipo (EN ISO 15927-4, últimos 10 años)",
      actualYear: (year: number) => `Año real ${year}`,
      tmyxStation: "Estación TMYx",
      tmyxStationTitle: "Distancia y diferencia de altitud respecto al emplazamiento. La mejor coincidencia cuenta 100 m de altura como 10 km; la temperatura se corrige según la diferencia de altitud.",
      bestMatch: (station: string) => `Mejor coincidencia: ${station}`,
      lat: "Lat",
      latitude: "Latitud [°]",
      lon: "Lon",
      longitude: "Longitud [°]",
      elevTitle: "Altitud del emplazamiento [m]. Vacío: la altitud de los datos climáticos (en gris). Introduzca un valor para corregir la temperatura al emplazamiento (0,65 K por cada 100 m).",
      elev: "Alt.",
      elevAuto: "auto",
      siteElevation: "Altitud del emplazamiento [m]",
      viewClimateData: "Ver datos climáticos",
      viewClimateDataLoading: "Ver datos climáticos (cargando)",
      climateDataLoading: "Cargando datos climáticos…",
      loadingClimateData: "Cargando datos climáticos",
      climateData: "Datos climáticos",
      backToMap: "Volver al mapa",
      map: "Mapa",
      epwFile: "Archivo EPW",
      fileUnreadable: (name: string) => `No se ha podido leer el archivo ${name}.`,
      weatherStationHint: "Una estación meteorológica cercana al emplazamiento es la fuente más fiable (EN 15026 §5.2.1).",
      elevationFromEpw: (elevation: number) => `${elevation} (del EPW)`,
      fromEpw: "del EPW",
      epwElevationTitle: "Vacío: la altitud del EPW. Introduzca un valor para corregir la temperatura al emplazamiento (0,65 K por cada 100 m).",
      notEpwFile: (name: string) => `${name} no es un archivo EPW: elija un archivo con extensión .epw.`,
      readingClimate: "leyendo el clima…",
      loaded: "cargado",
      replace: "Sustituir…",
      remove: "Quitar",
      dropEpwHere: "Arrastre aquí un archivo EPW",
      epwSources: "TMYx, NSRDB, Meteonorm o cualquier otra fuente",
      chooseEpw: "Elegir archivo EPW…",
      noWindData: "No hay datos de viento en este archivo climático.",
      loadingTheClimateData: "Cargando los datos climáticos…",
      insideBasis: "UNI EN ISO 13788 clase 3",
      ashraeNotAvailable: ", ASHRAE 160 aún no disponible",
      heatingDesignBasis: "temperatura de diseño de calefacción 99,6 %",
      coldestHour: (day: number, month: string, time: string) => `hora más fría, ${day} de ${month} ${time}`,
      aboveSaturation: (rh: string) => ` Esa presión de vapor supera la saturación a Te (HR ${rh} %): se propone 100 %.`,
      climateOfSite: "el clima del emplazamiento",
      suggestionTitle: (method: string, source: string, notes: string) => `${method}. Clima: ${source}.${notes} Los campos siguen siendo editables.`,
      fromTheClimate: (basis: string) => `Del clima (${basis}):`,
      inUse: "en uso",
      use: "Usar",
      searchFailed: "La búsqueda ha fallado.",
      noPlaceFound: "No se ha encontrado ningún lugar.",
      searchNotSent: "No se ha podido enviar la búsqueda.",
      mapLabel: "Mapa: haga clic para elegir el emplazamiento",
      searchPlaceholder: "Busque una ciudad, una calle o un lugar",
      searchLabel: "Buscar un lugar",
      searching: "Buscando",
      usZonesTitle: "Zonas climáticas IECC de los Estados Unidos (PNNL Building America Solution Center)",
      usZones: "Zonas climáticas de EE. UU.",
      addToFavourites: "☆ Añadir a favoritos",
      favouritesFull: "Favoritos completos (10): elimine primero uno.",
      favouriteName: "Nombre del favorito",
      cancel: "Cancelar",
      add: "Añadir",
      renameFavourite: (name: string) => `Nuevo nombre de ${name}`,
      favouriteHint: "arrastre para reordenar, doble clic para renombrar",
      removeFavourite: (name: string) => `Quitar ${name} de favoritos`,
      morePlaces: (n: number) => `${n} lugares más`,
      more: (n: number) => `${n} más`,
    monthsShort: ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"],
    chartLabel: "Año de referencia: media diaria e intervalo diario de cada magnitud",
    quantitiesLabel: "Magnitudes mostradas (haga clic para ocultar o mostrar)",
    allHidden: "Todas las magnitudes están ocultas.",
    legendTitle: (name: string, hidden: boolean) => `${name} - haga clic para ${hidden ? "mostrar" : "ocultar"}`,
    series: {
      outsideTemperature: "Temperatura del aire exterior",
      insideTemperature: "Temperatura interior",
      outsideRh: "Humedad relativa exterior",
      insideRh: "Humedad relativa interior",
      solar: "Radiación solar global sobre el plano horizontal",
      wind: "Velocidad del viento a 10 m",
      rain: "Lluvia sobre el plano horizontal",
    },
    windRoseLabel: "Rosa de los vientos: porcentaje de horas por dirección y velocidad",
    windClassesLabel: "Clases de velocidad del viento (haga clic para ocultar o mostrar)",
    windSpeed: "velocidad del viento",
    windFrom: (direction: string, percent: string) => `Desde ${direction}: ${percent} % de las horas`,
    windMean: (value: string, unit: string) => `media ${value} ${unit}`,
    windMax: (value: string, unit: string) => `máx. ${value} ${unit}`,
    },
    stratigraphy: {
      flipInsideOutsideTitle: "Invertir interior/exterior - intercambia qué extremo es Interior/Exterior (y Ti/RHi/Rsi con Te/RHe/Rse) sin reordenar las capas",
      resetZoomTitle: "Restablecer el zoom (desplace la rueda sobre el dibujo para acercar/alejar, arrastre para moverse con el zoom activo)",
      clearLayersTitle: "Eliminar todas las capas (pide confirmación; Deshacer las recupera)",
      condensationToggleTitle: "Mostrar/ocultar la condensación intersticial en el dibujo - capas húmedas en azul, con marcadores de gota en los puntos de condensación",
      fluxVectorsTitle: "Mostrar los vectores de flujo de calor sobre la sección (solo modo higrotérmico 2D) - la longitud de la flecha sigue la magnitud local del flujo, la dirección sigue el gradiente de temperatura (perpendicular a las isotermas)",
      overlayTemperatureTitle: "Superponer el perfil de temperatura al dibujo (arrastre el tirador para fijar qué parte del ancho cubre)",
      overlayHumidityTitle: "Superponer el perfil de humedad (RH) al dibujo (arrastre el tirador para fijar qué parte del ancho cubre)",
      meshGridTitle: "Mostrar las celdas individuales de la malla de cálculo higrotérmico 2D, con o sin superposición de temperatura/humedad (solo modo malla 2D, extensión fijada con el mismo tirador)",
      thickness: "Espesor",
      editMaterial: "Editar material…",
      changeMaterial: "Cambiar material…",
      removeBridge: "Eliminar puente térmico",
      resizeHandleTitle: "Arrastre para redimensionar este recuadro - doble clic para restablecer",
      layerTable: "Tabla de capas",
      layerTableHide: "ocultar",
      layerTableShow: "mostrar las propiedades calculadas de cada capa",
      layerTableHint: "Propiedades térmicas calculadas para la estratigrafía actual, del interior al exterior. Arrastre el tirador de una fila para reordenar, haga clic en el nombre de un material (o en su icono de lápiz) para verlo/editarlo - o utilice la sección gráfica de arriba para más acciones (clic derecho para insertar/duplicar/eliminar/puentes térmicos).",
      layerTable2DNote: "Modo higrotérmico 2D: los valores de abajo se muestrean del campo 2D convergido a lo largo de la columna central del vano (sin elemento estructural), no son un valor 1D real por capa - consulte el panel Higrotérmico 2D de la página Resultados para ver el campo completo.",
      colMaterial: "Material",
      colTempMin: "Temp. mín",
      colTempMinTitle: "Temperatura en la cara más fría de esta capa, en la condición de diseño",
      colTempMax: "Temp. máx",
      colTempMaxTitle: "Temperatura en la cara más cálida de esta capa, en la condición de diseño",
      colSdTitle: "Espesor de aire equivalente a la difusión de vapor (μ × espesor)",
      colCondensate: "Condensado",
      colCondensateTitle: "Tasa de condensación intersticial, si la hay, en la cara interior de esta capa",
      colWeight: "Peso",
      colWeightTitle: "Masa superficial por unidad de área",
      colHeatCapacity: "Capacidad térmica",
      colHeatCapacityTitle: "Capacidad calorífica específica",
      makeSideInside: "Haga clic para convertir este lado en interior",
      makeSideOutside: "Haga clic para convertir este lado en exterior",
      sideSwitched: (top: boolean, outside: boolean) => `Lado ${top ? "superior" : "inferior"}: ${outside ? "EXTERIOR" : "INTERIOR"}`,
      differsFromLibrary: "Difiere del material de la biblioteca",
      variable: "variable",
      thicknessField: "espesor",
      checkPositive: (fields: string) => `Revise ${fields}: se requiere un número positivo.`,
      stud: "Montante",
      material: "Material",
      thermalHygrometric: "Térmico e higrométrico",
      acoustic: "Acústico",
      permeance: "Permeancia",
      membraneLocked: "Membrana Rothoblaas: valores de la ficha técnica, no editables.",
      layerValuesHint: "Valores solo de esta capa, aplicados con Intro o al salir de un campo.",
      studValuesHint: "Valores solo de este montante, aplicados con Intro o al salir de un campo.",
      checkedOn: (date: string) => `Comprobada el ${date}`,
      dataSheet: "Ficha técnica",
      resetToLibrary: "Restablecer los valores de la biblioteca",
    outside: "EXTERIOR",
    inside: "INTERIOR",
    noLayersDrawing: "Aún no hay capas - añada una capa o cargue un ejemplo.",
    noLayersTable: "No hay capas - arrastre un material de la biblioteca a la sección gráfica de arriba.",
    moveUp: "Subir (hacia el exterior)",
    moveDown: "Bajar (hacia el interior)",
    excludedClickToInclude: "Excluida del cálculo - haga clic para incluirla",
    includedClickToExclude: "Incluida en el cálculo - haga clic para excluirla",
    exteriorResistance: "Resistencia superficial exterior (Rse)",
    interiorResistance: "Resistencia superficial interior (Rsi)",
    dragToReorder: "Arrastre para reordenar",
    viewEditMaterial: "Ver/editar material",
    customMaterialRow: "Esta capa usa un material personalizado/editado, no una referencia estándar de la biblioteca",
    supersaturated: "HR>100%",
    editStud: "Editar montante/cabio",
    removeStud: "Eliminar montante/cabio",
    studSummary: (name: string, width: string, spacing: string, depth: string, unit: string) => `${name} · ${width}×${spacing}${unit}, profundidad ${depth}${unit}`,
    wholeComponent: "Componente completo",
    wholeComponentMidBay: "Componente completo (columna a mitad de vano)",
    sdName: {
      sdValue: "valor sd",
      permeance: "permeancia",
      vapourResistance: "resistencia al vapor",
    },
    insertInterior: "↑ Insertar capa (lado interior)",
    insertExterior: "↓ Insertar capa (lado exterior)",
    placeStuds: "Colocar montantes o cabios…",
    removeStuds: "Eliminar montantes o cabios",
    duplicateLayer: "Duplicar capa",
    disableLayer: "Desactivar capa (excluir del cálculo)",
    enableLayer: "Activar capa",
    deleteLayer: "Eliminar capa",
    changeMaterialTitle: "Cambiar material",
    clearAllTitle: "¿Eliminar todas las capas?",
    clearAllMessage: (n: number) => `${n === 1 ? "La única capa" : `Las ${n} capas`} de esta estratigrafía ${n === 1 ? "se eliminará" : "se eliminarán"}. Puede recuperarlas con Deshacer (Ctrl+Z).`,
    clearAllConfirm: "Eliminar todas",
    ashrae160Note: "ASHRAE 160 aún no disponible (sus valores se están verificando): se usa el modelo UNI / EN",
    },
    materialLibrary: {
      title: "Biblioteca de materiales",
      materials: "Materiales",
      collapseExpand: "Contraer/expandir la biblioteca de materiales",
      openLibrary: "Abrir la biblioteca de materiales",
      searchPlaceholder: "Filtrar materiales...",
      materialSingular: "material",
      materialPlural: "materiales",
      noMatches: "Ningún material coincide.",
      backTo: (name: string) => `Volver a ${name}`,
      backToCategories: "Volver a las categorías",
      dragToInsert: "Arrastre a la tabla de capas para insertar",
      dragHint: "Arrastre un material sobre la sección gráfica para insertarlo como nueva capa (Esc cancela).",
      chooseCategoryHint: "Elija una categoría y luego arrastre un material sobre la sección gráfica para insertarlo como nueva capa.",
      generic: "Genérico",
      filtersLabel: "Filtros de materiales",
      min: "mín",
      max: "máx",
      minLambda: "Conductividad térmica mínima",
      maxLambda: "Conductividad térmica máxima",
      membranes: "Membranas",
      vapourDiffusionAs: "Difusión de vapor como",
      minimumOf: (name: string) => `${name}: mínimo`,
      maximumOf: (name: string) => `${name}: máximo`,
      permeance: "Permeancia",
      vapourResistance: "Resistencia al vapor",
      manufacturer: "Fabricante",
      resetFilters: "Restablecer filtros",
      thickness: "Espesor",
      variable: "variable",
      subcategories: {
        custom: "Personalizados",
      },
    },
    shell: {
      calculating: "Calculando…",
      cancelCalculation: "Cancelar el cálculo",
    },
    shortcuts: {
      undo: "Deshacer",
      redo: "Rehacer",
      flip: "Invertir interior/exterior",
      toggleMode: "Cambiar el modo de cálculo",
      changeMaterial: "Cambiar el material de la capa seleccionada",
      toggleLibrary: "Abrir/cerrar la biblioteca de materiales",
      togglePanels: "Contraer/expandir la barra lateral y la barra de resultados",
      pressKeys: "Pulse las teclas…",
      none: "(ninguno)",
    },
    history: {
      saved: (time: string) => `Guardado a las ${time}`,
      undoTitle: (label: string) => `Deshacer: ${label}`,
      undoLabel: (label: string) => `Deshacer ${label}`,
      nothingToUndo: "Nada que deshacer",
      redoTitle: (label: string) => `Rehacer: ${label}`,
      redoLabel: (label: string) => `Rehacer ${label}`,
      nothingToRedo: "Nada que rehacer",
      menuCount: (shown: number, available: number) => `${shown} de ${available}`,
      steps: (steps: number) => `${steps} ${steps === 1 ? "paso" : "pasos"}`,
      actionTitle: (detail: string, steps: number) =>
        `${detail} Haga clic para deshacer ${steps === 1 ? "1 acción" : `${steps} acciones`}.`,
      initialState: "Estado inicial",
      initialStateDetail: "Estado cargado al iniciar esta sesión.",
      sessionStart: "Inicio de la sesión",
      fieldLabels: {
        thickness: "espesor",
        materialId: "material",
        enabled: "estado de cálculo",
        widthMM: "anchura",
        spacingMM: "separación",
        depthMM: "profundidad",
        mode: "modo de cálculo",
        stratFlipped: "orientación interior/exterior",
        Rsi: "resistencia superficial interior",
        Rse: "resistencia superficial exterior",
        patm: "presión atmosférica",
        meshRows: "filas de la malla simplificada",
        meshRowsHygro2D: "filas de la malla 2D",
        maxIterHygro2D: "límite de iteraciones del acoplamiento",
        epsU2D: "tolerancia de la transmitancia",
        epsRH2D: "tolerancia de presión de vapor / HR",
        solverMethod2D: "solucionador 2D",
        autoMesh: "malla automática",
        meshTolerance: "precisión de la malla",
        Ti: "temperatura interior",
        Te: "temperatura exterior",
        RHi: "humedad relativa interior",
        RHe: "humedad relativa exterior",
        name: "nombre",
        notes: "notas",
      } as Record<string, string>,
      unknownMaterial: "Material desconocido",
      layerName: (position: number, material: string) => `capa ${position} (${material})`,
      enabled: "activada",
      disabled: "desactivada",
      notSet: "sin definir",
      scopeChanged: (scope: string) => `${scope}: modificado.`,
      scopeDiff: (scope: string, changes: string) => `${scope}: ${changes}.`,
      layerAdded: "Capa añadida",
      layerAddedDetail: (layer: string, position: number, thickness: string) =>
        `Añadida ${layer} en la posición ${position}, espesor ${thickness} mm.`,
      layerDeleted: "Capa eliminada",
      layerDeletedDetail: (layer: string, position: number, thickness: string) =>
        `Eliminada ${layer} de la posición ${position}, espesor ${thickness} mm.`,
      stratigraphyReplaced: "Estratigrafía sustituida",
      stratigraphyReplacedDetail: (from: number, to: number, added: number, removed: number) =>
        `El número de capas pasó de ${from} a ${to}; ${added} añadidas y ${removed} eliminadas.`,
      layersReordered: "Capas reordenadas",
      layersReorderedDetail: (layer: string, from: number, to: number) => `Movida ${layer} de la posición ${from} a la ${to}.`,
      bridgeAdded: "Puente térmico añadido",
      bridgeAddedDetail: (material: string, layer: string, width: string, spacing: string, depth: string) =>
        `Añadido elemento de ${material} a ${layer}; anchura ${width} mm, separación ${spacing} mm, profundidad ${depth} mm.`,
      bridgeDeleted: "Puente térmico eliminado",
      bridgeDeletedDetail: (material: string, layer: string) => `Eliminado elemento de ${material} de ${layer}.`,
      bridgeMoved: "Puente térmico movido",
      bridgeMovedDetail: (material: string, from: string, to: string) => `Movido elemento de ${material} de ${from} a ${to}.`,
      bridgeModified: "Puente térmico modificado",
      bridgeScope: (material: string, layer: string) => `Elemento de ${material} en ${layer}`,
      layerMaterialChanged: "Material de la capa cambiado",
      layerMaterialChangedDetail: (position: number, from: string, to: string) =>
        `Material de la capa ${position} cambiado de ${from} a ${to}.`,
      layerThicknessChanged: "Espesor de la capa cambiado",
      layerThicknessChangedDetail: (layer: string, from: string, to: string) =>
        `Espesor de ${layer} cambiado de ${from} mm a ${to} mm.`,
      layerDisabled: "Capa desactivada",
      layerDisabledDetail: (layer: string) => `Excluida ${layer} de los cálculos.`,
      layerEnabled: "Capa activada",
      layerEnabledDetail: (layer: string) => `Incluida ${layer} en los cálculos.`,
      layerModified: "Capa modificada",
      flipped: "Interior/exterior invertidos",
      flippedDetail: "Se invirtió la orientación interior/exterior mostrada de la estratigrafía.",
      modeChanged: "Modo de cálculo cambiado",
      modeChangedDetail: (from: string, to: string) => `Modo de cálculo: ${from} → ${to}.`,
      projectDetailsModified: "Datos del proyecto modificados",
      projectDetailsScope: "Datos del proyecto",
      customMaterialAdded: "Material personalizado añadido",
      customMaterialAddedDetail: (name: string) => `Añadido el material personalizado «${name}» a la biblioteca de materiales.`,
      customMaterialDeleted: "Material personalizado eliminado",
      customMaterialDeletedDetail: (name: string) => `Eliminado el material personalizado «${name}» de la biblioteca de materiales.`,
      customMaterialModified: "Material personalizado modificado",
      customMaterialModifiedDetail: (name: string) => `Modificado el material personalizado «${name}».`,
      materialLibraryModified: "Biblioteca de materiales modificada",
      materialLibraryModifiedDetail: "Se modificaron varios materiales personalizados.",
      climateModified: "Clima de proyecto modificado",
      climateScope: "Clima de proyecto",
      settingsModified: "Configuración de cálculo modificada",
      settingsScope: "Configuración de cálculo",
      projectModified: "Proyecto modificado",
      projectModifiedDetail: "Se modificaron varios valores del proyecto en esta acción guardada.",
    },
    materials: {
      panelDesc: "Materiales predefinidos y personalizados. Los materiales personalizados se guardan en el almacenamiento local de este navegador.",
      newMaterial: "+ Nuevo material",
      searchPlaceholder: "Buscar por nombre...",
      allCategories: "Todas las categorías",
      allManufacturers: "Todos los fabricantes",
      sortName: "Ordenar: Nombre",
      sortDensity: "Ordenar: Densidad ρ",
      sortLambda: "Ordenar: λ",
      sortMu: "Ordenar: μ",
      sortWcap: "Ordenar: wcap",
      sortDirectionTitle: "Alternar ascendente/descendente",
      sortAsc: "▲ Asc.",
      sortDesc: "▼ Desc.",
      colSymbol: "Símbolo",
      colName: "Nombre",
      colManufacturer: "Fabricante",
      customBadge: "personalizado",
      edit: "Editar",
      delete: "Eliminar",
      deleteConfirm: "¿Eliminar este material personalizado?",
      variableSdTitle: (min: number, max: number) => `Sd variable: ${min}–${max} m (ficha técnica)`,
      navHint: "Elija una categoría o busque por nombre arriba.",
      sourceAssumed: "No figura en la ficha técnica: valor supuesto",
      sourceDerived: "No figura en la ficha técnica: derivado de sus valores",
      dataNotes: {
        lambdaBelowAir: (lambda: number) => `la λ = ${lambda} W/(m·K) de la ficha técnica es inferior a la del aire en reposo: no se usa`,
        densityDerived: "densidad derivada de los valores de la ficha técnica",
      },
    },
    materialModal: {
      titleMembrane: "Membrana Rothoblaas",
      titleNew: "Nuevo material",
      titleEditCopy: "Editar material (guardado como copia personalizada)",
      titleEdit: "Editar material",
      unnamed: "Material sin nombre",
      membraneLockedHint: "Las membranas Rothoblaas se muestran con los valores de su ficha técnica y no se pueden editar ni copiar.",
      builtinHint: "Los materiales predefinidos no se pueden modificar: al guardar se crea una copia personalizada y las capas de esta estratigrafía que usan este material pasan a la copia.",
      technicalDataSheet: "Ficha técnica ↗",
      productPage: "Página del producto ↗",
      checked: (date: string) => `verificado el ${date}`,
      laboratoryNotStated: "laboratorio no indicado",
      propertiesLabel: "Propiedades del material",
      tabIdentity: "Identidad",
      tabGraphics: "Gráfica",
      tabThermal: "Térmica",
      tabMoisture: "Humedad",
      tabAcoustic: "Acústica",
      name: "Nombre",
      manufacturer: "Fabricante",
      optional: "opcional",
      category: "Categoría",
      categoryPlaceholder: "p. ej. insulation",
      thickness: "Espesor",
      thicknessVariable: "Espesor variable",
      thicknessKnown: "Espesores conocidos (del fabricante)",
      reference: "Referencia",
      referencePlaceholder: "p. ej. 100",
      min: "Mín.",
      max: "Máx.",
      knownThicknesses: "Espesores conocidos",
      knownThicknessesHint: ", separados por comas - el primero es el de referencia",
      knownThicknessesPlaceholder: "p. ej. 40, 60, 80, 100, 120",
      source: "Fuente",
      laboratory: "Laboratorio",
      date: "Fecha",
      file: "Archivo",
      sectionSymbolCaption: "Símbolo de sección (trama):",
      automaticFromCategory: " - automático, según la categoría",
      sectionSymbol: "Símbolo de sección",
      auto: "Auto",
      hatches: {
        "": "Auto (según la categoría)",
        concrete: "Hormigón / solado (punteado)",
        masonry: "Fábrica maciza (hiladas de ladrillo)",
        "masonry-hollow": "Fábrica hueca (hiladas de ladrillo + huecos)",
        render: "Revoco / enlucido / mortero (puntos finos)",
        board: "Placa rígida, tipo yeso (lisa)",
        wool: "Aislante fibroso (bucles)",
        foam: "Aislante de espuma rígida (círculos)",
        fiberboard: "Tablero de fibra de madera / granular (moteado)",
        wood: "Madera (vetas)",
        membrane: "Membrana / lámina (línea gruesa)",
        stone: "Piedra natural (grietas irregulares)",
        metal: "Metal (diagonal fina)",
        generic: "Genérico (rayado diagonal)",
      },
      thermalHint: "Una sola conductividad térmica por material (sin λ(w) en la versión web).",
      sdValue: "Valor Sd",
      vapourResistanceZ: "Resistencia al vapor Z",
      variableSd: "Sd variable",
      variableSdRange: (min: number, max: number) => `: ${min}–${max} m en la ficha técnica`,
      variableSdCurve: " (curva Sd(HR) medida: véase Propiedades higrotérmicas)",
      admissibleCondensate: "Condensación admisible",
      admissibleCondensateTitle: "Condensación admisible del análisis de Glaser mensual (tabla heredada frmInfoVAP)",
      condDefault: (label: string) => `Predeterminado: ${label}`,
      vapourNote: (thickness: string, unit: string, mu: string) => `Para ${thickness} ${unit}: μ = ${mu}`,
      vapourNoteNeedsThickness: "Requiere un espesor (de referencia o espesores conocidos).",
      porosity: "Porosidad",
      saturation: "Saturación",
      capillary: "Capilar",
      waterAbsorption: "Absorción de agua",
      airflow: "Flujo de aire",
      acousticHint: "Opcional: lo usan las herramientas acústicas, no los cálculos higrotérmicos.",
      errorMembrane: "Las membranas son solo productos Rothoblaas: un material personalizado no puede pertenecer a la categoría Membrana.",
      errorSdThickness: (label: string) => `Para introducir la difusión del vapor como ${label}, asigne primero un espesor al material (espesor de referencia o espesores conocidos).`,
      errorSdPositive: (label: string) => `${label} debe ser un número mayor que 0.`,
      save: "Guardar material",
      cancel: "Cancelar",
      close: "Cerrar",
      density: "Densidad ρ",
      lambdaDry: "Conductividad térmica en seco λ",
      cp: "Calor específico c",
      mu: "Factor de resistencia al vapor μ",
      wCap: "Contenido de agua capilar wcap",
      emissivity: "Emisividad ε",
    },
    materialHygro: {
      title: "Propiedades higrotérmicas",
      showTitle: "Mostrar las propiedades higrotérmicas",
      hideTitle: "Ocultar las propiedades higrotérmicas",
      tabSorption: "Sorción",
      tabSuction: "Succión",
      thickness: "Espesor",
      sdNamePermeance: "permeancia",
      sdNameVapourResistance: "resistencia al vapor",
      dryCup: "Dry cup [US perm]",
      muDry: "μ seco [-]",
      w80ClassAssumed: (cls: string) => `Sin curva de sorción medida: valor de la clase “${cls}” del modelo cuasidinámico`,
      noSorptionCurve: "Sin curva de sorción medida",
      porosity: "Porosidad [%]",
      sdChartTitle: (thickness: string, unit: string) => `Sd en función de la humedad relativa (medido, ${thickness} ${unit})`,
      sorptionMeasured: "Isoterma de sorción (medida)",
      sorptionTable: "Isoterma de sorción (tabla del material)",
      sorptionLinear: "Contenido de agua: recta del modelo cuasidinámico",
      suctionTitle: "Curva de succión (almacenamiento de humedad)",
      klTitle: "Conductividad líquida k(θ)",
      muwTitle: "Factor de resistencia al vapor μ(w)",
      membraneVariableNote: "Sd variable en la ficha técnica, pero sin curva Sd(HR) medida: los cálculos usan el estado más cerrado (Sd fijo).",
      membraneFixedNote: "Membrana: lámina no higroscópica (w = 0), Sd independiente de la humedad.",
      footSdCurve: "Sd(φ): medido según EN ISO 12572, leído de forma log-lineal entre los puntos (como en los análisis 1D, mensual y cuasidinámico); el análisis 2D usa el estado más cerrado.",
      footTable: "w(φ) y μ(w) tal como los usan los análisis 2D y cuasidinámico.",
      footTableMeasured: "w(φ) y μ(w) tal como los usan los análisis 2D y cuasidinámico (sorción a partir de la función de almacenamiento, Kelvin a 20 °C).",
      footNoCurve: (cls: string) => `Sin curva de sorción medida: el análisis 2D no usa contenido de agua; el análisis cuasidinámico almacena w = ξ·φ (clase “${cls}”).`,
      footLambda: "λ es un único valor (sin λ(w)).",
      assumedNoData: "Supuesto: sin datos medidos para este material",
      assumedTag: "supuesto",
      sorptionClasses: {
        membrane: "Membrana",
        woodBased: "Madera, derivados de la madera y fibras naturales",
        gypsum: "Placas y enlucidos de yeso",
        nonHygroInsulation: "Aislamiento no higroscópico",
        aac: "Hormigón celular curado en autoclave",
        lightweightConcrete: "Hormigón ligero",
        concrete: "Hormigón",
        concreteBlock: "Bloques de hormigón",
        calciumSilicate: "Silicato cálcico",
        firedClayBrick: "Ladrillo cerámico",
        naturalStone: "Piedra natural",
        metal: "Metal",
        mineralRender: "Revocos, morteros y placas minerales",
        unclassified: "Sin clasificar",
      },
    },
    calculation: {
      panelDesc: "Ajustes de los análisis estáticos: resistencias superficiales y presión atmosférica. Las resistencias superficiales también se ajustan en el dibujo de la estratigrafía.",
      advancedSettings: "Ajustes avanzados",
      showHide: "mostrar / ocultar",
      atmosphericPressure: "Presión atmosférica [Pa]",
      modelsHint: "Modelo de presión de saturación: aproximación de Magnus-Tetens (fijo). Modelo de difusión: método de Glaser estacionario / difusión de vapor 2D en la condición de diseño (fijo). Modelo de condensación: discontinuidad del flujo de vapor en el plano de condensación (1D) / indicación local HR>100% (2D) (fijo). Se indican aquí por transparencia; todavía no los puede cambiar el usuario. Los ajustes de malla, iteraciones y tolerancias del solucionador higrotérmico 2D se han trasladado a Ajustes (el icono del engranaje, arriba a la derecha) → pestaña Solucionador 2D.",
      diagnosticsTitle: "Diagnóstico del solucionador (malla 2D)",
      diagnosticsIntro: "El detalle completo de cómo se obtuvo realmente el último resultado higrotérmico 2D - método, tamaño de malla, convergencia, historial de residuos - por separado para la resolución del campo térmico y del de presión de vapor (pueden diferir mucho: el campo de vapor suele estar mucho peor condicionado). La convergencia del solucionador (esta tarjeta) y la precisión técnica (la tabla de convergencia de malla de abajo, con malla automática) son dos cuestiones distintas - una solución totalmente convergida en una malla gruesa puede seguir siendo imprecisa.",
      thermal: "Térmico",
      vapourPressure: "Presión de vapor",
      vapour: "Vapor",
      addLayerForDiagnostics: "Añada al menos una capa para ver aquí el diagnóstico del solucionador.",
      noDiagnosticsYet: "Todavía no hay diagnóstico.",
      methodUsed: "Método usado",
      autoSuffix: " (auto)",
      solverAuto: "automático",
      meshCells: "Celdas de la malla",
      iterationsCycles: "Iteraciones / ciclos",
      residualInitial: "Residuo (inicial)",
      residualFinal: "Residuo (final)",
      reductionFactor: "Factor de reducción",
      solverFlux: "Flujo del solucionador q",
      energyBalanceError: "Error del balance energético",
      spdSystem: "Sistema SPD",
      conductivityContrast: "Contraste de conductividad",
      finalStatus: "Estado final",
      yes: "Sí",
      no: "No",
      residualChartTitle: "Residuo por iteración (escala logarítmica)",
      compareSolvers: "Comparar CG y V-cycle en este problema",
      runCalculationFirst: "Ejecute primero un cálculo.",
      solvingBothMethods: "Calculando con ambos métodos…",
      colMethod: "Método",
      colQThermal: "q (térmico)",
      colIterations: "Iteraciones",
      colResidual: "Residuo",
      colStatus: "Estado",
      colTime: "Tiempo",
      meshLadder: "Secuencia de convergencia de malla (este cálculo):",
      colRows: "Filas",
      colSolverConverged: "Solucionador convergido",
      colAccepted: "Aceptada",
    },
    charts: {
      noDataYet: "Todavía no hay datos",
      legendToggle: (label: string) => `${label} (haga clic para ocultar o mostrar)`,
    },
    search: {
      clearSearch: "Borrar la búsqueda",
      filters: "Filtros",
    },
    monthly: {
      title: "Glaser mensual",
      panelDesc: "Análisis mensual cuasiestático (1D), el método de la anterior aplicación Thermal: Glaser en cada interfaz mes a mes, con la condensación que se acumula y se evapora de un mes al siguiente, alimentado por las medias mensuales del clima del emplazamiento. Se ejecuta con cada cálculo, junto a los resultados Simplificado y Malla 2D.",
      inputsTitle: "Datos de cálculo",
      summaryClass: (cls: number) => `clase de humedad ${cls}`,
      summaryConstant: (ti: string, unit: string, rhi: string) => `Ti ${ti} ${unit}, RHi ${rhi} %`,
      climateOfSite: "clima del emplazamiento",
      summaryDrying: "tiempo de secado",
      outsideClimate: "Clima exterior",
      outsideClimateTip: "El clima del emplazamiento elegido en la pestaña Datos (un emplazamiento en el mapa o un archivo EPW), el mismo que en los demás análisis. Medias mensuales de los valores horarios: Te = media de T, pv,e = media de RH·psat(T).",
      loadingClimate: "Cargando el clima…",
      change: "cambiar →",
      noClimate: "Todavía no hay clima.",
      insideClimate: "Clima interior",
      tipIso13788: "Clases de humedad, UNI EN ISO 13788: Δp = 270 · clase · clamp((20 − Te) / 20; 0; 1) Pa, pi = pe + 1.10 · Δp, Ti = 20 °C.",
      tipEn15026: "EN 15026 Annex D: hora a hora a partir de la media móvil de 24 h de la temperatura exterior del archivo EPW, luego promediada por mes.",
      tipConstant: "Temperatura y humedad relativa interiores constantes en todos los meses.",
      insideModel: "Modelo de clima interior",
      ashraeOption: "ASHRAE 160 - aún no disponible: sus valores se están verificando",
      modelIso13788: "Clases de humedad (UNI EN ISO 13788)",
      untilAshrae: " - hasta que ASHRAE 160 esté disponible",
      modelConstant: "Ti y RHi constantes",
      modelEn15026: "EN 15026 Annex D",
      humidityClass: "Clase de humedad",
      classOption: (cls: number) => `Clase ${cls}`,
      moistureLoad: "Carga de humedad",
      loads: {
        "normal+5": "Carga de humedad normal + 5 % (caso de diseño)",
        normal: "Carga de humedad normal",
        low: "Carga de humedad baja",
        high: "Carga de humedad alta",
      },
      dryingTime: "Tiempo de secado",
      dryingTip: "Meses necesarios para secar 1 kg/m² de agua situada en el centro de la capa elegida, a partir de octubre, con el clima mensual. Método de todo el análisis: UNI EN ISO 13788, Glaser mensual con acumulación.",
      dryingLayer: "Capa del cálculo de secado",
      chooseLayer: "Elija la capa…",
      monthlyClimate: "Clima mensual",
      monthlyClimateUsed: "Clima mensual y clima interior utilizado",
      monthlyClimateTip: (utcOffset: string) => `Medias mensuales de los valores horarios en hora local (UTC${utcOffset}): Te = media de T; pe = media de RH·psat(T); RHe = pe / psat(Te).`,
      statusEmpty: "La estratigrafía está vacía: añada primero capas en el panel Estratigrafía.",
      statusInternalWall: "Sin lado exterior (INTERIOR en ambos lados: un muro interior): el análisis mensual necesita un clima exterior y uno interior.",
      statusBothOutside: "Sin lado exterior (EXTERIOR en ambos lados): el análisis mensual necesita un clima exterior y uno interior.",
      statusChooseClimate: "Elija el clima del emplazamiento en la pestaña Datos (un emplazamiento en el mapa o un archivo EPW).",
      failed: "El análisis mensual ha fallado.",
      verified: "Verificado",
      notVerified: (n: number) => `No verificado (${n} ${n > 1 ? "comprobaciones" : "comprobación"})`,
      checkResidual: "balance anual",
      checkAdmissible: "condensación admisible",
      checkMould: "moho (f_Rsi)",
      allPass: "No queda condensación al final del ciclo, toda cantidad acumulada dentro de su valor admisible, f_{Rsi} por encima de f_{Rsi,min}.",
      allPassBoth: "No queda condensación al final del ciclo, toda cantidad acumulada dentro de su valor admisible, f_{Rsi} por encima de f_{Rsi,min}, en ambas secciones.",
      settingsUsed: "Configuración utilizada",
      startOfCycle: "Inicio del ciclo",
      subLayers: "Subcapas",
      subLayersValue: (rMax: string) => `cada capa con R > ${rMax} m²K/W dividida en subcapas iguales`,
      humidityDependentSd: "Sd dependiente de la humedad",
      condensationTest: "Comprobación de condensación",
      surfaces: "Superficies",
      surfacesValue: (rsi: string, rse: string) => `R_{si} = ${rsi} m²K/W, R_{se} = ${rse} m²K/W (cámaras de aire según EN ISO 6946, como en los demás análisis)`,
      sections: "Secciones",
      sectionsValue: (list: string) => `${list} - secciones 1D separadas, sin método combinado`,
      admissibleCondensate: "Condensación admisible",
      mould: "Moho",
      uValue: (section: string) => `Transmitancia U (${section})`,
      interfaces: (n: number) => `${n} interfaces`,
      maxAccumulated: "Condensación acumulada máx.",
      interfacesCondensing: (n: number) => `${n} ${n === 1 ? "interfaz" : "interfaces"}`,
      noCondensation: "sin condensación",
      leftAtEnd: "Restante al final del ciclo",
      mustBeZero: "debe ser 0",
      mouldStat: "Moho: f_{Rsi} / f_{Rsi,min}",
      criticalMonth: (month: string) => `mes crítico ${month}`,
      noMonthAtRisk: "ningún mes en riesgo",
      months: "meses",
      dryingTag: (layer: string) => `1 kg/m² en ${layer}, desde octubre`,
      monthByMonth: (section: string) => `Mes a mes - ${section}`,
      month: "Mes",
      maTotal: "M_{a} total",
      noInterfaceCondenses: "Ninguna interfaz condensa en ningún mes.",
      monthsNote: (unit: string) => `G_{c}: condensación (+) o evaporación (−) del mes; M_{a}: acumulada al final del mes [${unit}]. Interfaces numeradas desde la superficie exterior.`,
      admissibleTitle: (section: string) => `Condensación admisible - ${section}`,
      interface: "Interfaz",
      maxMa: (unit: string) => `M_{a} máx. [${unit}]`,
      admissibleColumn: (unit: string) => `Admisible [${unit}]`,
      materialAndRule: "Material y regla",
      notAssessed: "no evaluada",
      within: "dentro del límite",
      exceeded: "⚠ superada",
      admissibleNote: "En una interfaz entre dos materiales, la menor de sus cantidades admisibles.",
      glaserDiagram: "Diagrama de Glaser",
      abscissa: "Abscisa",
      thickness: "Espesor",
      diagramMonth: "Mes del diagrama",
      condensationOverCycle: "Condensación a lo largo del ciclo",
      condensationNote: (n: number, months: string, maxG: string, unit: string, at: string) => `⚠ Se forma condensación en ${n} ${n === 1 ? "mes" : "meses"} (${months}): como máximo ${maxG} ${unit} en ${at};`,
      evaporatedBy: (month: string) => `se ha evaporado al final de ${month}.`,
      evaporatesWithin: "se evapora dentro del ciclo.",
      notAllEvaporates: "no se evapora por completo dentro del ciclo.",
      mouldTitle: (section: string) => `Moho - UNI EN ISO 13788 - ${section}`,
      mouldNote: (rh: string, rsi: string, fRsi: string, rMin: string) => `RH_{si} ≤ ${rh} %, R_{si} = ${rsi} m²K/W: f_{Rsi} de la sección = ${fRsi}${rMin}. Meses con T_{i} ≤ T_{e}: no evaluados (—); f_{Rsi,min} ≤ 0: sin requisito en ese mes.`,
      mouldRmin: (value: string, unit: string) => `; resistencia térmica mínima de las capas para el mes crítico: ${value} ${unit}`,
      curvesShown: "Curvas mostradas (clic para ocultar o mostrar)",
      legendP: "presión de vapor p",
      legendPsat: "presión de saturación p_{sat} (● cara de una capa, ○ dentro de una capa)",
      legendWet: "interfaz con condensación",
      glaserAria: (month: string) => `Diagrama de Glaser, ${month}`,
      depthAxis: (unit: string) => `profundidad desde el interior [${unit}]`,
      sdAxisSi: "Sd desde el interior [m]",
      sdAxisUk: "resistencia al vapor desde el interior [MN·s/g]",
      sdAxisImperial: "resistencia al vapor desde el interior [rep = 1/perm]",
      inside: "interior",
      outside: "exterior",
      pressureAxis: "presión [Pa]",
      surface: "superficie",
      node: (k: number) => `nodo ${k}`,
      psatTip: (point: string, psat: number) => `${point}: p_sat ${psat} Pa`,
      legendCondensed: "condensada en el mes",
      legendEvaporated: "evaporada en el mes",
      legendAccumulated: "acumulada al final del mes",
      noCondensateAnyMonth: "sin condensación en ningún mes",
      accumulationAria: "Agua condensada, evaporada y acumulada por mes",
      condensed: "condensada",
      evaporated: "evaporada",
      accumulated: "acumulada",
      sectionLabels: {
        bay: "Vano (entre los montantes)",
        stud: "Montante",
        whole: "Muro completo",
      },
      interfaceOuter: "superficie exterior",
      interfaceInner: (material: string) => `${material} | superficie interior`,
      interfaceWithin: (material: string) => `dentro de ${material}`,
      insideIso13788: (cls: number, fs: string, ti: number) =>
        `Clase de humedad UNI EN ISO 13788 ${cls}${cls === 3 ? " (DM 26/06/2015)" : ""}: Δp = 270·${cls}·clamp((20 − Te)/20; 0; 1) Pa, pi = pe + ${fs}·Δp (como máximo psat(Ti)), Ti = ${ti} °C (Te si es más cálida)`,
      insideConstant: (ti: number, rhi: number) => `Constante: Ti = ${ti} °C, RHi = ${rhi} %`,
      insideEn15026: (load: string) => `EN 15026 Anexo D, ${load}: valores horarios a partir de la media móvil de 24 h de la temperatura exterior, promediados por mes`,
      startNotes: {
        OCTOBER_LEGACY: "Octubre, como el método heredado",
        NONE_CONDENSES: "ningún mes condensa partiendo de seco: ciclo desde octubre",
        ALL_CONDENSE: "todos los meses condensan partiendo de seco: ciclo desde octubre",
        FIRST_CONDENSING: (month: string) => `primer mes que condensa tras un mes sin condensación: ${month}`,
      },
      failureMould: (fRsi: string, fRsiMin: string, month: string) => `f_Rsi = ${fRsi} inferior a f_Rsi,min = ${fRsiMin} (mes crítico: ${month})`,
      failureResidual: (g: string, at: string) => `condensación restante al final del ciclo: ${g} g/m² en ${at}`,
      failureAdmissible: (g: string, at: string, admissible: string, material: string, rule: string) =>
        `${g} g/m² en ${at}, por encima de los ${admissible} g/m² admisibles (${material}: ${rule})`,
      warningPicardCapped: (section: string, passes: number, tolM: number) =>
        `${section}: la iteración de Sd dependiente de la humedad alcanzó ${passes} pasadas en al menos un mes sin estabilizarse dentro de ${tolM} m; se muestran los valores de la última pasada.`,
      warningOutsideRhAbove100: "En al menos un mes la presión de vapor exterior media supera la presión de saturación de la temperatura media (RHe > 100 %); se usa tal cual.",
      presetLabels: {
        legacy: "Thermal heredado",
        iso13788: "UNI EN ISO 13788 (predeterminado)",
      },
      startRules: {
        legacy: "Octubre (heredado)",
        iso13788: "primer mes que condensa tras un mes sin condensación (UNI EN ISO 13788), por sección",
      },
      sdRules: {
        legacy: "Sd dependiente de la humedad a partir de la HR de la cara interior de su subcapa (regla heredada)",
        iso13788: "Sd dependiente de la humedad a partir de la HR media de las dos caras de su subcapa",
      },
      picardRule: (tolM: number, max: number) => `hasta que la suma de |ΔSd| ≤ ${tolM} m, como máximo ${max} pasadas`,
      pTolExact: "p ≥ psat exacto (heredado)",
      pTolRule: (pTol: number) => `p ≥ psat·(1 − ${pTol})`,
      mouldRule: (rh: number, rsi: number) => `UNI EN ISO 13788: f_Rsi,min por mes con RH_si ≤ ${rh} %, R_si = ${rsi} m²K/W`,
      admissibleRule: "tabla heredada frmInfoVAP por material; en una interfaz, la menor cantidad admisible de los materiales que la tocan; \"30 d\" leído como 30·ρ·d",
    },
    dynamic: {
      // Inputs tab
      desc: "Hora a hora durante varios años con datos climáticos reales, en el marco de la EN 15026: temperaturas, humedad relativa, condensación y secado de la estratigrafía actual.",
      preview: "Vista previa",
      why: "¿Por qué \"cuasidinámico\"?",
      hide: "ocultar",
      settingsTitle: "Ajustes de cálculo (Ajustes → Cuasidinámico)",
      whyDynamicTerm: "Dinámico",
      whyDynamic: ": el tiempo se simula hora a hora, de modo que las capas almacenan y liberan calor y humedad - humectación invernal y secado estival, humedad de construcción que se seca a lo largo de los años. Es lo que los cálculos estacionarios (Glaser, 2D, mensual) no pueden mostrar.",
      whyQuasiTerm: "Cuasi",
      whyQuasi: ": las propiedades de los materiales se mantienen constantes. λ, ρc y µ no varían con la humedad; el almacenamiento de humedad es lineal, w = ξ·φ con ξ la secante de la curva de sorción al 80 % de HR; el agua solo se mueve como vapor (sin transporte líquido/capilar). El calor latente y la capacidad calorífica del contenido de agua son opcionales (Efectos incluidos en el modelo, abajo). Las membranas higrovariables (CLIMA CONTROL) sí varían su Sd con la HR, y la condensación se sigue mientras se forma y se seca.",
      whyFullBefore: "El modelo ",
      whyFullTerm: "dinámico completo",
      whyFull: " de la EN 15026 (como en WUFI) añade almacenamiento y permeabilidad dependientes de la humedad, conducción líquida y λ(w), y la entalpía del vapor y del líquido en el balance energético; requiere datos higrométricos completos de los materiales y está validado con el benchmark del Annex B de la norma. Es el siguiente paso. El modelo cuasidinámico es fiable para los cerramientos regidos por la difusión de vapor (cerramientos ligeros de madera y aislados con membranas) y es una herramienta de evaluación preliminar, no una certificación.",
      tabsLabel: "Análisis cuasidinámico",
      tabInputs: "Datos de entrada",
      tabResults: "Resultados",
      runFirst: "Ejecute primero el análisis cuasidinámico",
      outerSurface: "Superficie exterior",
      absorptance: "Absortancia solar",
      emissivity: "Emisividad",
      insideLoad: "Clima interior (EN 15026 Annex D)",
      loads: {
        "normal+5": "Carga de humedad normal + 5 % (caso de diseño)",
        normal: "Carga de humedad normal",
        low: "Carga de humedad baja",
        high: "Carga de humedad alta",
      },
      ashrae160Note: "ASHRAE 160 aún no disponible (sus valores se están verificando): se usa el modelo UNI / EN.",
      modelAndRun: "Modelo y cálculo",
      run: "Cálculo",
      years: "Años",
      woodCriterion: "Criterio de la madera [M.-%]",
      woodCriterionTitle: "Fijo: contenido de humedad de los materiales derivados de la madera ≤ 16 M.-%",
      construction: "Humedad de construcción (HR inicial 95 %, si no 80 %)",
      stepAuto: (step: string) => `automático (${step})`,
      solver2d: (method: string, tolerance: string, rows: number) => `${method} · tolerancia ${tolerance} · ${rows} filas`,
      direct1d: "solver 1D directo",
      calcSummary: (step: string, solver: string) => `Cálculo: paso ${step} · ${solver} ·`,
      changeInSettings: "cámbielo en Ajustes ⚙",
      runButton: (mode: string) => `Ejecutar el análisis cuasidinámico (${mode})`,
      calculating: "Calculando…",
      hint2d: "Modo malla 2D (cabecera): los montantes y el aislamiento forman una única malla, acoplada. Unos 10-20 s por año simulado con 20-40 filas; las mallas más finas tardan más, y el servidor detiene un cálculo tras 300 s.",
      hint1d: "Modo simplificado (cabecera): una columna 1D a través de las capas; los montantes no se incluyen (cambie la cabecera a malla 2D para incluirlos).",
      emptyStratigraphy: "La estratigrafía está vacía: añada primero capas en el panel Estratigrafía.",
      noClimate: "Elija primero el clima: pestaña Datos, Clima del emplazamiento (un emplazamiento en el mapa o un archivo EPW).",
      noOutdoorSide: "Sin lado exterior: el análisis cuasidinámico necesita un lado EXTERIOR y un lado INTERIOR en el dibujo (pestaña Estratigrafía).",
      analysisFailed: "El análisis ha fallado.",
      cancelled: "El cálculo se ha cancelado; se sigue mostrando el resultado anterior (si lo hay).",
      unreadable: "No se ha podido leer la respuesta del servidor.",
      unreachable: "No se ha podido contactar con el servidor.",
      timeout: "El cálculo ha tardado más de lo que permite el servidor (300 s). Simule menos años, use una malla 2D más gruesa o un paso de tiempo más largo.",
      statusError: (status: number) => `El servidor ha respondido con el estado ${status}.`,
      stopped: "El cálculo se ha detenido antes del final (es posible que el servidor haya alcanzado su límite de 300 s). Simule menos años, use una malla 2D más gruesa o un paso de tiempo más largo.",
      lastResult: (mode: string, years: number, source: string) => `Último resultado: ${mode}, ${years} año(s), ${source} ·`,
      openResults: "abrir Resultados →",
      mode2d: "Malla 2D",
      mode1d: "1D",
      modeSimplified: "Simplificado",
      modeSimplified1d: "Simplificado (1D)",
      // effects included in the model
      effectsLegend: "Efectos incluidos en el modelo",
      effects: {
        dynamic: {
          label: "Sistema dinámico",
          hint: "Almacenamiento de calor y humedad (los términos ∂/∂t). Desactivado: cada paso de tiempo es el estado estacionario de su clima (Glaser hora a hora); solo se acumula la condensación.",
        },
        waterHeatCapacity: {
          label: "Capacidad calorífica del contenido de agua",
          hint: "ρc + w·c_w, con c_w = 4190 J/(kg·K) y el contenido de agua w del paso de tiempo anterior.",
        },
        latentHeat: {
          label: "Calor latente",
          hint: "Calor liberado donde el vapor se adsorbe o condensa y absorbido donde se evapora: h_v · aporte neto de vapor de cada celda, h_v = 2500 kJ/kg (EN 15026:2007), a partir del campo de vapor del paso de tiempo anterior.",
        },
        variableSd: {
          label: "Resistencia al vapor variable µ(w), Sd(HR)",
          hint: "Sd(HR) de las membranas con una curva medida (CLIMA CONTROL 80 y 160) y µ(w) de los materiales con una curva µ dependiente de la humedad, actualizados en cada paso de tiempo. Desactivado: sus valores fijos.",
        },
        rain: {
          label: "Lluvia",
          hint: "Lluvia batiente sobre la superficie exterior, a partir de la lluvia y el viento del clima; el agua que entra sale por evaporación (sin transporte capilar).",
        },
        conductivity: {
          label: "Conductividad variable λ(w)",
          hint: "Requiere datos de λ dependientes de la humedad para cada material.",
        },
        capillarity: {
          label: "Capilaridad",
          hint: "Transporte líquido: requiere la curva de sorción completa y los coeficientes de transporte líquido de cada material.",
        },
      },
      availableSoon: "disponible próximamente",
      needsDynamic: "Requiere el sistema dinámico (almacenamiento).",
      noRain: "El clima elegido no tiene lluvia (PVGIS no la incluye): elija ERA5 (Open-Meteo), una estación TMYx o un archivo EPW con lluvia.",
      rainSettings: "Ajustes de la lluvia",
      waterEntering: "Agua que entra en el muro",
      rainModeTitle: "Fijado por las normas de los menús (Ajustes → General)",
      rainAbsorb: "Fachada absorbente (EN 15026): 70 % en la capa exterior",
      rainDrain: "Revestimiento con drenaje (ASHRAE 160): 1 % detrás de él",
      rainAbsorbHint: "Revoco, fábrica vista: el 70 % de la lluvia batiente entra como agua líquida en las celdas más exteriores de la capa exterior, hasta la saturación capilar del material (w_cap); el resto escurre.",
      rainDrainHint: "Revestimiento o paneles sobre una cámara drenada: el 1 % de la lluvia batiente se deposita en la parte exterior de la primera capa detrás del revestimiento (la fuga de diseño de la ASHRAE 160).",
      exposure: "Exposición (coeficiente de lluvia batiente R2)",
      exposures: {
        low: "Edificio bajo (hasta 10 m)",
        "tall-low": "Edificio alto, parte inferior",
        "tall-mid": "Edificio alto, parte central",
        "tall-top": "Edificio alto, parte superior",
      },
      exposureOption: (label: string, r2: number) => `${label} — R2 = ${r2} s/m`,
      rainFormula: "Lluvia sobre el muro = R2 × velocidad del viento × lluvia horizontal × cos(ángulo entre el viento y la fachada), solo desde el lado de barlovento; las cubiertas reciben además la lluvia horizontal × cos(inclinación).",
      // orientation of the outer surface
      directions: ["N", "NE", "E", "SE", "S", "SO", "O", "NO"],
      orientLabel: (tilt: number, direction: string, azimuth: number) => `Superficie exterior: inclinación ${tilt}°, orientada al ${direction} ${azimuth}°. Arrastre o use las teclas de flecha para girarla.`,
      orientValue: (tilt: number, azimuth: number) => `inclinación ${tilt}°, orientación ${azimuth}°`,
      outside: "exterior",
      inside: "interior",
      turning: "↔ girando (orientación)",
      tilting: "↕ inclinando",
      tiltTitle: (now: string) => `Inclinación: 0° cubierta plana, 90° muro vertical (ahora: ${now})`,
      flatRoof: "cubierta plana",
      wall: "muro",
      roof: "cubierta",
      azimuthTitle: (now: string) => `Orientación: dirección de la superficie exterior, grados desde el norte, en sentido horario (ahora: ${now})`,
      anyFlat: "cualquiera, plana",
      orientHint: (wallMinTilt: number) => `Arrastre el muro: izquierda/derecha lo gira (orientación), hacia abajo lo levanta hacia la vertical, hacia arriba lo baja - uno cada vez, según el primer movimiento de cada arrastre. Teclas de flecha: pasos de 1°, Mayús 5°. Una inclinación ≥ ${wallMinTilt}° se trata como muro, por debajo como cubierta.`,
      // progress window
      running: "Análisis cuasidinámico en curso",
      cancelling: "Cancelando…",
      sending: "Enviando la estratigrafía al servidor",
      comparison: " (comparación)",
      simulatedUpTo: (day: string, years: number) => `simulado hasta el ${day} de ${years} año(s)`,
      elapsed: (time: string) => `transcurrido ${time}`,
      timeLeft: (time: string) => ` · quedan unos ${time}`,
      // Results tab
      resultsDesc: "Resultado del último cálculo. Arrastre sobre un gráfico temporal para seleccionar un periodo; haga doble clic para todo el cálculo.",
      noResult: "Aún no hay resultados: ejecute el análisis en Análisis cuasidinámico.",
      checksNotSatisfied: (failed: number, total: number) => `⚠ ${failed} de ${total} comprobaciones no cumplidas`,
      allSatisfied: "todas las comprobaciones cumplidas",
      noCheckApplicable: "ninguna comprobación aplicable",
      verdictLine: (mode: string, years: number, source: string) => `${mode} · ${years} año(s) · ${source} · evaluación preliminar, no certificación · haga clic en una comprobación para ver los detalles`,
      modeMismatch: (resultMode: string, headerMode: string) => `Este resultado se calculó en modo ${resultMode}; la cabecera está ahora en ${headerMode}. Ejecute de nuevo el cálculo para actualizarlo.`,
      notApplicable: "n/a",
      satisfied: "cumplida",
      notSatisfied: "no cumplida",
      notSatisfiedWarn: "⚠ no cumplida",
      sectionsLabel: "Secciones",
      secRun: "Cálculo y datos climáticos",
      secPeriod: "Resultados en el periodo",
      secPeriodTitle: "Resultados en el periodo seleccionado",
      secConv: "Convergencia",
      secConvTitle: "Convergencia en el periodo seleccionado",
      secCharts: "Gráficos",
      secChartsTitle: "Gráficos en el periodo seleccionado",
      secFields: "Campos 2D",
      secFieldsTitle: "Campos 2D en horas destacadas",
      secHourly: "Hora a hora",
      secHourlyTitle: "Hora a hora (último año simulado)",
      secLayers: "Capas del modelo",
      resultBadge: (mode: string) => `resultado ${mode}`,
      source: "Fuente",
      sourceDbs: (radiation: string, weather: string) => ` (radiación ${radiation}, meteorología ${weather})`,
      location: "Ubicación",
      period: "Periodo",
      insideClimate: "Clima interior",
      attribution: "Atribución",
      runLine: (years: number, month: string, step: string, grid: string, cells: number, seconds: string, mass: string, energy: string) =>
        `Cálculo: ${years} años desde el 1 de ${month}, paso de tiempo ${step}, ${grid}${cells} celdas; calculado en ${seconds} s; residuos de balance: masa ${mass}, energía ${energy}.`,
      gridText: (rows: number, columns: number) => `malla 2D ${rows} × ${columns} = `,
      rhInterfaces: "Humedad relativa en las interfaces (media diaria)",
      rhAxis: "HR [%]",
      tInterfaces: (unit: string) => `Temperatura en las interfaces (media diaria) [${unit}]`,
      outsideAir: "aire exterior",
      membraneSd: "Sd de las membranas (media diaria, banda = rango diario)",
      condensateTitle: "Condensación (media diaria) [g/m²]",
      total: "total",
      waterTitle: "Agua total en el elemento [kg/m²]",
      totalWater: "agua total",
      rainTitle: "Lluvia sobre la superficie exterior y que entra en el muro (por día) [kg/m²]",
      rainAxis: "kg/m² por día",
      rainOnSurface: "lluvia batiente sobre la superficie",
      rainEntering: "que entra en el muro",
      woodTitle: "Contenido de humedad de las capas derivadas de la madera [M.-%]",
      rhMapTitle: "Humedad relativa a través del elemento, día a día",
      fieldColdest: "En la hora más fría del cálculo",
      fieldPeak: "En el pico de condensación",
      inPeriod: "en el periodo",
      outsidePeriod: "fuera del periodo",
      fieldLine: (date: string, hour: string, te: string, unit: string) => `${date}, ${hour}:00 (hora del cálculo) · exterior ${te} ${unit}`,
      fieldCondensate: (value: string) => ` · condensación ${value} g/m²`,
      noCondensateRun: "Sin condensación en todo el cálculo.",
      hourlyTitle: "Hora a hora: HR en las 4 interfaces más húmedas del último año",
      hourlyLastYearOnly: (from: string) => `Las series horarias solo cubren el último año simulado (desde el ${from}).`,
      selectLastYear: "Seleccionar el último año",
      membraneVariable: (min: number, max: number) => `membrana, Sd ${min}–${max} m según la HR`,
      membraneFixed: (sd: string) => `membrana, Sd ${sd} m`,
      xiText: (xi: string, source: string) => `ξ = ${xi} kg/m³ (${source})`,
      // period filter
      dayLabel: (day: number, month: string, year: number) => `${day} ${month}, año ${year}`,
      yearN: (year: number) => `Año ${year}`,
      days: "días",
      presetPeriods: "Periodos predefinidos",
      wholeRun: "Todo el cálculo",
      season: (season: string, from: string, to: string, year: number) => `${season} (${from}–${to}), año ${year}`,
      winter: "Invierno",
      summer: "Verano",
      from: "Desde",
      to: "hasta",
      periodHint: "Arrastre sobre la franja o sobre cualquier gráfico de abajo para seleccionar un periodo; haga doble clic en un gráfico para todo el cálculo.",
      day: "Día",
      month: "Mes",
      simulatedYear: "Año simulado",
      overviewLabel: "Periodo seleccionado dentro de todo el cálculo: arrastre para cambiarlo",
      overviewTitle: "temperatura del aire exterior, todo el cálculo",
      // results in the period
      surfaceTmin: "Superficie interior, temperatura mín.",
      fRsiMin: "Factor de temperatura f_Rsi, mín.",
      fRsiTag: (day: string, limit: number) => `${day} · límite ${limit}`,
      noHour10K: "ninguna hora con Ti − Te ≥ 10 K",
      condMax: "Condensación, máx.",
      condTag: (day: string, days: number) => `${day} · ${days} día(s) con condensación`,
      noneInPeriod: "ninguna en el periodo",
      condLastDay: "Condensación el último día",
      driedOut: "secada",
      stillPresent: "todavía presente",
      woodMax: "Contenido de humedad de la madera, máx.",
      rhMax: "HR en una interfaz, máx. (horaria)",
      rainIn: "Lluvia que entra en el muro",
      rainTag: (onSurface: string) => `de ${onSurface} kg/m² de lluvia batiente sobre la superficie`,
      waterChange: "Agua en el elemento, variación",
      waterTag: (from: string, to: string, trend: string) => `${from} → ${to} kg/m² · ${trend}`,
      netWetting: "humectación neta",
      netDrying: "secado neto",
      noNetChange: "sin variación neta",
      outsideMean: "Aire exterior, media",
      outsideTag: (rh: string, min: string, max: string, unit: string) => `HR ${rh} % · de ${min} a ${max} ${unit} (medias diarias)`,
      checksInPeriod: "Comprobaciones en el periodo",
      screening: "evaluación preliminar, no certificación",
      checkCondensate: (limit: number) => `Condensación ≤ ${limit} g/m² durante todo el periodo`,
      checkCondensateDetail: (max: string, day: string, days: number, end: string) => `máx. ${max} g/m² el ${day}; ${days} día(s) con condensación; ${end} g/m² el último día`,
      noCondensatePeriod: "Sin condensación en el periodo.",
      checkWood: (limit: number) => `Materiales derivados de la madera: contenido de humedad ≤ ${limit} M.-%`,
      checkWoodDetail: (name: string, max: string, day: string, daysOver: number) => `${name}: máx. ${max} M.-% el ${day}${daysOver ? `, ${daysOver} día(s) por encima` : ""}`,
      noWood: "Ningún material derivado de la madera.",
      checkFrsi: (limit: number) => `Factor de temperatura f_Rsi ≥ ${limit} (horas con Ti − Te ≥ 10 K)`,
      checkFrsiDetail: (value: string, day: string) => `f_Rsi mínimo ${value} el ${day}`,
      noHour10KPeriod: "Ninguna hora con Ti − Te ≥ 10 K en el periodo.",
      accumulationNote: "La comprobación de acumulación de un año a otro requiere años completos: consulte las comprobaciones de final de cálculo más arriba.",
      membranesWood: "Membranas y materiales derivados de la madera",
      colMembrane: "Membrana",
      colSdMean: "Sd medio [m]",
      colSdMin: "Sd mín [m]",
      colSdMax: "Sd máx [m]",
      byRh: "según la HR",
      fixedSd: "fijo",
      noMembrane: "Ninguna membrana en la estratigrafía.",
      colWood: "Material derivado de la madera",
      colUMean: "u medio [M.-%]",
      colUMax: "u máx [M.-%]",
      colDaysOver: (limit: number) => `Días > ${limit} M.-%`,
      woodNote: "u = pico diario de la celda más húmeda del material.",
      layersInPeriod: "Capas en el periodo",
      colLayer: "Capa",
      colTmin: (unit: string) => `T mín [${unit}]`,
      colTmean: (unit: string) => `T media [${unit}]`,
      colTmax: (unit: string) => `T máx [${unit}]`,
      colRhMean: "HR media [%]",
      colRhMax: "HR máx [%]",
      colWaterMean: "Agua media [kg/m²]",
      colCondMax: "Condensación máx [g/m²]",
      membraneFace: "membrana: en 1D una cara entre las celdas (consulte su Sd y las interfaces a ambos lados)",
      layersNote: "Capas del interior hacia el exterior. Los valores de cada capa son medias de volumen diarias; el mínimo y el máximo se refieren a esas medias diarias.",
      interfacesInPeriod: "Interfaces en el periodo",
      colInterface: "Interfaz",
      colRhMaxHourly: "HR máx, horaria [%]",
      colDays80: "Días con HR media > 80 %",
      interfacesNote: "T mínima y máxima de las medias diarias; HR máxima sobre las horas del periodo.",
      profileTitle: "Humedad relativa a través del elemento en el periodo",
      // convergence
      convSettings: (method: string, tolerance: string, maxIter: number, step: string) => `${method} · tolerancia ${tolerance} · máx. ${maxIter} iteraciones por resolución · paso de tiempo ${step}`,
      hoursConverged: "Horas convergidas",
      everyHour: "todas las horas",
      hoursNotConverged: (hours: number) => `${hours} hora(s) sin convergencia`,
      maxIterations: "Resolución lineal, iteraciones máx.",
      limitN: (limit: number) => `límite ${limit}`,
      directSolver: " · solver directo",
      maxResidual: "Resolución lineal, residuo máx.",
      toleranceTag: (tolerance: string) => `tolerancia ${tolerance}`,
      energyResidual: "Balance energético, residuo máx.",
      relativeLimit: (limit: string) => `relativo · límite ${limit}`,
      massResidual: "Balance de masa, residuo máx.",
      massTag: (limit: string, max: string) => `relativo · límite ${limit} · máx. de todo el cálculo ${max} kg/m² por paso`,
      notConvergedTitle: "Hora a hora: horas sin convergencia marcadas",
      causes: ["resolución térmica", "resolución del vapor", "conjunto activo de condensación", "Sd de las membranas"],
      causeHours: (cause: string, hours: number) => `${cause} ${hours} h`,
      notConvergedList: (list: string) => `Sin convergencia: ${list}.`,
      allConverged: "Todas las horas del periodo han convergido: todas las resoluciones lineales han alcanzado la tolerancia, el conjunto activo de condensación se ha estabilizado y la iteración del Sd de las membranas ha convergido.",
      residualNote: "Los residuos de balance se muestran por separado: un residuo de masa relativo superior a 1e−6 en una hora con un flujo casi nulo a través de las superficies puede corresponder a una cantidad absoluta mínima.",
      iterationsTitle: (perHour: boolean) => `Iteraciones de la resolución lineal (${perHour ? "por hora" : "peor hora de cada día"})`,
      residualsTitle: (perHour: boolean) => `Residuos, log10 (${perHour ? "por hora" : "peor hora de cada día"})`,
      iterations: "iteraciones",
      maxIterSeries: "iteraciones máx. (calor o vapor)",
      linearRelative: "resolución lineal (relativo)",
      energyBalance: "balance energético",
      massBalance: "balance de masa",
      failsTitle: "Horas sin convergencia (las 30 primeras del periodo)",
      colHour: "Hora",
      colNotConverged: "Sin convergencia",
      colIterations: "Iteraciones",
      colLinearResidual: "Residuo lineal",
      converged: "convergida",
      linearNotConverged: "resolución lineal sin convergencia",
      activeSetNotConverged: "conjunto activo o Sd de las membranas sin convergencia",
      // charts
      curvesShown: "Curvas mostradas (haga clic para ocultar o mostrar)",
      rhCondensation: "100 % (condensación)",
      membrane: "membrana",
      profileLabel: "Humedad relativa a través del elemento",
      outsideCaps: "EXTERIOR",
      profileMean: "media de las medias diarias",
      profileMax: "media diaria más alta",
      profileMin: "media diaria más baja",
      insideAtBottom: "interior abajo",
      quantity: "Magnitud",
      fieldRh: "HR",
      fieldT: "Temperatura",
      fieldWc: "Condensación",
      fieldHover: (layer: string, stud: boolean, x: string, y: string, temperature: string, unit: string, rh: string, condensate: string) =>
        `${layer}${stud ? " (montante)" : ""} · x ${x} mm, ${y} mm desde el interior · T ${temperature} ${unit} · HR ${rh} % · condensación ${condensate} kg/m³`,
      noCondensateHour: "sin condensación en esta hora",
      fieldHint: "Pase el ratón sobre una celda para ver sus valores. Anchura: la mitad de la separación entre montantes, entre los planos de simetría.",
    },
    results: {
      badge2D: "malla 2D - último cálculo",
      badgeSimplified: "simplificado - último cálculo",
      badgeNone: "sin resultados todavía",
      warnings: "Advertencias",
      stratigraphy: "Estratigrafía",
      fieldTitle: "Campo a través de la estratigrafía",
      zoomResetTitle: "Restablecer el zoom (desplácese sobre el dibujo para acercar/alejar)",
      standingNoteTitle: "Métodos numéricos y selección de materiales",
      standingNoteDetail: "Cuando procede (modo de malla 2D) se utilizan métodos numéricos. La verificación de su validez y de la idoneidad de cada material y producto seleccionado en la estratigrafía, para el caso concreto, es responsabilidad del proyectista.",
      inputErrorTitle: "Cálculo no realizado - datos no válidos",
      inputErrorTitleQuota: "Cálculo no ejecutado - tiempo de cálculo del servidor agotado",
      inputErrorTitleRateLimit: "Cálculo no ejecutado - límite de solicitudes alcanzado",
      cancelledTitle: "CÁLCULO CANCELADO",
      cancelledDetail: "Cálculo cancelado por el usuario. Los valores siguientes proceden del último cálculo realmente completado.",
      noWarnings: "Añada al menos una capa a la estratigrafía para ver aquí las advertencias técnicas.",
      uLimitLabels: {
        wall: "Muro exterior (DE Bestand, 0,24)",
        roof: "Cubierta / último forjado (DE Bestand, 0,24)",
        floor: "Suelo contra terreno/no calefactado (DE Bestand, 0,30)",
        custom: "Límite personalizado",
      },
      checks: {
        uValue: "Transmitancia U",
        uValue2D: "Transmitancia U (2D)",
        limit: (value: string, reference: string) => `límite ${value} (${reference})`,
        interstitial: "Condensación intersticial y moho",
        interstitial2D: "Condensación intersticial y moho (2D)",
        interstitialCondensation: "condensación intersticial",
        possibleMould: "posible formación de moho",
        none: "ninguna",
        wet1D: (rate: string, node: string) => `${rate} g/(m²·h) en el nodo ${node} (EN ISO 13788, mes de diseño)`,
        wet2D: (cells: number, share: string) => `${cells} ${cells > 1 ? "celdas" : "celda"} con HR > 100 % · ${share} % de la sección (condición de diseño)`,
        maxRh: (where: string, rh: string) => `HR máx. ${where} ${rh} % (condición de diseño)`,
        betweenLayers: "entre las capas",
        inTheField: "en el campo",
        noFace: "ninguna interfaz entre capas",
        surface1D: "HR de la superficie interior (moho)",
        surfaceDetail1D: "límite 80 % · haga clic para el diagrama psicrométrico",
        surface2D: "Superficie interior (moho)",
        surfaceDetail2D: "HR máx., límite 80 % · temperatura mín. · haga clic para el diagrama psicrométrico",
        solver: "Solucionador",
        notConverged: "no convergido",
        solverDetail: "campos térmico y de vapor",
        monthly: "Glaser mensual",
        notCalculated: "no calculado",
        checksNotSatisfied: (n: number) => `${n} ${n > 1 ? "verificaciones no cumplidas" : "verificación no cumplida"}`,
        verified: "verificado",
        monthlyOk: "balance anual, condensación admisible, moho",
        monthlyFailure: {
          residual: "condensación remanente al final del ciclo",
          admissible: "condensación admisible superada",
          mould: "riesgo de moho (f_Rsi)",
        },
        satisfied: "Cumplida",
        notSatisfied: "No cumplida",
        ofLimit: (percent: number) => `${percent} % del límite`,
        notSatisfiedCount: (bad: number, total: number) => `⚠ ${bad} de ${total} verificaciones no cumplidas`,
        allSatisfied: (total: number) => `las ${total} verificaciones cumplidas`,
        mode2D: "Malla 2D (estacionario, volúmenes finitos)",
        mode1D: "1D simplificado (EN ISO 6946 / EN ISO 13788)",
        clickForDetails: "haga clic en una verificación para ver los detalles",
      },
      temperature: "Temperatura",
      humidity: "Humedad",
      humidityRh: "Humedad (HR)",
      vapourPressure: "Presión de vapor",
      vapourPressurePa: "Presión de vapor [Pa]",
      moistureContent: "Contenido de humedad",
      secondProfileChart: "Segundo gráfico del perfil",
      field2DDesc: "Medio intereje simétrico de montantes (o todo el vano si no hay elemento estructural), alturas de las filas a escala. Haga clic en una celda para ver sus valores exactos. Arrastre la línea vertical para llevar a esa posición los gráficos del perfil vertical de al lado",
      moistureNote: "Rojo = sobresaturado (HR>100%) - el equilibrio de sorción w(HR) ya no describe correctamente esa celda, tanto si se dispone de un modelo de humedad como si no.",
      midBay: "centro del vano",
      midBayColumn: "columna en el centro del vano",
      studCentreline: "eje del montante",
      fromStud: (distance: string) => `${distance} desde el eje del montante`,
      verticalProfile: (where: string) => `Perfil vertical (${where})`,
      temperatureThroughThickness: "Temperatura en el espesor",
      rhThroughThickness: "HR en el espesor",
      psatChartLabel: "Presión de saturación y de vapor en el espesor",
      rhChartLabel: "Humedad relativa en el espesor",
      cell: {
        hint: "Haga clic en una celda del mapa superior para ver aquí sus valores.",
        interiorFilm: "Capa superficial interior",
        exteriorFilm: "Capa superficial exterior",
        layer: (n: number) => `Capa ${n}`,
        material: "material",
        noMoistureModel: "no disponible (sin modelo de humedad)",
        dryNoModel: "(seco, sin modelo)",
        saturated: "Estado: SATURADO / POSIBLE CONDENSACIÓN",
        rawNote: ">100% - valor técnico, no la HR indicada arriba",
      },
      field1DDesc: "La sección muestra la única condición de diseño fija con la que se resuelve el cálculo (edite Te/HRe/Ti/HRi directamente en la sección gráfica del panel Estratigrafía), no una media anual, dibujada a la misma escala que el panel Estratigrafía. Sin un cálculo 2D del puente térmico, el campo de color es 1D: varía solo a través del espesor y los contornos de los elementos estructurales se dibujan solo como referencia (el color no varía lateralmente). Cuando se dispone de un cálculo del puente térmico con malla 2D, la vista Temperatura muestra ese campo realmente resuelto y la vista Humedad la HR local del mismo campo, calculada junto con el perfil 1D de presión de vapor de Glaser (la resistencia al vapor no se resuelve lateralmente, por lo que es una aproximación y no un verdadero cálculo 2D del vapor - para ello seleccione el modo de cálculo de malla 2D).",
      sectionTitle: (field: string) => `${field} a través de la estratigrafía - condición de diseño`,
      sectionTitleShort: (field: string) => `${field} a través de la estratigrafía`,
      noResultsYet: "Sin resultados todavía.",
      diagramNote2D: (humidity: boolean): string =>
        humidity
          ? "Campo de temperatura de la malla 2D + perfil de vapor 1D (pv no resuelta lateralmente), reflejado respecto al eje de cada montante"
          : "Malla 2D, reflejada respecto al eje de cada montante (se muestran 2 interejes)",
      profilesTitle: "Perfiles en el espesor (condición de diseño)",
      glaserSubtitleBefore: "Diagrama de Glaser — condensación donde ",
      glaserSubtitleAfter: ", en la condición de diseño",
      info: {
        uvalue: "Coeficiente de transmisión térmica, comprobado frente al límite de referencia elegido (o un valor personalizado).",
        rw: "Estimación simplificada según la ley de masa (al estilo de la EN 12354-1, Rw ≈ 37,5·log10(m′) − 42 dB) a partir solo de la masa superficial total - no válida para soluciones de varias hojas/con cámara (resonancia y coincidencia no modeladas). No sustituye a un Rw ensayado o certificado.",
        condensationGlaser: "Glaser 1D: presión de vapor superior a la de saturación en una interfaz interior, en la condición de diseño fija (panel Estratigrafía). Una comprobación estacionaria, no un balance anual.",
        surfaceCondensation: "Temperatura/HR justo en la superficie interior, en la condición de diseño fija. Por encima de ~80% de HR favorece el moho incluso sin condensación propiamente dicha; al 100% de HR la superficie condensa.",
        interstitialCondensation2d: "HR local máxima en el campo 2D convergido. Por encima de ~80% favorece el moho; al 100% esa celda está sobresaturada (condensa).",
      },
      bar: {
        empty: "Añada al menos una capa para ver aquí los resultados.",
        moreInfo: "Más información",
        previousSection: "Sección anterior",
        nextSection: "Sección siguiente",
        sectionOf: (i: number, n: number) => `Sección ${i} de ${n}`,
        collapseToggle: "Contraer/expandir la barra de resultados",
        thermal: "Térmico",
        thermal2D: "Térmico (2D)",
        hygrometric: "Higrométrico",
        hygrometric2D: "Higrométrico (2D)",
        acoustic: "Acústico",
        uValue: "Transmitancia U",
        uValue2D: "Transmitancia U (2D)",
        transmittanceConvergence: "Convergencia de la transmitancia",
        vapourConvergence: "Convergencia presión de vapor / HR",
        converged: "Convergido",
        notConverged: "No convergido",
        solveTime: (time: string) => `Tiempo de cálculo: ${time}`,
        surfaceCondensation: "Condensación superficial",
        condensing: "Condensación",
        mouldRisk: "Riesgo de moho",
        ok: "OK",
        noSurfaceCondensation: "Sin condensación superficial",
        condensationGlaser: "Condensación (Glaser)",
        interstitialCondensation: "Condensación intersticial",
        noCondensation: "Sin condensación",
        suggestion: "Sugerencia",
        leverIterations: "aumentar las iteraciones máximas",
        leverMesh: "refinar la malla (más filas)",
        leverTolerance: "relajar la tolerancia (εU/εRH)",
        leverOr: " o ",
        suggestTry: (levers: string) => `Pruebe a: ${levers} (Configuración → Solucionador 2D).`,
        suggestAllTried: "Ya se han aumentado las iteraciones/la malla y relajado la tolerancia respecto a los valores predeterminados y sigue sin converger - compruebe si la estratigrafía tiene una relación de conductividades extrema entre materiales.",
        weight: "Peso",
        rw: "Rw (estimación ley de masa)",
      },
      psychro: {
        title: "Diagrama psicrométrico",
        hint: "el aire interior hasta el 80 % de HR",
        hintValue: (temperature: string) => `el aire interior hasta el 80 % de HR: ${temperature}`,
        unavailable: "Disponible una vez calculada la estratigrafía.",
        insideAir: "Aire interior",
        humidityRatio: "Humedad específica W",
        theta80: "θ al 80 % de HR",
        insideSurface: "Superficie interior",
        insideSurfaceMin: "Superficie interior, mín.",
        noMouldRisk: "Superficie por encima de θ80%: sin riesgo de moho",
        mouldRisk: "Superficie por debajo de θ80%: riesgo de moho",
        chartLabel: "Diagrama psicrométrico: el aire interior enfriado a humedad específica constante hasta el 80 % de HR",
        dryBulb: (unit: string) => `temperatura de bulbo seco [${unit}]`,
        humidityRatioAxis: "humedad específica W [g/kg aire seco]",
        enthalpy: "entalpía h [kJ/kg]",
      },
    },
    tutorial: {
      startTitle: "¿Iniciar el tutorial?",
      startMessage: "Una breve demostración de las funciones principales sobre el muro de entramado de madera validado con Ubakus, ya calculado: cada paso se realiza por usted, y usted continúa con Siguiente. Su proyecto se deja a un lado y vuelve tal como está al final.",
      start: "Iniciar el tutorial",
      preparing: "Preparando el tutorial…",
      running: "El tutorial está en curso: pulse Siguiente, o Salir del tutorial para abandonarlo.",
      stepOf: (step: number, total: number) => `Paso ${step} de ${total}`,
      showing: "Mostrando…",
      stepDone: "✓ Hecho",
      skip: "Salir del tutorial",
      next: "Siguiente",
      startButton: "Empezar",
      finish: "Finalizar",
      demoName: "Muro de demostración",
      steps: {
        intro: {
          title: "Tutorial",
          text: "Una breve demostración de las funciones principales sobre un ejemplo real - el muro de entramado de madera con cámara de instalaciones validado con Ubakus, ya calculado. Cada paso se realiza por usted y se muestra en la página: pulse Siguiente para continuar, o Salir del tutorial para abandonarlo. Su propio proyecto se deja a un lado y vuelve tal como está al final.",
        },
        name: {
          title: "Nombre del proyecto",
          text: "El proyecto recibe un nombre (opcional): aparece en el informe.",
        },
        site: {
          title: "Emplazamiento y clima",
          text: "El emplazamiento define el clima de las verificaciones de condensación: un clic en el mapa, un lugar buscado por nombre o uno de los lugares del mapa. Aquí se mantiene el emplazamiento del ejemplo.",
        },
        element: {
          title: "Elemento",
          text: "Muro: el ejemplo es un muro exterior. La cubierta y el contacto con el terreno fijan otras resistencias superficiales.",
        },
        analysis: {
          title: "Análisis",
          text: "Estático: valor U, condensación de Glaser en la condición de diseño y Glaser mensual con el clima del emplazamiento. Cuasidinámico: calor y humedad hora a hora durante varios años del clima del emplazamiento, en sus propias pestañas. El ejemplo utiliza el análisis estático.",
        },
        stratigraphy: {
          title: "Estratigrafía",
          text: "La pestaña Estratigrafía: las capas del muro, del exterior al interior, con sus montantes.",
        },
        addMaterial: {
          title: "Añadir un material",
          text: "Un material se arrastra desde la biblioteca hasta el dibujo: un hueco muestra dónde irá, y se añade donde se suelta.",
        },
        thickness: {
          title: "Cambiar un espesor",
          text: "Un clic en una capa abre sus propiedades: el espesor se escribe y se confirma con Intro.",
        },
        layerValues: {
          title: "Valores de una sola capa",
          text: "En el mismo panel se cambia la λ de esta capa: solo cambia esta capa, el material de la biblioteca queda como está.",
        },
        undo: {
          title: "Deshacer",
          text: "Deshacer (o Ctrl+Z) revierte los cambios uno a uno, hasta que el ejemplo vuelve a estar como antes; ▾ muestra los cambios recientes.",
        },
        results: {
          title: "Análisis y resultados",
          text: "Análisis y resultados: el ejemplo ya está calculado.",
        },
        checks: {
          title: "Las verificaciones",
          text: "Valor U frente al límite, condensación intersticial (Glaser), humedad superficial interior (moho) y el resultado del Glaser mensual; debajo, la temperatura y la presión de vapor a través del muro.",
        },
        monthly: {
          title: "Glaser mensual",
          text: "Un clic en la verificación Glaser mensual: condensación y secado mes a mes con el clima del emplazamiento.",
        },
        report: {
          title: "Informe",
          text: "El Informe: todo en un único documento, para imprimir o guardar como PDF.",
        },
        done: {
          title: "Hecho",
          text: "Este es el recorrido principal: proyecto y emplazamiento, estratigrafía, resultados, informe. Pulse Finalizar: su propio proyecto vuelve tal como lo dejó. Tutorial, en la barra lateral, lo muestra de nuevo en cualquier momento.",
        },
      },
    },
    report: {
      printAndContents: "Impresión y contenido del informe",
      print: "Imprimir…",
      printInfo: (pages: number, bw: boolean) => `${pages} ${pages > 1 ? "páginas" : "página"} · A4 · ${bw ? "blanco y negro" : "color"}`,
      contents: "Contenido",
      groups: {
        inputs: "Datos de entrada",
        results: "Resultados",
      },
      all: "todos",
      none: "ninguno",
      notAvailable: "No disponible para el cálculo actual",
      fieldShown: "Campo mostrado",
      sections: {
        project: "Proyecto",
        site: "Emplazamiento y clima",
        conditions: "Condiciones de diseño",
        drawing: "Dibujo de la estratigrafía",
        layers: "Tabla de capas",
        checks: "Verificaciones",
        uvalue: "Cálculo de la transmitancia U",
        nodes: "Valores en las interfaces",
        temp: "Perfil de temperatura",
        profiles2d: "Perfiles verticales 2D",
        compare: "Comparación 1D / 2D",
        climate: "Año de referencia del emplazamiento",
      },
      relativeHumidity: "Humedad relativa",
      untitledProject: "Proyecto sin título",
      notes: "Notas",
      date: "Fecha",
      calculation2D: "Malla 2D (régimen estacionario, volúmenes finitos)",
      location: "Ubicación",
      climateData: "Datos climáticos",
      elevation: "Altitud",
      topSide: (side: string) => `Lado superior (${side}): temperatura / HR / resistencia superficial`,
      bottomSide: (side: string) => `Lado inferior (${side}): temperatura / HR / resistencia superficial`,
      materialInsideOutside: "Material (interior → exterior)",
      check: "Verificación",
      value: "Valor",
      reference: "Referencia",
      outcome: "Resultado",
      satisfied: "CUMPLE",
      notSatisfied: "NO CUMPLE",
      overallOutcome: "Resultado global",
      allSatisfied: "SE CUMPLEN TODAS LAS VERIFICACIONES",
      notAllSatisfied: "NO SE CUMPLEN TODAS LAS VERIFICACIONES",
      uFraming: "U con los elementos de la estructura (método combinado EN ISO 6946)",
      interface: "Interfaz",
      insideNode: (n: number) => `${n} (interior)`,
      outsideNode: (n: number) => `${n} (exterior)`,
      verdict: "Resultado",
      notVerified: (failures: string) => `no verificado: ${failures}`,
      method: "Método",
      methodValue: (preset: string, start: string) => `${preset}; inicio del ciclo: ${start}`,
      monthlySection: (u: string, maxAccumulated: string, left: string, fRsi: string, fRsiMin: string) => `U ${u} W/(m²K) · máx. acumulado ${maxAccumulated} · restante al final ${left} · f_Rsi ${fRsi} / ${fRsiMin}`,
      glaserMonthShown: "Diagrama de Glaser (mes mostrado en la página)",
      quantity: "Magnitud",
      simplified1D: "1D simplificado",
      mesh2D: "Malla 2D",
      insideSurfaceTemperature: "Temperatura superficial interior",
      insideSurfaceRh: "HR superficial interior",
      min: "(mín.)",
      max: "(máx.)",
      empty: "Marque en la lista Contenido las secciones que desea incluir.",
      disclaimer1: "Todos los cálculos deben ser revisados y aprobados por el proyectista responsable antes de su uso.",
      disclaimer2: "Los valores de los materiales proceden de los certificados o de las fichas técnicas de los productos; el uso de métodos numéricos (modo malla 2D) y la elección de los materiales y productos de la estratigrafía son responsabilidad del proyectista.",
      pageOf: (page: number, pages: number) => `Página ${page} de ${pages}`,
      reportPages: "Páginas del informe",
      viewer: "Visor del informe",
      previousPage: "Página anterior",
      nextPage: "Página siguiente",
      page: "Página",
      zoomOut: "Alejar",
      zoomIn: "Acercar",
      zoom: "Zoom",
      fitWidthPct: (percent: string) => `Ajustar al ancho (${percent} %)`,
      fitPagePct: (percent: string) => `Ajustar a la página (${percent} %)`,
      fitWidth: "Ajustar al ancho",
      fitPage: "Ajustar a la página",
      pageLayout: "Diseño de página",
      layouts: {
        continuous: "Páginas continuas",
        single: "Página única",
        two: "Dos páginas en paralelo",
      },
      blackAndWhite: "Blanco y negro",
      blackAndWhiteOn: "Blanco y negro (activado: también al imprimir)",
      bwShort: "B/N",
    },
  },

  // Texts of the Design System V2 components (src/components/ds2).
  designSystem: {
    openInNewLabel: "Se abre en una pestaña nueva",
    breadcrumbLabel: "Ruta de navegación",
    openMenuLabel: "Abrir menú",
    closeMenuLabel: "Cerrar menú",
    closeLabel: "Cerrar",
    accountMenuLabel: "Menú de cuenta",
    paginationOfLabel: "de",
    previousPageLabel: "Página anterior",
    nextPageLabel: "Página siguiente",
    selectPlaceholder: "Selecciona...",
    dropFilesLabel: "¡Suelta aquí!",
    dropZoneLabel: "Zona para soltar archivos",
    dropRejectedLabel: "Este tipo de archivo no es compatible",
    searchPlaceholder: "Buscar",
    clearSearchLabel: "Borrar búsqueda",
    toggleSubmenuLabel: (text) => `Alternar ${text}`,
    contextMenuTriggerLabel: "Abrir menú de acciones",
    increaseLabel: (value: number) => `Aumentar ${value}`,
    decreaseLabel: (value: number) => `Disminuir ${value}`,
    datePicker: {
      clearButton: "Borrar fecha",
      nextMonth: "Mes siguiente",
      placeholder: "Seleccionar fecha",
      previousMonth: "Mes anterior",
      selectMonth: "Seleccionar mes",
      selectYear: "Seleccionar año",
      calendarLabel: "Elegir una fecha",
    },
    cancelLabel: "Cancelar",
    okLabel: "Aceptar",
    removeLabel: "Quitar",
    requiredFieldLabel: "Este campo es obligatorio.",
    fieldTooSmallLabel: "El valor de este campo es demasiado bajo.",
    fieldTooLargeLabel: "El valor de este campo es demasiado alto.",
    fieldInvalidFormatLabel: "El valor de este campo tiene un formato no válido.",
    fieldInvalidValueLabel: "El valor de este campo no es válido.",
    browseOrDropFileLabel: "Arrastra y suelta, o haz clic para buscar",
    browseOrDropImageLabel: "Arrastra y suelta, o haz clic para subir una imagen",
    removeImageLabel: "Quitar imagen",
    resetImageLabel: "Restablecer imagen",
    translationsLabel: "Traducciones",
    increaseOrderLabel: "Aumentar orden",
    decreaseOrderLabel: "Disminuir orden",
    stagingLabel: "Entorno de prueba",
    unexpectedErrorLabel: "Algo salió mal. Por favor, inténtalo de nuevo.",
    saveLabel: "Guardar",
    loadingLabel: "Cargando",
    browseFilesLabel: "Examinar archivos",
    noOptionsLabel: "Sin opciones",
    quantityOfLabel: (label: string) => `Cantidad de ${label}`,
    chooseOrDropFileLabel: "Elige un archivo o arrástralo aquí para subirlo.",
    stepOfLabel: (current: number, total: number) => `Paso ${current} de ${total}`,
    stepCompletedLabel: "completado",
    backToStepLabel: (step: number) => `Volver al paso ${step}`,
  },
};

export default es;
